import { type Translation } from "../hooks/useTranslations";
import type { NoticeParams } from "../pages/engines/lockCalculator/model/noticeParams";

import { type UILanguageCode } from "../types";

const it: Translation = {
  languageCode: "it" as UILanguageCode,
  projects: {
    all: "Tutti i progetti",
    new: "Nuovo progetto",
    singular: "Progetto",
    plural: "Progetti",
    my: "I miei progetti",
    createNew: "Crea nuovo progetto",
    delete: "Elimina progetto",
    search: "Cerca progetti",
    shareWithUser: "Condividi progetto con utente",
    editPermissions: "Modifica permessi",
    editSharings: "Modifica condivisioni",
    invitations: "Inviti al progetto",

    invitationStatus: {
      pending: "In attesa",
      accepted: "Accettato",
      rejected: "Rifiutato",
      cancelled: "Annullato",
      statusChanges: {
        accept: "Invito accettato con successo.",
        rejected: "Invito rifiutato.",
      },
    },

    invitePage: {
      badge: "Invito di accesso al progetto",
      fallbackAuthorName: "un membro del team",
      collaborationLead: "ti ha invitato a collaborare a questo progetto e a consultarne i contenuti condivisi.",
      invitedBy: "Invitato da",
      invitationSentTo: "Invito inviato a",
      projectName: "Nome progetto",
      description: "Descrizione",
      inviteNoLongerAvailable:
        "Questo invito non è più disponibile perché è già stato accettato, rifiutato o annullato.",
      acceptSignedIn: "Accettando questo invito aprirai subito il progetto.",
      acceptSignedOut: "Accettando questo invito dovrai accedere prima di ottenere l'accesso al progetto.",
      acceptAction: "Accetta invito",
      rejectAction: "Rifiuta invito",
    },

    questions: {
      delete: "Vuoi davvero eliminare questo progetto?",
      unshare: "Vuoi davvero rimuovere la condivisione del progetto per questo utente?",
    },

    errors: {
      deleteProjectWithConfigurationsNotPossible:
        "Non è possibile eliminare un progetto se sono presenti configurazioni.",
      deleteProjectWithDocumentsNotPossible: "Non è possibile eliminare un progetto se sono presenti document.",
      notFound: "Progetto non trovato.",
      sharingNotFound: "Condivisione non trovata.",
      sharingAlreadyExists: "Il progetto è già condiviso con questo utente.",
      documentNotFound: "Documento non trovato.",
      missingDocumentName: "Nome del documento mancante.",
      documentAlreadyPresent: "Documento già presente.",
      missingProjectStatus: "Stato progetto mancante.",
      inviteReadNotAllowed: "Non sei autorizzato a visualizzare questo invito.",
      inviteInsertNotAllowed: "Non sei autorizzato a inviare un invito per questo progetto.",
      inviteUpdateNotAllowed: "Non sei autorizzato ad accettare o rifiutare questo invito.",
      inviteDeleteNotAllowed: "Non sei autorizzato a eliminare questo invito.",
      inviteAlreadyExists: "Esiste già un invito per questo utente.",
      inviteNotFound: "Invito non trovato.",
      inviteAlreadyCancelled: "Questo invito è già stato annullato.",
      inviteAlreadyAccepted: "Questo invito è già stato accettato.",
      inviteAlreadyRejected: "Questo invito è già stato rifiutato.",
    },
  },

  calculations: {
    all: "Tutti i calcoli",
    new: "Nuovo calcolo",
    plural: "Calcoli",
    create: "Crea calcolo",
    createNew: "Crea nuovo calcolo",
    delete: "Elimina calcolo",
    duplicate: "Duplica calcolo",
    my: "I miei calcoli",
    search: "Cerca calcoli",
    reset: "Reset calcolo",
    newCalculationName: "Nome nuovo calcolo",
    shareWithUser: "Condividi calcoli con utente",
    editSharings: "Modifica condivisioni",
    invitations: "Inviti al calcolo",
    invitePage: {
      badge: "Invito di accesso a un calcolo",
      collaborationLead: "l'ha invitata a collaborare su questo calcolo e a consultarne i contenuti condivisi.",
      calculationName: "Nome del calcolo",
      inviteNoLongerAvailable:
        "Questo invito non è più disponibile perché è già stato accettato, rifiutato o annullato.",
      acceptSignedIn: "Accettando questo invito il calcolo verrà aperto immediatamente.",
      acceptSignedOut: "Accettando questo invito le verrà chiesto di accedere prima di concedere l'accesso al calcolo.",
    },

    questions: {
      delete: "Vuoi davvero eliminare questo calcolo?",
      unshare: "Vuoi davvero rimuovere la condivisione del calcolo per questo utente?",
      reset: "Vuoi davvero resettare tutti i dati del calcolo corrente?",
    },

    errors: {
      notFound: "Calcolo non trovato.",
      sharingNotFound: "Condivisione non trovata.",
      sharingAlreadyExists: "Il calcolo è già condiviso con questo utente.",
      moduleNotSupported: "Modulo di calcolo non supportato.",
      moduleRestricted: "Questo modulo non consente la creazione, modifica o duplicazione di calcoli.",
      inviteInsertNotAllowed: "Non sei autorizzato a inviare un invito per questo calcolo.",
      inviteUpdateNotAllowed: "Non sei autorizzato ad accettare o rifiutare questo invito.",
      inviteDeleteNotAllowed: "Non sei autorizzato a eliminare questo invito.",
      inviteAlreadyExists: "Esiste già un invito per questo utente.",
      inviteNotFound: "Invito non trovato.",
      inviteAlreadyCancelled: "Questo invito è già stato annullato.",
      inviteAlreadyAccepted: "Questo invito è già stato accettato.",
      inviteAlreadyRejected: "Questo invito è già stato rifiutato.",
    },
  },

  sharingRequests: {
    title: "Richieste di accesso",
    requestedAt: "Richiesta il",
    message: "Messaggio",
    accept: "Accetta",
    reject: "Rifiuta",
    acceptTitle: "Accetta la richiesta di accesso",
    rejectQuestion: "Vuoi davvero rifiutare questa richiesta di accesso?",
    accepted: "Richiesta di accesso accettata.",
    rejected: "Richiesta di accesso rifiutata.",
    notShared: {
      projectTitle: "Progetto non condiviso",
      calculationTitle: "Calcolo non condiviso",
      projectText: "Questo progetto non è stato condiviso con te, quindi non puoi accedervi.",
      calculationText: "Questo calcolo non è stato condiviso con te, quindi non puoi accedervi.",
      requestText: "Puoi chiedere al proprietario di condividerlo con te: riceverà la tua richiesta via email.",
      messageLabel: "Messaggio per il proprietario",
      request: "Richiedi l'accesso",
      sent: "La tua richiesta di accesso è stata inviata.",
      pending: (date: string) =>
        `Hai richiesto l'accesso il ${date}: riceverai un'email non appena il proprietario risponderà.`,
    },
    managePage: {
      projectTitle: "Richiesta di accesso al progetto",
      calculationTitle: "Richiesta di accesso al calcolo",
      requestedBy: "Richiesta da",
      project: "Progetto",
      calculation: "Calcolo",
      projectLead: "ti chiede l'accesso a questo progetto.",
      calculationLead: "ti chiede l'accesso a questo calcolo.",
      alreadyAccepted: "Questa richiesta è già stata accettata.",
      alreadyRejected: "Questa richiesta è già stata rifiutata.",
    },
    errors: {
      notFound: "Richiesta di accesso non trovata.",
      readNotAllowed: "Non sei autorizzato a visualizzare questa richiesta di accesso.",
      alreadyExists: "Hai già richiesto l'accesso: il proprietario non ha ancora risposto.",
      alreadyShared: "È già stato condiviso con te: ricarica la pagina.",
      alreadyAccepted: "Questa richiesta di accesso è già stata accettata.",
      alreadyRejected: "Questa richiesta di accesso è già stata rifiutata.",
    },
  },

  notifications: {
    title: "Notifiche",
    projectSharingRequest: (user: string, project: string) =>
      `${user} chiede l'accesso al progetto ${project}`,
    calculationSharingRequest: (user: string, calculation: string) =>
      `${user} chiede l'accesso al calcolo ${calculation}`,
    projectSharingInvite: (user: string, project: string) =>
      `${user} ti ha invitato al progetto ${project}`,
    calculationSharingInvite: (user: string, calculation: string) =>
      `${user} ti ha invitato al calcolo ${calculation}`,
  },

  users: {
    add: "Aggiungi utente",
    all: "Tutti gli utenti",
    plural: "Utenti",
    remove: "Rimuovi utente",
    invite: "Invita utente",
    search: "Cerca utenti",
    usersEmail: "Email utente",
    usersName: "Nome utente",

    questions: {},

    pending: {
      plural: "Utenti in attesa",
      edit: "Modifica utente in attesa",
      delete: "Rimuovi utente in attesa",
      createdAt: "Aggiunto",
      info: "Se questa email non ha ancora un account, l'utente resta in attesa fino al suo primo accesso, quando riceve questi ruoli e permessi.",
    },

    errors: {
      notFound: "Utente non trovato.",
      alreadyExists: "Esiste già un utente con questa email.",
      pendingAlreadyExists: "Esiste già un utente in attesa con questa email.",
    },
  },

  items: {
    all: "Tutti gli elementi",
    singular: "Elemento",
    plural: "Elementi",
    createNew: "Crea nuovo elemento",
    delete: "Elimina elemento",
    search: "Cerca elementi",
    list: "Lista elementi",

    questions: {
      delete: "Vuoi davvero eliminare questo elemento?",
    },

    errors: {
      notFound: "Elemento non trovato.",
      doesNotExist: "L'articolo con questo codice non esiste.",
      missingCode: "Codice elemento mancante.",
    },
  },

  clients: {
    all: "Tutti gli client",
    plural: "Client",
    createNew: "Crea nuovo client",
    delete: "Elimina client",
    search: "Cerca client",

    questions: {
      delete: "Vuoi davvero eliminare questo client?",
    },

    errors: {
      notFound: "Client non trovato.",
      insertNotAllowed: "Non sei autorizzato ad inserire un nuovo client.",
      readNotAllowed: "Non sei autorizzato a leggere questo client.",
      updateNotAllowed: "Non sei autorizzato ad aggiornare questo client.",
      deleteNotAllowed: "Non sei autorizzato ad eliminare questo client.",
    },
  },

  accessControl: {
    title: "Controllo accessi",
    plural: "Controllo accessi",
    disclaimer:
      "Il controllo accessi stabilisce quali utenti possono usare ogni applicazione. Non scavalca il geo-blocking: un utente consentito viene comunque rifiutato nei paesi in cui l'applicazione, o uno dei suoi moduli, è bloccata dal geo-blocking. Per far accedere un utente da qualsiasi paese, assegnagli il permesso geo_blocking:bypass (ad esempio con il ruolo geo-blocking-bypass): rimuove ogni geo-blocking, su tutte le applicazioni e tutti i moduli.",
    applications: "Applicazioni",
    modules: "Moduli",
    modulesDisclaimer:
      "Il controllo accessi di un modulo vale solo nell'applicazione principale: l'applicazione propria del modulo, se esiste, segue la sua scheda. Un utente escluso da un modulo non lo vede, né vede i suoi calcoli, e non può aprirli.",

    settings: {
      lockSwitchLabel: "Limita l'applicazione agli utenti autorizzati",
      lockSwitchDescription:
        "Quando attivo, solo gli utenti con una regola 'consentito' o con accesso da amministratore possono utilizzare l'applicazione.",
      confirmEnable: "Vuoi davvero limitare l'applicazione ai soli utenti autorizzati?",
      confirmDisable: "Vuoi davvero consentire nuovamente l'accesso a tutti gli utenti autenticati?",
    },

    // the modules' tab: a module's lock switch, in the main application only
    moduleSettings: {
      lockSwitchLabel: "Limita il modulo agli utenti autorizzati",
      lockSwitchDescription:
        "Quando attivo, solo gli utenti con una regola 'consentito' o con accesso da amministratore possono utilizzare il modulo nell'applicazione principale.",
      confirmEnable: "Vuoi davvero limitare il modulo ai soli utenti autorizzati?",
      confirmDisable: "Vuoi davvero consentire nuovamente l'accesso a tutti gli utenti autenticati?",
    },

    userAccess: {
      notAllowed: "Non consentito",
      causes: {
        rule: "Regola",
        default: "Predefinito",
        admin: "Amministratore",
        locked: "App limitata",
      },
    },

    rules: {
      plural: "Regole di accesso",
      app: "Applicazione",
      createNew: "Aggiungi regola",
      search: "Cerca per email o motivo",
      delete: "Elimina regola",
      email: "Email",
      user: "Utente",
      status: "Stato",
      statusAllowed: "Consentito",
      statusBlocked: "Bloccato",
      reason: "Motivo",
      createdAt: "Creato il",
      import: "Importa",
      importTitle: "Importa regole",
      importFrom: "Importa da",
      currentStatus: "Stato attuale",
      selectAll: "Seleziona tutto",
      importHint:
        "Le regole già presenti con uno stato diverso non sono selezionate: selezionale per sovrascrivere stato e motivo attuali.",

      questions: {
        delete: "Vuoi davvero eliminare questa regola di accesso?",
      },
    },

    forbiddenTitle: "Non consentito",
    forbiddenText: "Non sei autorizzato ad accedere a questa applicazione.",
    signedInAs: "Accesso effettuato come",

    errors: {
      forbidden: "Non sei autorizzato ad accedere a questa applicazione.",
      ruleNotFound: "Regola di accesso non trovata.",
      ruleMissingEmail: "L'email è obbligatoria.",
      ruleInvalidStatus: "Lo stato deve essere consentito o bloccato.",
      ruleCannotTargetSelf: "Non puoi aggiungere una regola di accesso per te stesso.",
      settingsReadNotAllowed: "Non sei autorizzato a leggere le impostazioni di accesso.",
      settingsUpdateNotAllowed: "Non sei autorizzato ad aggiornare le impostazioni di accesso.",
      ruleReadNotAllowed: "Non sei autorizzato a leggere le regole di accesso.",
      ruleInsertNotAllowed: "Non sei autorizzato a creare una regola di accesso.",
      ruleDeleteNotAllowed: "Non sei autorizzato ad eliminare una regola di accesso.",
      lastAdmin: "Non puoi rimuovere l'ultimo utente in grado di gestire il controllo accessi dell'applicazione.",
      invalidScope: "Ambito di accesso sconosciuto.",
      invalidModule: "Modulo sconosciuto.",
    },
  },

  geoBlocking: {
    plural: "Geo-blocking",
    disclaimer:
      "Il geo-blocking stabilisce da quali paesi si può usare ogni applicazione, o un modulo. Le regole di un modulo valgono ovunque il modulo venga usato, nell'applicazione principale e nella sua; quelle di un'applicazione solo per quell'applicazione. Senza regole, tutti i paesi sono consentiti; appena una regola è 'Consentito', solo i paesi consentiti. Il controllo accessi non lo scavalca: per far accedere un utente da qualsiasi paese, assegnagli il permesso geo_blocking:bypass (ad esempio con il ruolo geo-blocking-bypass).",
    settings: "Regioni e paesi",
    modules: "Moduli",
    rules: {
      singular: "Regola",
      target: "Regione o paese",
      type: "Tipo",
      region: "Regione",
      country: "Paese",
      status: "Stato",
      statusAllowed: "Consentito",
      statusBlocked: "Bloccato",
      createdAt: "Creata",
    },
    regions: {
      plural: "Regioni",
      singular: "Regione",
      countries: "Paesi",
    },
    countries: {
      plural: "Paesi",
      code: "Codice",
    },
    errors: {
      moduleBlocked: "Non sei autorizzato ad accedere a questo modulo.",
      ruleAlreadyExists: "Esiste già una regola per questa regione o questo paese.",
      regionNameAlreadyExists: "Esiste già una regione con questo nome.",
      regionInUse: "La regione è usata da una regola di geo-blocking.",
      blocksSelf: "Questa modifica bloccherebbe il tuo accesso all'applicazione.",
    },
  },

  usageStatistics: {
    plural: "Statistiche di utilizzo",
    from: "Dal",
    to: "Al",
    apps: "Applicazioni",
    visits: "Visite",
    description: "Numero di utenti distinti al giorno (UTC).",
    errors: {
      readNotAllowed: "Non hai i permessi per visualizzare le statistiche di utilizzo.",
      invalidPeriod: "Scegli un periodo valido, di al massimo un anno.",
    },
  },

  helpRequests: {
    title: "Aiuto",
    dialogTitle: "Richiedi assistenza",
    intro: "Descrivi il tuo problema: il nostro team di consulenza tecnica ti risponderà via email.",
    type: "Tipo",
    subject: "Oggetto",
    message: "Messaggio",
    screenshot: "Screenshot",
    attachScreenshot: "Allega lo screenshot della pagina corrente",
    screenshotUnavailable: "Non è stato possibile acquisire lo screenshot della pagina.",
    attachments: "Allegati",
    attachmentsHint: (maxFiles: number, maxMegabytes: number) =>
      `Fino a ${maxFiles} file, ${maxMegabytes} MB in totale.`,
    removeAttachment: "Rimuovi allegato",
    diagnosticsInfo:
      "Verranno allegate informazioni tecniche sul tuo browser e gli ultimi errori della pagina, per aiutarci ad analizzare il problema.",
    send: "Invia",
    success: "La tua richiesta è stata inviata. Riceverai una conferma via email.",

    errors: {
      invalidForm: "Non è stato possibile leggere la richiesta, oppure supera la dimensione consentita.",
      missingSubject: "L'oggetto è obbligatorio.",
      subjectTooLong: "L'oggetto è troppo lungo.",
      missingMessage: "Il messaggio è obbligatorio.",
      messageTooLong: "Il messaggio è troppo lungo.",
      tooManyAttachments: (maxFiles: number) => `Puoi allegare al massimo ${maxFiles} file.`,
      attachmentsTooLarge: (maxMegabytes: number) => `Gli allegati superano ${maxMegabytes} MB in totale.`,
      invalidScreenshot: "Lo screenshot non è valido.",
      invalidDiagnostics: "Le informazioni tecniche non sono valide.",
    },
  },

  feedbacks: {
    title: "Segnala agli sviluppatori",
    dialogTitle: "Invia feedback",
    intro: "Segnala un errore o condividi un suggerimento: lo riceverà il team di sviluppo.",
    types: {
      bug: "Errore",
      suggestion: "Suggerimento",
      other: "Altro",
    },
    success: "Grazie, il tuo feedback è stato inviato.",

    errors: {
      sendNotAllowed: "Non è autorizzato a inviare feedback.",
      invalidType: "Scegli un tipo di feedback valido.",
    },
  },

  home: {
    greeting: (name: string) => `Ciao, ${name}`,
    subtitle: "Scegli un calcolatore per iniziare un nuovo calcolo.",
    availableCalculators: "Calcolatori disponibili",
  },

  modules: {
    all: "Tutti i moduli",
    plural: "Moduli",
    createNew: "Crea nuovo modulo",
    delete: "Elimina modulo",
    search: "Cerca moduli",
    singular: "Modulo",

    questions: {
      delete: "Vuoi davvero eliminare questo modulo?",
    },

    errors: {
      notFound: "Modulo non trovato.",
      insertNotAllowed: "Non hai i permessi per inserire un nuovo modulo.",
      readNotAllowed: "Non hai i permessi per leggere questo modulo.",
      updateNotAllowed: "Non hai i permessi per modificare questo modulo.",
      deleteNotAllowed: "Non hai i permessi per eliminare questo modulo.",
    },
  },

  categories: {
    all: "Tutte le categorie",
    plural: "Categorie",
    createNew: "Crea nuova categoria",
    delete: "Elimina categoria",
    search: "Cerca categorie",
    view: "Visualizza categorie",
    edit: "Modifica categoria",

    questions: {
      delete: "Vuoi davvero eliminare questa categoria?",
    },

    errors: {
      notFound: "Categoria non trovata.",
      insertNotAllowed: "Non hai i permessi per inserire una nuova categoria.",
      readNotAllowed: "Non hai i permessi per leggere questa categoria.",
      updateNotAllowed: "Non hai i permessi per modificare questa categoria.",
      deleteNotAllowed: "Non hai i permessi per eliminare questa categoria.",
    },
  },

  roles: {
    plural: "Ruoli",
  },

  permissions: {
    plural: "Permessi",
  },

  secrets: {
    plural: "Secret",

    errors: {
      missingValue: "Valore secret mancante.",
    },
  },

  adminDashboard: {
    projects: "Visualizza e gestisci i progetti di tutti gli utenti",
    calculations: "Visualizza e gestisci i calcoli di tutti gli utenti",
    modules: "Configura i moduli di calcolo e le relative impostazioni",
    categories: "Gestisci le categorie usate per raggruppare i moduli",
    items: "Gestisci il catalogo degli articoli",
    clients: "Gestisci i client API, i relativi segreti e permessi",
    users: "Gestisci gli utenti e i relativi ruoli",
    accessControl: "Limita le applicazioni e gestisci gli utenti consentiti o bloccati",
    usageStatistics: "Visualizza quanti utenti accedono ogni giorno alle applicazioni e ai moduli",
    geoBlocking: "Consenti o blocca applicazioni e moduli per paese",
  },

  common: {
    date: "Data",
    active: "Attivo",
    add: "Aggiungi",
    errorInvalidVisibility: "Visibilità non valida: scegli pubblica, interna o nascosta.",
    administration: "Amministrazione",
    all: "Tutto",
    alreadyPresent: "Già presente/i",
    angle: "Angolo",
    angles: "Angoli",
    archived: "Archiviato",
    author: "Autore",
    automaticSync: "Sync automatico",
    back: "Indietro",
    baseHeight: "Altezza base",
    cancel: "Annulla",
    categories: "Categorie",
    category: "Categoria",
    clamped: "Bloccato",
    thickness: "Spessore",
    close: "Chiudi",
    code: "Codice",
    clearPoints: "Cancella punti",
    undo: "Annulla",
    components: "Componenti",
    confirmed: "Confermato",
    coordinates: "Coordinate",
    copy: "Copia",
    country: "Paese",
    create: "Crea",
    created: "Creato",
    createNew: "Crea nuovo",
    curve: "Curva",
    customer: "Cliente",
    dataHasBeenSavedSuccessfully: "I dati sono stati salvati correttamente.",
    default: "Predefinito",
    degrees: "Gradi",
    delete: "Elimina",
    description: "Descrizione",
    designer: "Progettista",
    details: "Dettagli",
    download: "Scarica",
    dragAndDropOrUploadAnImage: "Trascina e rilascia o clicca per carica un'immagine",
    draw: "Disegna",
    dropIt: "Rilascialo!",
    duplicate: "Duplica",
    edit: "Modifica",
    editor: "Editore",
    email: "E-mail",
    error: "Errore",
    errorLoadingData: "Errore caricamento dati",
    extensions: "Estensioni",
    factor: "Fattore",
    gap: "Fuga",
    failed: "Fallito/i",
    fieldContainsInvalidValueInsertBiggerOne: "Il campo contiene un valore non valido, inserirne uno più grande.",
    fieldInvalidValue: "Il campo contiene un valore non valido.",
    colorHexInvalid: "Inserisci un colore HEX valido nel formato #RRGGBB.",
    finish: "Fine",
    fixed: "Fisso",
    forbidden: "Non consentito",
    from: "Da",
    generalData: "Date generali",
    genericError: "Si è verificato un errore, si prega di riprovare.",
    groovingMinHeight: "Altezza min scanalatura",
    groovingMaxHeight: "Altezza max scanalatura",
    groovingThickness: "Spessore scanalatura",
    height: "Altezza",
    help: "Aiuto",
    hidden: "Nascosto",
    hide: "Nascondi",
    home: "Home",
    installed: "Installato",
    intermediate: "Intermedio",
    isVisible: "È visibile",
    languages: {
      arabic: "Arabo",
      chinese: "Cinese",
      croatian: "Croato",
      czech: "Ceco",
      danish: "Danese",
      dutch: "Olandese",
      english: "Inglese",
      estonian: "Estone",
      finnish: "Finlandese",
      french: "Francese",
      german: "Tedesco",
      greek: "Greco",
      hungarian: "Ungherese",
      icelandic: "Islandese",
      italian: "Italiano",
      japanese: "Giapponese",
      korean: "Coreano",
      latvian: "Lettone",
      lithuanian: "Lituano",
      norwegian: "Norvegese",
      plural: "Lingue",
      polish: "Polacco",
      portugese: "Portoghese",
      russian: "Russo",
      singular: "Lingua",
      slovak: "Slovacco",
      slovenian: "Sloveno",
      spanish: "Spagnolo",
      swedish: "Svedese",
      turkish: "Turco",
    },
    translations: "Traduzioni",
    lastAccess: "Ultimo accesso",
    lastModified: "Ultima modifica",
    lastName: "Cognome",
    lastSync: "Ultimo sync",
    length: "Lunghezza",
    screwDiameter: "Diametro vite",
    screwHeadHeight: "Altezza testa vite",
    loading: "Caricamento...",
    location: "Luogo",
    login: "Accedi",
    logout: "Logout",
    loop: "Anello",
    mappings: "Mappature",
    max: "Massimo",
    maxHeight: "Altezza massima",
    meters: "Metri",
    min: "Minimo",
    minHeight: "Altezza minima",
    minimumOrderQuantity: "Quantità minima ordinabile",
    minOrderQuantity: "Qtà min. ordinabile",
    minValue: "Valore minimo",
    minWidth: "Larghezza minima",
    myProfile: "Il Mio Profilo",
    name: "Nome",
    never: "mai",
    next: "Avanti",
    no: "No",
    noItemsFound: "Nessun elemento trovato",
    notFound: "Non trovato",
    offer: "Offerta",
    ok: "Ok",
    onlyImagesAreAllowed: "Sono ammesse solo immagini.",
    open: "Apri",
    optional: "opzionale",
    order: "Ordine",
    overwrite: "Sovrascrivere",
    owner: "Proprietario",
    pitch: "Pendenza",
    print: "Stampa",
    public: "Pubblico",
    everyOne: "Tutti",
    visibility: "Visibilità",
    unitSystem: "Sistema di unità",
    unitSystemMetric: "Metrico",
    unitSystemImperial: "Imperiale",
    remove: "Rimuovi",
    removeImage: "Rimuovi immagine",
    reset: "Reimposta",
    resetToDefault: "Reimposta ai valori predefiniti",
    resetView: "Reimposta vista",
    resourceNotFound: "La risorsa da lei cercata non è stata trovata.",
    roof: "Tetto",
    rothoblaas: "Rothoblaas",
    rothoblaasUsers: "Utenti Rothoblaas",
    save: "Salva",
    search: "Cerca",
    select: "Seleziona",
    selectProjectInwhichCreateCopy: "Seleziona il progetto nel quale creare la copia",
    sentAt: "Inviato il",
    sessionExpired: "Sessione scaduta",
    settings: "Impostazioni",
    shared: "Condiviso",
    sharedByMe: "Condiviso da me",
    sharedWithMe: "Condiviso con me",
    canEdit: "Può modificare",
    viewOnly: "Sola visualizzazione",
    sharedViaProject: "Condiviso tramite il progetto",
    shape: "Forma",
    show: "Mostra",
    span: "Luce",
    spans: "Luci",
    status: "Stato",
    stripeLength: "Lunghezza striscia",
    substructureHeight: "Altezza sottostruttura",
    substructures: "Sottostrutture",
    support: "Supporto",
    supportHeight: "Altezza supporto",
    supports: "Supporti",
    sync: "Sync",
    testEnvironment: "Ambiente di test",
    to: "A",
    toBeInstalled: "Da installare",
    toInspect: "Da ispezionare",
    type: "Tipo",
    unlock: "Sblocca",
    update: "Aggiorna",
    value: "Valore",
    visible: "Visibile",
    width: "Larghezza",
    quantity: "Quantità",
    yes: "Si",
    chooseSecondPointDifferentXY: "Scegliere un secondo punto con x e y differenti.",
    zoomIn: "Aumenta zoom",
    zoomOut: "Riduci zoom",
    youAreNotAllowed: "Non sei autorizzato ad accedere a questa risorsa.",
    area: "Area",
    units: "Unità",
    volume: "Volume",
    preview: "Anteprima",
    summary: "Riepilogo",

    unitM: "m",
    unitCm: "cm",
    unitGPa: "GPa",
    unitMm: "mm",
    unitInch: "in",
    unitN: "N",
    unitNMm: "N·mm",
    unitDeg: "°",
    unitPcs: "pz",
    unitM2: "m²",
    unitM4: "m^4",
    unitKn: "kN",
    unitKnM2: "kN/m²",
    unitKnM: "kN/m",
    unitMpa: "MPa",
    titlePreview: "Anteprima",
    wasteFactor: "Fattore di scarto",

    cookiePolicy: "Informativa sui cookie",
    privacyPolicy: "Informativa sulla privacy",
    termsAndConditions: "Termini e condizioni",
    writePermissions: "Permessi di scrittura",
    invitationHasBeenSentSuccessfully: "Invito inviato con successo.",
    resendInvite: "Reinvia invito",
    cancelInviteQuestion: "Vuoi davvero annullare questo invito?",
    deleteInviteQuestion: "Vuoi davvero eliminare questo invito?",
    invite: "Invita",
    singleOptionAvailable:
      "Campo disabilitato: in base alla configurazione scelta è disponibile una sola opzione selezionabile.",
  },
  concreteResinModule: {
    addCalculation: "Salva calcolo",
    anchorMapping: "Mappatura ancorante",
    anchorMappings: "Mappature ancoranti",
    cartridges: "Cartucce",
    chemicalAnchor: "Ancorante chimico",
    chemicalAnchors: "Ancoranti chimici",
    downloadPdf: "Scarica PDF",
    downloadPdfNoCalculationsSavedHint: "Salva almeno un calcolo per scaricare il PDF.",
    holeDepth: "Profondità foro ( h₁ )",
    holeDiameter: "Diametro foro ( d₀ )",
    holeDiameterImperial: "Punta USA [in]",
    holeDiameterImperialInfo: 'Calcolata dal diametro foro: la punta da 1/64" più piccola non inferiore ad esso.',
    holeNumbers: "Numero fori",
    holes: "fori",
    instruction2:
      "Questo calcolo stima la quantità di resina in base al fattore di eccedenza selezionato. L'eccedenza dipende spesso dall'applicazione e dall'esperienza dell'installatore.",
    instructionsSummaryPrefix:
      "La quantità di resina è stimata sul riempimento del foro indicato nell'ETA del prodotto: partendo dal fondo del foro pulito, riempirlo con l'adesivo fino a circa due terzi, ritirando lentamente l'ugello di miscelazione statica man mano che il foro si riempie, per evitare sacche d'aria; se l'ugello non raggiunge il fondo del foro, utilizzare un'apposita prolunga. Applicare e iniettare il prodotto secondo la documentazione Rothoblaas per l'uso previsto. Rothoblaas si riserva il diritto di modificare metodi di calcolo, design o modelli senza preavviso né responsabilità; le risorse di progettazione più aggiornate sono su",
    instructionsSummarySuffix: ".",
    liters: "Litri",
    mlQuantity: "Quantità ml",
    noCalculationsSaved: "Nessun calcolo salvato",
    overageFactor: "Fattore di sfrido",
    rodDiameter: "Diametro barra ( d )",
    titleCalculationsSaved: "Calcoli salvati",
    titleHoleGeometry: "Geometria foro",
    titleInstructionsAndNotes: "Istruzioni e note",
    titleSummary: "Riepilogo",
    totalVolumeRequired: "Volume totale richiesto",
    unitOverageFactor: "%",
    unitSystemImperial: "Imperiale",
    unitSystemMetric: "Metrico",
    waste: "sfrido",
    cartridgesShort: "cart.",
    unitLiters: "L",
    errors: {
      anchorInUseByMapping: "L'ancorante è usato da una mappatura: elimina prima le sue mappature",
      anchorInUseByEntry: "L'ancorante è usato in calcoli salvati: impostalo come Nascosto invece di eliminarlo",
      anchorNotFound: "Ancorante chimico non trovato.",
      anchorNotAvailable: "L'ancorante chimico selezionato non è più disponibile: scegline un altro.",
      anchorAlreadyPresent: "L'articolo è già un ancorante chimico.",
      mappingAlreadyPresent: "L'ancorante chimico ha già una mappatura con questi diametri.",
      invalidRodDiameter: "Il diametro della barra non è disponibile per l'ancorante chimico selezionato.",
      invalidHoleDiameter: "Il diametro del foro non corrisponde al diametro della barra selezionato.",
      mappingNotFound: "Mappatura ancorante non trovata.",
      entryMissingChemicalAnchor: "Scegli un ancorante chimico prima di salvare il calcolo",
      entryIncompleteCalculation: "Compila tutti i campi prima di salvare il calcolo",
    },
  },

  usLiftingCalculatorModule: {
    calculator: {
      aboveTarget: (target: number) => `Il sollevamento rientra nella capacità ma supera l'obiettivo del ${target}%.`,
      allSavedMembersCleared: "Tutti gli elementi salvati sono stati cancellati.",
      allowAnotherDevice: "Consentire un altro dispositivo se quello selezionato non offre configurazioni verificate.",
      alternateDevice: "Nessuna configurazione verificata con il dispositivo di sollevamento selezionato.",
      anchorPointsDetail: "Numero di punti di ancoraggio",
      angleDiagramAlt: "Intervalli dell'angolo di sollevamento del dispositivo e del tipo di sollevamento selezionati",
      angleDiagramTitle: "Intervalli dell'angolo di sollevamento",
      angleFromHorizontal: "Angolo rispetto all'orizzontale",
      angleLegendTitle: "Legenda degli intervalli di angolo",
      anglePhotoTitle: "Angolo di sollevamento",
      applicableLifts: "Sollevamenti applicabili",
      automatic: "Automatico",
      backToModeSelection: "Torna alla selezione modalità",
      beamDepth: "Altezza trave",
      beamDepthDetail: "Altezza trave",
      beamOrientationAlt: (orientation: string) => `RAPTOR sulla trave: ${orientation}`,
      beamSpecifications: (depth: string, width: string) => `altezza ${depth} × larghezza ${width}`,
      beamWidth: "Larghezza trave",
      beamWidthDetail: "Larghezza trave",
      buildingPdf: "Creazione del PDF…",
      capacityTablePreview: "Anteprima capacità del singolo dispositivo di sollevamento",
      capacityUtilization: "Utilizzo della capacità",
      clearSavedMembers: "Cancella elementi salvati",
      clearSavedMembersConfirm:
        "Sei sicuro di voler cancellare tutti gli elementi salvati? Verranno rimossi i sollevamenti salvati da tutte le modalità e l’operazione non può essere annullata.",
      clearSavedMembersTitle: "Cancellare gli elementi salvati?",
      cltPanelDepthDetail: "Spessore pannello CLT",
      cltThickness: "Spessore CLT",
      columnSpecifications: (width: string) => `larghezza ${width}`,
      columnWidth: "Larghezza pilastro",
      columnWidthDetail: "Larghezza pilastro",
      completedExcelTemplate: "Modello Excel compilato",
      contactTechSupport: "Contatta l'assistenza tecnica",
      contactUs: "Contatti",
      copied: "Copiato",
      copyFailed: "Copia non riuscita",
      copyTable: "Copia tabella",
      customLiftDesign: "Progettazione di sollevamento personalizzata",
      datasheetNoticeText:
        "Consultare la scheda tecnica di ciascun dispositivo di sollevamento per le condizioni d'uso, le istruzioni di installazione, le capacità dei fissaggi, gli orientamenti di sollevamento approvati e tutti i requisiti e le limitazioni applicabili.",
      datasheetNoticeTitle: "Questo strumento non sostituisce le schede tecniche applicabili.",
      decreaseByEighth: "Riduci di 1/8 di pollice",
      deviceFamilyUnavailable: (name: string) => `${name} — Non disponibile`,
      deviceImageAlt: "Dispositivo di sollevamento selezionato",
      devices: "Dispositivi di sollevamento",
      dimensionFormatError: "Inserire la dimensione in pollici, ad es. 4 1/8.",
      downloadExcel: "Scarica Excel",
      downloadTemplate: "Scarica modello",
      downloaded: "Scaricato",
      draftOpened: "Bozza aperta",
      eServiceTemplateRequired: "È richiesto il modello eService approvato",
      eServiceXlsx: "eService XLSX",
      editingMember: (name: string) => `Modifica di ${name}`,
      engineeringReviewRequired: "Revisione tecnica richiesta",
      enlargeImage: "Ingrandisci immagine",
      enterLiftName: "Inserisci un nome per il sollevamento.",
      enterNameOrSave: "Inserisci un nome elemento oppure seleziona Salva sollevamento per assegnarlo nella finestra.",
      enterWeightBeforeSaving: "Inserisci il peso dell'elemento sollevato prima di salvare.",
      enterWeightForRecommendation: "Inserisci il peso dell'elemento sollevato per ottenere una raccomandazione.",
      export: "Esporta",
      exportFailed: "Esportazione non riuscita",
      exportFilledExcel: "Esporta Excel compilato",
      exportHelp: "Scegli cosa inviare al rapporto PDF.",
      exportMemberExcel: "Esporta Excel degli elementi",
      exportReport: "Esporta rapporto",
      fastenerTakeoff: "Distinta dei fissaggi",
      guidanceApplication: "Applicazione",
      guidanceInstallation: (device: string) => `Installazione ${device}`,
      guidanceLabel: "Indicazioni di installazione",
      guidanceLoadDirections: "Direzioni di carico consentite",
      guidanceMinimumDistances: "Distanze minime",
      guidanceMinimumUnavailable: "Inserire una larghezza valida per visualizzare le distanze minime.",
      howRecommendationsSelected: "Come vengono scelte le raccomandazioni",
      increaseByEighth: "Aumenta di 1/8 di pollice",
      installation: "Installazione",
      installationIllustrations: "Illustrazioni di installazione",
      installationIllustrationsTitle: "WASP / WASPL - Illustrazioni di installazione",
      installationLine: (installation: string) => `Installazione: ${installation}`,
      installationType: "Tipo di installazione",
      installationTypeColumn: "Tipo di installazione",
      installationTypes: {
        inclined: "Inclinata",
        perpendicular: "Perpendicolare con fresatura",
        standard: "Standard",
      },
      issues: {
        angleUnsupported: "Selezionare un angolo di sollevamento supportato. L'angolo inserito non è stato sostituito.",
        beamDepthMaximum: (maximum: string) => `L'altezza della trave non deve superare ${maximum}.`,
        beamDepthMinimum: (minimum: string) =>
          `L'altezza della trave deve essere almeno ${minimum}. La dimensione inserita non è stata modificata.`,
        beamDepthPositive:
          "L'altezza della trave deve essere un numero positivo. La dimensione inserita non è stata modificata.",
        beamOrientationRestricted:
          "L'orientamento RAPTOR selezionato è limitato dalla larghezza della trave e dal numero di viti.",
        beamWidthMaximum: (maximum: string) => `La larghezza della trave non deve superare ${maximum}.`,
        beamWidthMinimum: (minimum: string) =>
          `La larghezza della trave deve essere almeno ${minimum}. La larghezza inserita non è stata modificata.`,
        beamWidthPositive:
          "La larghezza della trave deve essere un numero positivo. La larghezza inserita non è stata modificata.",
        cltEdgeGrainMinimum: (minimum: string) =>
          `Lo spessore CLT deve essere almeno ${minimum} per i sollevamenti sul bordo e per ribaltamento.`,
        cltFaceGrainMinimum: (minimum: string, device: string) =>
          `Lo spessore CLT deve essere almeno ${minimum} per i sollevamenti ${device} sulla faccia.`,
        cltSlabMaximum: (maximum: string) => `Lo spessore del solaio CLT non deve superare ${maximum}.`,
        cltThicknessPositive:
          "Lo spessore CLT deve essere un numero positivo. La dimensione inserita non è stata modificata.",
        columnWidthMinimum: (minimum: string) =>
          `La larghezza del pilastro deve essere almeno ${minimum}. La dimensione inserita non è stata modificata.`,
        columnWidthPositive:
          "La larghezza del pilastro deve essere un numero positivo. La dimensione inserita non è stata modificata.",
        memberDepthMaximum: (maximum: string) => `La dimensione dell'elemento non deve superare ${maximum}.`,
      },
      liftName: "Nome sollevamento",
      liftNamePlaceholder: "Inserisci un nome per il sollevamento",
      liftNotOverwritten: "Impossibile sovrascrivere il sollevamento.",
      liftNotSaved: "Impossibile salvare il sollevamento.",
      liftOverwritten: (name: string) => `${name} è stato sovrascritto con le proprietà e la raccomandazione attuali.`,
      liftRecommendation: "Raccomandazione di sollevamento",
      liftSaved: (name: string) => `${name} è stato salvato con le proprietà e la raccomandazione attuali.`,
      liftType: "Tipo di sollevamento",
      liftTypeFallbackNote:
        "Nessuna configurazione verificata per il ribaltamento. Come alternativa è mostrato il sollevamento verticale sulla faccia; questo non verifica un ribaltamento.",
      liftedMemberWeight: "Peso dell'elemento sollevato",
      liftingDevice: "Dispositivo di sollevamento",
      liftingPoints: "Punti di sollevamento",
      liftingSetup: "Configurazione di sollevamento",
      liftingSetupValue: (screws: number | string, devices: number | string) =>
        `${screws} viti × ${devices} dispositivi`,
      marginLine: (margin: string) => `Margine: ${margin}`,
      memberLoaded: (name: string) => `${name} è stato caricato.`,
      memberName: "Nome elemento",
      memberNamePlaceholder: "Inserisci un nome elemento",
      memberSpecifications: "Specifiche dell'elemento",
      memberType: "Tipo di elemento",
      memberTypes: {
        beam: "Trave",
        "clt-slab": "CLT Slab",
        "clt-wall": "CLT Wall",
        column: "Pilastro",
      },
      memberWeight: "Peso dell'elemento",
      memberWorkspace: "Area elementi",
      minimumDistancesCaption: (mounting: string, screwCount: number | string) =>
        `${mounting} · Viti per dispositivo: ${screwCount}`,
      mode1Description: "Configura e verifica un sollevamento specifico.",
      mode1Pill: "Modalità 1",
      mode2Description: "Inserisci un elemento per trovare opzioni di sollevamento.",
      mode2Eyebrow: "Modalità 2: Raccomandazione di sollevamento",
      mode2Pill: "Modalità 2",
      mode3Description: "Carica più elementi per ottenere consigli di sollevamento.",
      mode3Pill: "Modalità 3",
      modeSelection: "Selezione modalità",
      moduleTitle: "US Lifting Calculator",
      nameInUse: (name: string) =>
        `Un sollevamento salvato si chiama già “${name}”. Sovrascrivilo oppure inserisci un altro nome.`,
      newMember: "Nuovo elemento",
      noCapacitySubtitle: "La combinazione selezionata non è presente nelle tabelle riepilogative incorporate.",
      noCapacityTable: "Per questa configurazione non è indicata alcuna tabella di capacità.",
      noSavedLifts: "Non sono disponibili sollevamenti salvati.",
      noSavedMembers: "Nessun elemento salvato",
      noSavedMembersToProcess: "Salva prima un elemento in Modalità 1 o 2.",
      noSavedRecommendationLifts: "Non sono disponibili sollevamenti salvati della Modalità 2.",
      noScrews: (filters: string[]) => `Nessuna vite ${filters.join(" e ")}`,
      noScrewsListed: "Nessuna vite indicata",
      notAvailableYet: "Disponibile in una versione successiva.",
      numberOfAnchorPoints: "Numero di punti di ancoraggio",
      numberOfLiftingDevices: "Numero di dispositivi",
      numberOfRaptors: "Numero di RAPTOR",
      openEmailEnquiry: "Apri richiesta e-mail",
      openInMode1: "Apri in Modalità 1",
      openInMode2: "Apri in Modalità 2",
      openOrCreateMember: "Apri o crea un elemento",
      openSavedSelect:
        "Apri ogni sollevamento salvato, seleziona un'opzione con capacità verificata per la distinta e salvalo di nuovo.",
      openSupportMenu: "Apri il menu di assistenza",
      optionAlternativeTitle: (slot: number, label: string) => `Opzione ${slot} - ${label} alternativa`,
      optionTitle: (slot: number, label: string) => `Opzione ${slot} - ${label}`,
      orientation: "Orientamento",
      orientationUnavailable: (orientation: string) =>
        `${orientation} - Non disponibile con questa larghezza della trave`,
      overwriteExisting: "Sovrascrivi esistente",
      overwritten: "Sovrascritto",
      parallel: "Parallelo",
      parallelMounting: "Montaggio parallelo",
      perpendicular: "Perpendicolare",
      perpendicularMounting: "Montaggio perpendicolare",
      perpendicularWithMillingShort: "Perp. con fresatura",
      printCurrentScreen: "Stampa schermata corrente",
      printCurrentScreenHelp: "Il sollevamento sullo schermo, con il risultato e le immagini di riferimento.",
      printPdf: "Stampa PDF",
      printSavedMembers: "Stampa elementi salvati",
      printSavedMembersHelp:
        "Ogni sollevamento salvato è stampato su una pagina, seguito da una distinta dei fissaggi complessiva quando sono disponibili sollevamenti della Modalità 2.",
      processList: "Elabora elenco",
      processSavedMembers: "Elabora elenco degli elementi salvati",
      productCode: "Codice prodotto",
      productPage: "Pagina prodotto",
      properties: "Proprietà",
      quantityRequired: "Quantità richiesta",
      raptors: "RAPTOR",
      readyToSave: "La raccomandazione attuale è pronta per essere salvata.",
      reasons: {
        noAdditionalVgspl:
          "Nessun numero di viti o lunghezza VGSPL aggiuntivo verificato è disponibile con queste impostazioni.",
        noVgsplAlternative: "Nessuna alternativa VGSPL verificata è disponibile con queste impostazioni.",
        notEligible: (family: string) =>
          `Nessuna configurazione ${family} ammissibile è disponibile con queste impostazioni.`,
        notWithinCapacity: (family: string) =>
          `Nessuna configurazione ${family} rientra nella capacità con queste impostazioni.`,
        waspNoAlternative: (series: string) =>
          `Nessuna alternativa ${series} verificata è disponibile con WASP con queste impostazioni.`,
        waspNotWithinCapacity: (series: string) =>
          `Nessuna configurazione ${series} rientra nella capacità con queste impostazioni.`,
      },
      recommendedConfigurations: "Configurazioni consigliate",
      recommendedLiftConfiguration: "Configurazione di sollevamento consigliata",
      requiredFields: (fields: string) => `richiesti: ${fields}`,
      reset: "Ripristina",
      result: "Risultato",
      review: {
        allSupportedDevices: "Tutti i dispositivi supportati",
        determinism:
          "La ricerca nelle tabelle è deterministica, non affidata a un'IA in tempo reale. I selettori mostrano angoli tabellati distinti; gli schemi degli intervalli restano riferimenti. L'aggiornamento non costituisce approvazione tecnica.",
        deviceScope: "Dispositivi inclusi nella revisione",
        methodChecks:
          "Controllare dimensioni, orientamento, angolo tabellato, lunghezza e numero di viti prima dell'ordinamento. Senza dati obbligatori il sollevamento non è verificabile.",
        methodRaptor:
          "I sollevamenti RAPTOR di travi e sulla faccia confrontano VGSPL, un'alternativa VGSPL con numero o lunghezza di viti diversi e HBSPL. I sollevamenti RAPTOR MINI e quelli RAPTOR sul bordo o per ribaltamento confrontano solo VGSPL e HBSPL.",
        methodWasp: "Se consentito: WASP → WASPL → RAPTOR MINI → RAPTOR.",
        raptorNote: "Le immagini di applicazione e distanze minime RAPTOR sono mostrate con i risultati.",
        raptorOnly: "Solo RAPTOR",
        scopeNote: "Limitare a RAPTOR restringe selezione e ricerca, senza costituire validazione.",
      },
      reviewSettings: "Raccomandazioni e impostazioni di revisione",
      riggingBasis:
        "Capacità totale dell'imbracatura = numero di ancoraggi x capacità del singolo dispositivo al limite inferiore, più conservativo, dell'intervallo di angolo selezionato (o esattamente 90 gradi). La ripartizione uniforme del carico è ipotizzata, non confermata da questo strumento.",
      riggingMemo:
        "Questo orientamento non è approvato dalla scheda tecnica. Applicazioni con più punti di ancoraggio rispetto alle configurazioni pubblicate sono possibili se progettate da un ingegnere qualificato esterno per l'imbracatura.",
      saveLift: "Salva sollevamento",
      saveMember: "Salva elemento",
      saveMode1Lift: "Salva sollevamento Modalità 1",
      saveMode2Lift: "Salva sollevamento Modalità 2",
      saved: "Salvato",
      savedLiftConfigurations: "Configurazioni di sollevamento salvate",
      savedLiftCount: (count: number) => (count === 1 ? "1 sollevamento salvato" : `${count} sollevamenti salvati`),
      savedLiftLoaded:
        "Sollevamento salvato caricato. Modifica una proprietà e seleziona Salva sollevamento per aggiornarlo.",
      savedLifts: "Sollevamenti salvati",
      savedLiftsEyebrow: "Sollevamenti salvati",
      savedLiftsFileSuffix: "sollevamenti-salvati",
      savedMembers: "Elementi salvati",
      savedMembersAvailable: (count: number) => `Elementi salvati disponibili: ${count}`,
      savedMembersDescription:
        "Genera raccomandazioni dalle proprietà salvate senza caricare Excel. I sollevamenti salvati non vengono modificati.",
      savedOn: (mode: string, date: string) => `${mode} · Salvato il ${date}`,
      savedPill: "Salvati",
      savedRecommendations: "Raccomandazioni salvate",
      savedResult: "Risultato salvato",
      screw: "Vite",
      screwCountBeamWidth: (count: number) => `${count} - Non disponibile con questa larghezza della trave`,
      screwCountNotListed: (count: number) => `${count} — non indicato`,
      screwLength: "Lunghezza vite (in)",
      screwLengthOption: (length: string, code: string) => `${length} — ${code}`,
      screwLengthPhotoAlt: "Lunghezza della vite rispetto allo spessore dell'elemento",
      screwLengthReferenceText:
        "Per le applicazioni di sollevamento con componente del carico perpendicolare alla fibratura, per raggiungere la capacità massima del sistema di sollevamento si raccomanda che la lunghezza della vite infissa nel legno, esclusa la piastra RAPTOR da 10 mm, superi 0,7·d (L - 10 mm > 0,7·d; d = spessore dell'elemento in legno) per evitare fessurazioni. Se il rapporto non è soddisfatto, le verifiche critiche a taglio e di deformazione possono essere eseguite secondo AWC-NDS, capitoli 3 e 5.",
      screwLengthReferenceTitle: "Influenza del rapporto tra lunghezza della vite e spessore dell'elemento",
      screwLengthViewerTitle: "Riferimento per la lunghezza della vite",
      screwType: "Tipo di vite",
      screwsAtLeast: (minimum: string) => `di almeno ${minimum}`,
      screwsColumn: "N. viti",
      screwsLongerThan: (minimum: string) => `più lunga di ${minimum}`,
      screwsPerDevice: "Viti per dispositivo",
      screwsPerDeviceDetail: "Viti per dispositivo",
      screwsPerRaptor: "Viti per RAPTOR",
      screwsPerRaptorFigure: (count: number) => `${count} viti per RAPTOR`,
      screwsPerRaptorFigureAlt: (count: number) => `RAPTOR installato con ${count} viti`,
      screwsRow: (count: number) => `${count} viti`,
      screwsShorterThan: (maximum: string) => `più corta di ${maximum}`,
      selectCompletedTemplate: "Seleziona un modello compilato per iniziare.",
      selectListedScrew: "Seleziona una configurazione di viti indicata prima di salvare.",
      selectNewMember: "Seleziona Nuovo elemento per creare un sollevamento salvato oppure apri un elemento salvato.",
      selectedConfiguration: "Configurazione selezionata",
      shortScrewEmbedment: "Il fissaggio attuale non penetra per 0,7*d",
      shortScrewRatio: (length: string) =>
        `La vite selezionata è più corta della lunghezza raccomandata di 0,7d (${length}). Eseguire le verifiche critiche a taglio e di deformazione secondo AWC-NDS, capitoli 3 e 5. Queste verifiche non sono eseguite da questo strumento.`,
      specificGravity: "Peso specifico",
      statusAboveTarget: "Verificato - oltre l'utilizzo obiettivo",
      statusAlternativeLift: "Capacità verificata - sollevamento alternativo",
      statusDimension: "Dimensione fuori intervallo",
      statusEngineeringReview: "Verificato — Richiesta revisione tecnica",
      statusNoCapacity: "Capacità non indicata",
      statusNotVerified: "Non verificato",
      statusOrientation: "Orientamento non disponibile",
      statusPending: "In attesa",
      statusReview: "Revisione",
      statusUnavailable: "Non disponibile",
      statusVerified: "Verificato",
      submitFeedback: "Invia feedback",
      takeoffEmailBasis: "Base di calcolo: USA - NDS",
      takeoffEmailIncluded: (count: number) => `Sollevamenti salvati della Modalità 2 inclusi: ${count}`,
      takeoffEmailRequest: "Si prega di verificare disponibilità e prezzi per le quantità indicate.",
      takeoffEmailSubject: "US Lifting Calculator - distinta dei fissaggi",
      takeoffEmailTotal: (total: string) => `Fissaggi totali: ${total}`,
      takeoffEmailUnits: "Unità: imperiali (lb / in)",
      takeoffLength: "Lunghezza",
      takeoffTotal: "TOTALE",
      targetUtilization: "Imposta utilizzo obiettivo (%)",
      targetUtilizationShort: "Utilizzo obiettivo",
      techSupportSubject: "US Lifting Calculator - assistenza tecnica",
      template: "Modello",
      templateDownloaded: (device: string) => `Modello ${device} scaricato in unità imperiali (lb / in).`,
      totalCapacity: "Capacità totale",
      totalFasteners: "Fissaggi totali",
      totalRiggingCapacity: "Capacità totale dell'imbracatura",
      totalScrews: "Viti totali",
      unitIn: "in",
      unitLb: "lb",
      upload: {
        columns: {
          anchors: "N. ancoraggi",
          angle: "Angolo",
          liftingDevice: "Dispositivo di sollevamento",
          memberName: "Nome elemento",
          memberType: "Tipo di elemento",
          screw: "Vite",
          screwCount: "N. viti",
          status: "Stato",
          totalCapacity: "Capacità di sollevamento totale",
          totalScrews: "Numero totale di viti",
          utilization: "Utilizzo",
          weight: "Peso",
        },
        defaultAngle: "(predefinito)",
        enteredTableReference: "Angolo inserito → riferimento tabellare",
        externalRigging: "Progetto dell'imbracatura esterno richiesto",
        faceGrainAlternative: "Alternativa verticale sulla faccia; non è una verifica del ribaltamento.",
        fields: {
          angle: "Angolo di sollevamento",
          columnNotWithWasp: "Il pilastro non è disponibile con WASP / WASPL",
          columnRequiresRaptorMini: "Il pilastro richiede RAPTOR MINI",
          device: "Dispositivo di sollevamento",
          installationType: "Tipo di installazione",
          liftType: "Tipo di sollevamento",
          memberType: "Tipo di elemento",
          thicknessDepth: "Spessore / altezza",
          weight: "Peso",
        },
        firstRows: (shown: number, total: number) =>
          `Sono mostrati i primi ${shown} di ${total} elementi. L'Excel compilato li include tutti.`,
        issues: {
          angleColumn: "Il sollevamento dei pilastri richiede esattamente 90 gradi dall'orizzontale.",
          angleNotNumeric:
            "L'angolo di sollevamento deve essere un angolo numerico dall'orizzontale o un intervallo mostrato per il dispositivo selezionato.",
          angleOutOfRange: (minimum: number) =>
            `L'angolo di sollevamento deve essere tra ${minimum} e 90 gradi dall'orizzontale per il dispositivo selezionato. L'angolo inserito non è stato modificato.`,
          angleOutsideBand:
            "L'angolo di imbracatura inserito è fuori dall'intervallo tabellare selezionato. Reinserire e ricalcolare l'angolo effettivo; un angolo di imbracatura alternativo non è verificato.",
          angleRiggingMismatch:
            "L'angolo proposto non corrisponde all'imbracatura inserita. Correggere i dati prima di usare un risultato.",
          beamWidthRequired: "La larghezza della trave è obbligatoria per le righe di travi.",
          materialConflict: (text: string) =>
            `Nome del materiale e peso specifico in conflitto in "${text}". Confermare il materiale previsto; nessun valore è stato sostituito.`,
          materialUnrecognized: (text: string) =>
            `Materiale legnoso non riconosciuto "${text}". Usare un materiale elencato, DF, SYP o un peso specifico numerico supportato.`,
          materialUnsupported: (text: string, choices: string) =>
            `Peso specifico non supportato "${text}". Selezionare ${choices}; il valore inserito non è stato sostituito.`,
          orientationInvalid: "L'orientamento deve essere Parallelo o Perpendicolare.",
          orientationRestricted: "L'orientamento RAPTOR richiesto è limitato dalla larghezza della trave.",
          processingFailed: "Questo elemento non ha potuto essere elaborato.",
        },
        missing: (fields: string) => `Mancanti o non validi: ${fields}.`,
        notices: {
          andMore: (names: string, more: number) => `${names} e altri ${more}`,
          anglesMapped: (count: number) =>
            `${count === 1 ? "1 angolo è stato associato" : `${count} angoli sono stati associati`} agli intervalli tabellari: il valore inferiore dell'intervallo è il riferimento tabellare. L'angolo di imbracatura inserito non cambia; deve restare in quell'intervallo.`,
          appliesTo: (names: string) => `Si applica a: ${names}.`,
          materialDefault: (value: string, names: string) =>
            `G = ${value} è stato usato per i materiali vuoti: ${names}. Confermare il materiale legnoso.`,
          vgs13: "Le viti VGS13 e i relativi carichi di lavoro si applicano solo a WASPL.",
        },
        processed: (members: number, verified: number, saved: number) =>
          `${members === 1 ? "Elaborato 1 elemento" : `Elaborati ${members} elementi`}. ${verified} con raccomandazioni verificate. ${saved} salvati come elementi.`,
        processedResults: "Risultati elaborati",
        processedSaved: (members: number, verified: number) =>
          `Elaborati ${members} elementi salvati; ${verified} con raccomandazioni verificate. I sollevamenti salvati restano invariati.`,
        processing: "Elaborazione della cartella di lavoro...",
        processingSaved: "Elaborazione degli elementi salvati...",
        requestedUsed: "Richiesto → Usato",
        unverified: "Non verificato",
        verified: "Verificato",
        wasplOnlyShort: "Solo WASPL",
        workbookExported: "Foglio Excel compilato esportato.",
      },
      uploadFilledTemplate: "Carica modello compilato",
      uploadList: "Carica elenco per raccomandazioni",
      uploadListEyebrow: "Carica elenco",
      uploadListFileSuffix: "elenco-caricato",
      uploadMemberList: "Carica elenco elementi",
      useForTakeoff: "Usa questa opzione per la distinta dei sollevamenti salvati",
      utilization: "Utilizzo",
      verifiedRecommendationRequired:
        "Per salvare questo sollevamento è necessaria una raccomandazione con capacità verificata.",
      viewSavedLifts: "Visualizza sollevamenti salvati",
      wallPanelLiftType: "Tipo di sollevamento del pannello parete",
      waspOnly: "Solo WASP - questa configurazione non è consentita con WASPL.",
      wasplOnly: "Solo WASPL - questa configurazione non è consentita con WASP.",
      weight: "Peso",
      weightLabel: "Peso dell'elemento sollevato",
      weightPlaceholder: "Digita il peso, ad es. 2500",
      woodSpecificGravity: "Tipo di legno / peso specifico",
      woodSpecificGravityShort: "Legno / peso specifico",
    },
    generalPrinciples: {
      title: (device: string) => `${device} — Principi generali`,
      introRaptor:
        "Dalla scheda tecnica RAPTOR fornita. Questi principi sono specifici per RAPTOR; per gli altri dispositivi di sollevamento consultare la relativa scheda tecnica.",
      introRaptorMini: "Dalla scheda tecnica RAPTOR MINI fornita. Questi principi sono specifici per RAPTOR MINI.",
      introWasp: "Dalla scheda tecnica WASP / WASPL fornita. Questi principi valgono sia per WASP sia per WASPL.",
      fastenerLengthPlate:
        "La lunghezza del connettore va scelta di volta in volta in base alle dimensioni dell'elemento in legno, al posizionamento del connettore, all'angolo di sollevamento, al peso del carico da sollevare e alla disposizione della piastra di sollevamento. In ogni caso si raccomanda che i connettori abbiano una lunghezza maggiore del minimo richiesto, ma tale che la punta non fuoriesca dall'elemento da sollevare.",
      fastenerLength:
        "La lunghezza del connettore va scelta di volta in volta in base alle dimensioni dell'elemento in legno, al posizionamento del connettore, all'angolo di sollevamento e al peso del carico da sollevare. In ogni caso si raccomanda che i connettori abbiano una lunghezza maggiore del minimo richiesto, ma tale che la punta non fuoriesca dall'elemento da sollevare.",
      wllBasis:
        "I valori di capacità di sollevamento (WLL) indicati si basano su calcoli eseguiti secondo l'ICC-ES Evaluation Report ESR-4645, l'AWC-NDS 2018, l'AWC Technical Report 12 e sui risultati delle prove eseguite. La documentazione di riferimento indica un coefficiente di sicurezza pari o superiore a 5,0. I requisiti applicabili della ",
      oshaLink: "sezione OSHA 1926.251",
      wllBasisAfterLink:
        " devono essere verificati per la specifica operazione di sollevamento. Questo calcolatore non attesta la conformità OSHA né approva l'intero sistema di imbracatura.",
      specificGravityBasis: (maxCapacity: string) =>
        `Come base di calcolo è stato considerato un peso specifico G = 0,42. Per specie legnose con peso specifico diverso è stata utilizzata la seguente tabella di conversione con i fattori di modifica. I valori non superano mai la capacità massima della piastra di ${maxCapacity}.`,
      specificGravityBasisWasp: (waspCapacity: string, wasplCapacity: string) =>
        `Come base di calcolo è stato considerato un peso specifico G = 0,42. Per specie legnose con peso specifico diverso è stata utilizzata la seguente tabella di conversione con i fattori di modifica. I valori non superano mai la capacità massima di ${waspCapacity} per WASP e di ${wasplCapacity} per WASPL.`,
      capacityFactors:
        "Tutti i valori di capacità delle viti sono corretti con un fattore di accelerazione dinamica di 1/1,1, un fattore di durata del carico CD = 1,25, un fattore di temperatura Ct = 1,0 e un fattore di servizio in ambiente umido CM = 1,0, e presuppongono che le viti siano installate in cantiere immediatamente prima del sollevamento.",
      brittleFailureElement:
        "Le verifiche di eventuali riduzioni di resistenza dovute a possibili rotture fragili legate alla geometria dell'elemento sollevato devono essere eseguite separatamente secondo AWC-NDS, in particolare quando il connettore impegna meno del 70% dell'altezza dell'elemento sollevato.",
      brittleFailureBeam:
        "Le verifiche di eventuali riduzioni di resistenza dovute a possibili rotture fragili legate alla geometria dell'elemento sollevato devono essere eseguite separatamente secondo AWC-NDS, in particolare quando il connettore impegna meno del 70% dell'altezza della trave.",
      loadToGrain:
        "Tutti i calcoli CLT assumono un angolo tra carico e fibratura di 90°, indipendentemente dall'orientamento, senza riduzioni della lunghezza del connettore per il sollevamento sul bordo.",
      otherConfigurationsPlate:
        "Per il calcolo della capacità di sollevamento della piastra in configurazioni di installazione diverse da quelle qui indicate, contattare l'Ufficio Tecnico Rothoblaas.",
      otherConfigurations:
        "Per il calcolo della capacità di sollevamento in configurazioni di installazione diverse da quelle qui indicate, contattare l'Ufficio Tecnico Rothoblaas.",
      fatigue: (device: string) =>
        `Per rispettare i limiti a fatica della Service Class 0 della ASME BTH01-2023, nessun dispositivo di sollevamento ${device} può essere utilizzato per più di 20.000 cicli di carico.`,
      noPreDrilling: (specificGravity: string) =>
        `I valori di capacità di sollevamento indicati sono calcolati per la piastra fissata con viti inserite senza preforo, secondo l'ICC Evaluation Report ESR-4645. Tutte le distanze minime devono essere aumentate per pesi specifici superiori a ${specificGravity}.`,
      installation:
        "I connettori devono essere installati secondo le relative istruzioni di installazione, per evitare danni e mantenere le piene prestazioni previste dai parametri di resistenza di progetto.",
      noImpactWrenches:
        "Non è consentito l'uso di avvitatori a impulsi/a massa battente. Rispettare l'angolo di inserimento con l'aiuto di un preforo e/o di una dima di installazione. Evitare la flessione. Assicurare il corretto serraggio. Si raccomanda l'uso di avvitatori a controllo di coppia, ad es. con TORQUE LIMITER. In alternativa, serrare con una chiave dinamometrica.",
      hbsPlateValues:
        "I valori indicati nella scheda tecnica per la piastra di trasporto fissata con viti HBS PLATE sono stati calcolati considerando la geometria e i parametri meccanici della versione HBS PL.",
      hbsPContact:
        "Per le capacità portanti della piastra di trasporto fissata con la vite HBS P, contattare l'Ufficio Tecnico per ulteriore supporto.",
      singleUse: (screws: string) =>
        `${screws} possono essere utilizzate una sola volta. Una volta serrate e caricate, le viti non devono essere allentate e riutilizzate per fissare la piastra di trasporto. Non appena l'elemento in legno da trasportare è stato sollevato nella posizione finale e la piastra di trasporto non serve più a questo scopo, le viti devono essere svitate e smaltite correttamente.`,
      screwsHbsPlateVgs: "Le viti HBS PLATE e VGS",
      screwsVgs: "Le viti VGS",
      vgsPlateReuse: (device: string) =>
        `VGS PLATE è l'unico connettore riutilizzabile. Per l'uso con il dispositivo ${device}, fare riferimento alle schede tecniche e ai manuali di istruzioni di VGS PLATE e della relativa JIG REUSE.`,
      qualifiedPersonnelPlate:
        "La piastra di sollevamento può essere utilizzata solo da personale qualificato. Il manuale d'uso (fornito come codice QR sul prodotto e disponibile su www.rothoblaas.com) deve essere letto e compreso prima dell'uso. Le informazioni e le istruzioni in esso contenute devono essere rispettate. In caso di dubbi, contattare l'Ufficio Tecnico Rothoblaas prima dell'uso.",
      qualifiedPersonnelDevice:
        "Il dispositivo di sollevamento può essere utilizzato solo da personale qualificato. Il manuale d'uso (fornito come codice QR sul prodotto e disponibile su www.rothoblaas.com) deve essere letto e compreso prima dell'uso. Le informazioni e le istruzioni in esso contenute devono essere rispettate. In caso di dubbi, contattare l'Ufficio Tecnico Rothoblaas prima dell'uso.",
      specificGravityColumn: "Peso specifico [G]",
      modificationFactorColumn: "Fattore di modifica",
    },
    admin: {
      family: "Famiglia",
      devicesAndScrews: "Dispositivi e viti",
      lifts: "Sollevamenti",
      anchorCount: "Numero di ancoraggi",
      anchorCounts: "Numeri di ancoraggi",
      angle: "Angolo",
      capacities: "Capacità",
      capacity: "Capacità",
      capacityFactor: "Fattore di capacità",
      capacityLb: "Capacità [lb]",
      device: "Dispositivo",
      deviceFamilies: "Famiglie di dispositivi",
      deviceFamily: "Famiglia di dispositivi",
      deviceFamilyLiftType: "Tipo di sollevamento della famiglia",
      deviceFamilyLiftTypes: "Limiti dimensionali",
      deviceScrewMappings: "Mappature dispositivi-viti",
      deviceScrewMapping: "Mappatura dispositivo-vite",
      devices: "Dispositivi",
      diameter: "Diametro",
      installationType: "Tipo di installazione",
      length: "Lunghezza",
      liftType: "Tipo di sollevamento",
      liftTypes: "Tipi",
      maxCapacity: "Capacità massima",
      maxMemberDepth: "Altezza massima elemento",
      maxMemberWidth: "Larghezza massima elemento",
      memberType: "Tipo di elemento",
      minMemberDepth: "Altezza minima elemento",
      minMemberWidth: "Larghezza minima elemento",
      plateThickness: "Spessore piastra",
      productUrl: "URL prodotto",
      requiresRiggingReview: "Richiede verifica del rigging",
      screw: "Vite",
      screwCount: "Viti per dispositivo",
      screwFamilies: "Famiglie di viti",
      screwFamily: "Famiglia di viti",
      screws: "Viti",
      specificGravities: "Pesi specifici",
      specificGravity: "Peso specifico",
      washer: "Rondella",
    },
    errors: {
      calculationInvalidInput: "Il calcolo contiene un valore non valido.",
      calculationNotAvailable: "Un valore selezionato non è più disponibile. Verificare la selezione.",
      capacityAlreadyExists:
        "Esiste già una capacità con stesso dispositivo, vite, tipo di sollevamento, tipo di installazione, viti per dispositivo e angolo.",
      capacityNotFound: "Capacità non trovata.",
      deviceAlreadyExists: "L'articolo è già un dispositivo.",
      deviceFamilyNameAlreadyExists: "Esiste già una famiglia di dispositivi con questo nome.",
      deviceFamilyLiftTypeAlreadyExists: "La famiglia di dispositivi ha già questo tipo di sollevamento.",
      deviceFamilyLiftTypeInvalidLimits:
        "I limiti dell'elemento devono essere maggiori di zero e ogni massimo non inferiore al suo minimo.",
      deviceFamilyLiftTypeNotFound: "Tipo di sollevamento della famiglia non trovato.",
      deviceScrewMappingAlreadyExists: "La famiglia di dispositivi ha già questa famiglia di viti.",
      deviceScrewMappingNotFound: "Famiglia di viti della famiglia di dispositivi non trovata.",
      deviceInUseByLift:
        "Il dispositivo è usato in sollevamenti salvati: impostalo come Nascosto invece di eliminarlo.",
      deviceNotFound: "Dispositivo non trovato.",
      liftTypeAnchorCountAlreadyExists: "Il tipo di sollevamento ha già questo numero di ancoraggi.",
      liftTypeAnchorCountNotFound: "Numero di ancoraggi non trovato.",
      screwAlreadyExists: "L'articolo è già una vite.",
      screwInUseByLift: "La vite è usata in sollevamenti salvati: impostala come Nascosta invece di eliminarla.",
      screwNotFound: "Vite non trovata.",
      specificGravityAlreadyExists: "Esiste già un peso specifico con questo valore.",
      specificGravityInUseByLift:
        "Il peso specifico è usato in sollevamenti salvati: impostalo come Nascosto invece di eliminarlo.",
      specificGravityNotFound: "Peso specifico non trovato.",
      uploadFileTooLarge: "Il file supera 10 MB.",
      uploadInvalidWorkbook: "Il file caricato non è una cartella di lavoro .xlsx valida.",
      uploadMetricTemplate:
        "Usare il modello USA - NDS con pesi in lb e dimensioni in pollici. I modelli metrici o europei non sono accettati.",
      uploadMissingColumns: "Nel modello mancano colonne obbligatorie.",
      uploadNoHeader: "La cartella di lavoro deve includere una riga di intestazione con Member Name e Member Type.",
      uploadNoRows: "Nessuna riga di elementi trovata.",
      uploadNoWorksheet: "Nessun foglio di lavoro trovato nella cartella caricata.",
    },
  },

  deckDesignerModule: {
    configurations: {
      singular: "Configurazione",
      plural: "Configurazioni",
      errors: {
        connectorFamilyNotAvailable: "La famiglia di connettori selezionata non è più disponibile: scegline un'altra.",
        connectorNotAvailable: "Il connettore selezionato non è più disponibile: scegline un altro.",
        screwNotAvailable: "Una vite selezionata non è più disponibile: scegline un'altra.",
        supportFamilyNotAvailable: "La famiglia di supporti selezionata non è più disponibile: scegline un'altra.",
        supportNotAvailable: "Il supporto selezionato non è più disponibile: scegline un altro.",
        substructureNotAvailable: "La sottostruttura selezionata non è più disponibile: scegline un'altra.",
        invalidTotalHeight: "Altezza massima estradosso terrazza (H) non è valida per la stratigrafia selezionata.",
        noSupportForRequiredHeightRange: (min: number, max: number) =>
          `Con la configurazione attuale il supporto deve coprire un'altezza (h) ${min === max ? `di ${min} mm` : `da ${min} a ${max} mm`}: nessun supporto disponibile copre questo range. Modifica H o la pendenza.`,
        noSupportForSpacing:
          "L'interasse tra i supporti lungo la sottostruttura è troppo piccolo per i supporti disponibili (serve almeno 1,5 volte la loro larghezza). Riduci l'interasse sottostruttura.",
        noSupportFamilyOptions: "Nessuna opzione disponibile con la configurazione attuale.",
        noSupportForSlopeDrop: {
          title: "Nessun supporto disponibile può coprire il dislivello del terreno, qualunque sia H.",
          requiredRange: (min: number, max: number) =>
            `Altezza supporto necessaria (h): da ${min} a ${max} mm (dislivello ${max - min} mm)`,
          widestSupport: (supportCode: string, rangeMm: number) =>
            `Dislivello massimo coperto da un supporto: ${rangeMm} mm (${supportCode})`,
          fix: (maxSlope: number | null) =>
            `${maxSlope != null ? `Riduci la pendenza a massimo ${maxSlope}°` : "Riduci la pendenza"}, poi scegli H tra i valori compatibili.`,
        },
        noCompatibleConnectorSubstructureScrew:
          "Nessuna vite compatibile con il connettore e la sottostruttura selezionati.",
        noCompatibleSupportScrew: "Nessuna vite compatibile con il supporto selezionato.",
        noCompatibleConnectorBoardScrew: "Nessuna vite compatibile con lo spessore della tavola selezionato.",
        noCompatibleConnectorFamilyMapping:
          "Nessuna famiglia di connettori è compatibile con sottostruttura, tipo di fissaggio, tipo di posa e fuga selezionati.",
        noCompatibleConnectorFamilyBoardThickness:
          "Nessuna famiglia di connettori compatibile è adatta allo spessore tavola impostato.",
        noCompatibleConnectorFamilyBoardThicknessAndGroovingHeight:
          "Nessuna famiglia di connettori compatibile è adatta allo spessore tavola e all'altezza scanalatura impostati.",
        noCompatibleConnector: "Nessun connettore è compatibile con la configurazione attuale.",
        noCompatibleSupportFamilyMapping:
          "Nessuna famiglia di supporti è compatibile con la classe di materiale sottostruttura selezionata.",
      },
      calculation: {
        shapeAndGeometricData: "Forma e dimensioni",
        completeFieldsToContinue: "Compila tutti i campi di questa pagina per proseguire",
        showBillOfQuantities: "Mostra distinta",
        boards: "Tavole",
        billOfQuantities: "Distinta quantità",
        downloadPdf: "Scarica PDF",
        downloadPdfDescription:
          "Scarica il PDF con il riepilogo completo della configurazione: dati geometrici, tavole, sottostruttura, supporti, viste della terrazza, reazioni sugli appoggi e distinta prodotti.",
        downloadPdfDisabledHint: "Completa la configurazione per scaricare il PDF.",
        downloadPdfDisabledBoardFitHint: "Risolvi l'avviso sulla larghezza tavola e fuga per scaricare il PDF.",
        billOfQuantitiesEmpty: "Seleziona i dati della terrazza per calcolare la distinta quantità.",
        calculationDetailsEmpty: "Seleziona i dati della terrazza per visualizzare i dettagli del calcolo.",
        showCompatibleValues: "Vedi valori compatibili",
        stratigraphySummaryTitle: "Stratigrafia attuale",
        availableHeightForConnectorAndSupport: "Spazio disponibile per connettore e supporto",
        connectorThickness: "Spessore connettore",
        remainingHeightForSupport: "Spazio residuo per il supporto",
        billOfQuantitiesProduct: "Prodotto",
        billOfQuantitiesRequiredQuantity: "Quantità necessaria",
        billOfQuantitiesQuantityToOrder: "Quantità da ordinare",
        calculationDetails: "Dettagli calcolo",
        deckArea: "Area terrazza",
        totalBoards: "Tavole",
        joists: "Listelli",
        joistsLegend: "Listelli sottostruttura",
        layoutCalculation: "Calcolo layout",
        longJoists: "Listelli lunghi",
        shortJoists: "Listelli corti",
        longJoist: "Listello lungo",
        shortJoist: "Listello corto",
        joistsSpacing: "Interasse sottostruttura",
        supportCalculation: "Calcolo supporti",
        maxSupportSpacing: "Interasse max supporti",
        supportsPerLongJoist: "Supporti per listello lungo",
        supportsPerShortJoist: "Supporti per listello corto",
        supportSpacing: "Interasse supporti",
        supportsCount: "N. supporti",
        lShaped: "Forma ad L",
        horizontal: "Orizzontale",
        vertical: "Verticale",
        rectangular: "Forma rettangolare",
        boardDirection: "Direzione tavole",
        fasteningType: "Tipo di fissaggio",
        hiddenFastening: "A scomparsa",
        hiddenFasteningDescription: "Le tavole vengono fissate senza viti a vista, tramite connettori o clip nascosti.",
        visibleFastening: "A vista",
        visibleFasteningDescription: "Le tavole vengono fissate con viti visibili dalla superficie della terrazza.",
        groovingType: "Tipo di scanalatura",
        smoothGrooving: "Liscia",
        groovedGrooving: "Scanalata",
        maxBase: "Larghezza Max (L1)",
        maxHeight: "Lunghezza Max (L2)",
        substructureSection: (unit: string) => `Sezione elemento bs x hs [${unit}]`,
        secondaryBase: "Larghezza secondaria (l1)",
        secondaryHeight: "Lunghezza secondaria (l2)",
        totalHeight: "Altezza massima estradosso terrazza (H)",
        slope: "Pendenza",
        slopeDirectionTitle: "Pendenza del terreno",
        slopeDirectionHint:
          "Trascina sul disegno per tracciare la direzione in cui scende il terreno. Senza una direzione il terreno resta piano.",
        slopeIncompleteWarning:
          "Servono sia la pendenza sia la sua direzione: le modifiche alla pendenza non verranno salvate. Chiudere comunque?",
        totalHeightTooLittle: (height: string) =>
          `La stratigrafia selezionata richiede un'altezza totale di almeno ${height}.`,
        secondaryItemDoesNotFitTotalHeight: (code: string) =>
          `Attivando ${code} la famiglia di connettori selezionata non sarebbe più compatibile con l'altezza totale (H).`,
        totalHeightIncompatible: (minHeight: string, maxHeight: string) =>
          `L'altezza totale per questa stratigrafia deve essere compresa tra ${minHeight} e ${maxHeight}.`,
        planeInteractionHint: "Puoi disegnare e modificare la terrazza direttamente da qui.",
        planeInteractionStart: "Inizia a disegnare",
        // the button of the tutorial reopened from its icon
        planeInteractionContinue: "Continua a disegnare",
        showPlaneInteractionHint: "Mostra il tutorial",
        planeInteractionFeatureDraw: "Clicca sul piano per tracciare il perimetro della terrazza.",
        planeInteractionFeatureLength:
          "Dopo aver selezionato un punto, digita la lunghezza del lato (Backspace per correggerla) e premi Invio per confermarla.",
        planeInteractionFeatureDrag: "Trascina i lati della terrazza per modificarne le dimensioni.",
        planeInteractionFeatureEditLabel: "Fai click su una label per inserire la dimensione desiderata.",
        planeInteractionFeatureDifferentBoards:
          "Trascina da un lato all'altro le tavole di dimensioni diverse, se presenti.",
        planeInteractionFeatureClear: "Usa l'icona del cestino per cancellare i punti e ricominciare il disegno.",
        liveLoad: "Carico accidentale",
        deflection: "Freccia limite (EN1995-1-1 §7 / EN1999-1-1 §4)",
        gap: "Larghezza fuga compatibile",
        gapIncompatible: (minimumGap: string, maximumGap: string) =>
          `La fuga deve essere compresa tra ${minimumGap} e ${maximumGap}.`,
        joistsSpacingIncompatible: (minimumSpacing: number, maximumSpacing: number, unit: string) =>
          `L'interasse dei listelli deve essere compreso tra ${minimumSpacing} ${unit} e ${maximumSpacing} ${unit}.`,
        material: "Materiale",
        connectorMaterial: "Materiale connettore",
        timberMaterial: "Legno",
        wpcMaterial: "WPC",
        boardWidth: "Larghezza tavola",
        differentBoardWidthDragHint: "Puoi trascinare le tavole da un lato all'altro della terrazza.",
        differentBoard: "Tavola di larghezza diversa",
        differentBoardsColumn: "Tavole con larghezza diversa",
        longDifferentBoardWidth: "Tavola lunga",
        shortDifferentBoardWidth: "Tavola corta",
        boardThickness: "Spessore tavola",
        boardThicknessMustNotExceedBoardWidth: "Lo spessore della tavola non può superare la larghezza.",
        boardThicknessExceedsConnectorFamilyMaxThickness: (maxValue: string) =>
          `Lo spessore della tavola supera lo spessore massimo ammesso dalla famiglia di connettori selezionata (${maxValue}).`,
        boardThicknessBelowAsymmetricalGroovingMinimum: (minValue: string) =>
          `Lo spessore della tavola è troppo piccolo per la scanalatura asimmetrica selezionata (minimo ${minValue}).`,
        boardThicknessBelowGroovingMinimum: (minValue: string) =>
          `Lo spessore della tavola deve essere di almeno ${minValue} per il tipo di scanalatura selezionato.`,
        boardThicknessOutOfSymmetricalGroovingRange: (minValue: string, maxValue: string) =>
          minValue === maxValue
            ? `Lo spessore della tavola deve essere pari a ${minValue} per la configurazione attualmente selezionata.`
            : `Lo spessore della tavola deve essere compreso tra ${minValue} e ${maxValue} per la scanalatura simmetrica con la famiglia di connettori selezionata.`,
        groovingHeight: "Altezza scanalatura",
        groovingHeightOutOfConnectorFamilyRange: (minValue: string, maxValue: string) =>
          `L'altezza della scanalatura deve essere compresa tra ${minValue} e ${maxValue} per la famiglia di connettori selezionata.`,
        groovingHeightBelowConnectorMinimum: (minValue: number, unit: string) =>
          `L'altezza della scanalatura deve essere almeno ${minValue} ${unit} per il connettore selezionato.`,
        boardWidthMustNotExceedDeckDimension: (maxValue: string) =>
          `La larghezza tavola non può superare ${maxValue} per la direzione selezionata.`,
        sideNarrowerThanBoardWidth: "Il lato non può essere più stretto della larghezza tavola selezionata.",
        boardFitWarning:
          "La larghezza tavola e la fuga selezionate non si adattano esattamente alle dimensioni della terrazza. Per ottimizzare seleziona una delle 3 scelte seguenti:",
        boardFitApplied:
          "Modifica applicata. Se non ti convince, premi «Annulla» per ripristinare i valori precedenti.",
        appliedFitParameter: "Parametro",
        appliedFitPrevious: "Precedente",
        appliedFitCurrent: "Attuale",
        setBoardWidth: "Imposta larghezza tavola",
        setBoardWidthTo: "Imposta larghezza tavola a:",
        setDeckDimensions: "Imposta dimensioni terrazza",
        setDeckDimensionsTo: "Imposta dimensioni terrazza a:",
        setDifferentBoardWidth: "Imposta larghezze tavole diverse",
        setDifferentBoardWidthTo: "Imposta larghezze tavole diverse a:",
        topView: "Vista dall'alto",
        sideView: "Vista laterale",
        bottomView: "Vista da sotto",
        slopeView: "Pendenza",
        slopeViewSide: (from: number, to: number) => `Lato ${from}–${to}`,
        slopeViewLegendDeck: "Terrazza",
        slopeViewLegendTerrain: "Terreno",
        slopeViewExaggeration: "Esagerazione della pendenza (solo visualizzazione)",
        slopeViewShowArrow: "Mostra freccia della pendenza",
        unitSystemMetric: "Metrico",
        unitSystemImperial: "Imperiale",
        unitSystemChangeTitle: "Cambia unità di misura",
        unitSystemChangeWarning:
          "Cambiando l'unità di misura, forma e dimensioni verranno mantenute; tutti gli altri campi saranno azzerati. Vuoi continuare?",
        slopeViewExaggerationHelp:
          "Accentua la pendenza nel 3D per vederla meglio. È solo un effetto visivo: i valori e la configurazione non cambiano.",
        slopeViewClickHint: "Clicca su un'etichetta per vedere h, t e H in quel punto.",
        slopeViewNotToScale: "Schema esemplificativo, non in scala.",
        slopeViewUnreachable: "Non raggiungibile",
        slopeViewLegendTotalHeight: "H — altezza massima estradosso terrazza",
        slopeViewLegendSupportHeight: "h — altezza disponibile per il supporto",
        slopeViewLegendTerrainHeight: "t — altezza del terreno rispetto al vertice più basso, dove si misura H",
        supportCompatibleHRange: "H compatibile",
        supportMinHeight: "Altezza supporto min",
        supportMaxHeight: "Altezza supporto max",
        supportSlopeUnchanged: "La pendenza coincide con quella inserita.",
        supportSlopeReduced: "La pendenza sarà ridotta rispetto a quella inserita.",
        supportSlopeIncreased: "La pendenza sarà aumentata rispetto a quella inserita.",
        supportTotalHeightUnchanged: "H coincide con quella inserita.",
        supportTotalHeightReduced: "H sarà ridotta rispetto a quella inserita.",
        supportTotalHeightIncreased: "H sarà aumentata rispetto a quella inserita.",
        supportFlatTerrainOnly: "Solo con pendenze pari a 0°",
        supportSuggestedSlope: "Pendenza",
        supportWidth: "Larghezza supporto",
        suggestedValueUnchanged: "Il valore corrisponde a quello inserito.",
        suggestedValueReduced: "Il valore verrà ridotto rispetto a quello inserito.",
        suggestedValueIncreased: "Il valore verrà aumentato rispetto a quello inserito.",
        supportSpacingSuggestionsInfo:
          "Per ogni supporto, la configurazione più vicina che distanzia i supporti di almeno 1,5 volte la loro larghezza. Vengono modificati prima la sezione elemento e l'interasse sottostruttura; freccia limite e carico accidentale solo se non bastano.",
        noSupportSpacingSuggestion:
          "Nessuna configurazione distanzia i supporti di almeno 1,5 volte la loro larghezza.",
        supportApplySuggestion: "Imposta valori",
        noSupportCompatibleWithSlope:
          "Nessun supporto è compatibile con la pendenza inserita: senza pendenza ne esisterebbe almeno uno. Prova a ridurla, ad aumentare H, o a modificare le dimensioni della terrazza.",
        noSupportCompatibleGeneric:
          "Nessun supporto è compatibile con la configurazione attuale. Prova a modificare l'altezza H, il supporto scelto, o gli altri elementi della terrazza (tavola, connettore, elemento secondario).",
        supportScrew: "Vite supporto",
        connectorSubstructureScrew: "Vite connettore sottostruttura",
        connectorBoardScrew: "Vite connettore tavola",
        regularLongBoards: "Tavole lunghe regolari",
        regularShortBoards: "Tavole corte regolari",
        differentLongBoards: "Tavole lunghe con larghezza diversa",
        differentShortBoards: "Tavole corte con larghezza diversa",
      },
    },
    connectorFamilies: {
      singular: "Famiglia connettori",
      plural: "Famiglie connettori",
      isScrew: "È vite",
      requiresBoardScrew: "Richiede vite tavola",
      maxBoardThickness: "Spessore massimo tavola",
      description: "Descrizione",
      errors: {
        nameAlreadyPresent: "Esiste già una famiglia connettori con lo stesso nome.",
      },
    },
    connectorMappings: {
      singular: "Mappatura connettori",
      plural: "Mappature connettori",
      minimumGap: "Fuga minima",
      maximumGap: "Fuga massima",
    },
    connectors: {
      singular: "Connettore",
      plural: "Connettori",
      minGroovingHeight: "Altezza scanalatura minima",
      minBoardWidth: "Larghezza minima tavola",
      errors: {
        inUseByConfiguration: "Il connettore è usato in un calcolo salvato e non può essere eliminato.",
        itemAlreadyPresent: "Esiste già un connettore con lo stesso elemento.",
        screwAlreadyPresent: "Esiste già un connettore con la stessa vite.",
      },
    },
    fasteningTypes: {
      singular: "Tipo di fissaggio",
      plural: "Tipi di fissaggio",
      errors: {
        nameAlreadyPresent: "Esiste già un tipo di fissaggio con lo stesso nome.",
      },
    },
    groovingTypes: {
      singular: "Tipo di fresata",
      plural: "Tipi di fresata",
      errors: {
        nameAlreadyPresent: "Esiste già un tipo di fresata con lo stesso nome.",
      },
    },
    connectorMaterials: {
      singular: "Materiale connettore",
      plural: "Materiali connettore",
      errors: {
        inUseByConnector: "Il materiale è usato da un connettore: modifica o elimina prima il connettore.",
        nameAlreadyPresent: "Esiste già un materiale connettore con lo stesso nome.",
      },
    },
    secondaryItems: {
      singular: "Elemento secondario",
      plural: "Elementi secondari",
      use: "Uso elemento secondario",
      canUse: "Puo usare elemento secondario",
      errors: {
        inUseByConfiguration: "L'elemento secondario è usato in un calcolo salvato e non può essere eliminato.",
        inUseByConnectorMapping:
          "L'elemento secondario è usato da una mappatura connettori: elimina prima la mappatura.",
      },
    },
    screwFamilies: {
      singular: "Famiglia viti",
      plural: "Famiglie viti",
      errors: {
        inUseByScrew: "La famiglia di viti è usata da una vite: elimina prima le sue viti.",
        inUseByConnectorFamily: "La famiglia di viti è assegnata a una famiglia di connettori: rimuovila prima da lì.",
        inUseBySupportFamily: "La famiglia di viti è assegnata a una famiglia di supporti: rimuovila prima da lì.",
        nameAlreadyPresent: "Esiste già una famiglia viti con lo stesso nome.",
      },
    },
    screws: {
      singular: "Vite",
      plural: "Viti",
      errors: {
        inUseByConfiguration: "La vite è usata in un calcolo salvato e non può essere eliminata.",
        inUseByConnector: "La vite è usata come connettore: elimina prima quel connettore.",
        itemAlreadyPresent: "Esiste già una vite con lo stesso elemento.",
      },
    },
    substructures: {
      singular: "Sottostruttura",
      plural: "Sottostrutture",
      base: "Base",
      secondMomentOfArea: "Momento d'inerzia",
      countries: "Paesi",
      countryOptions: {
        eu: "Europa",
        us: "Stati Uniti",
        all: "Tutti",
      },
      errors: {
        inUseByConfiguration: "La sottostruttura è usata in un calcolo salvato e non può essere eliminata.",
        itemAlreadyPresent: "Esiste già una sottostruttura con lo stesso elemento.",
        baseHeightAlreadyPresent: "Esiste già una sottostruttura con la stessa base e altezza.",
      },
    },
    substructureFamilies: {
      singular: "Famiglia sottostrutture",
      plural: "Famiglie sottostrutture",
      errors: {
        nameAlreadyPresent: "Esiste già una famiglia sottostrutture con lo stesso nome.",
      },
    },
    substructureMaterialClasses: {
      singular: "Classe materiale sottostruttura",
      plural: "Classi materiale sottostruttura",
      modulusOfElasticity: "Modulo elastico",
      errors: {
        inUseByConfiguration: "La classe di materiale è usata in un calcolo salvato e non può essere eliminata.",
        inUseBySupport: "La classe di materiale è assegnata a un supporto: rimuovila prima da lì.",
        nameAlreadyPresent: "Esiste già una classe materiale sottostruttura con lo stesso nome.",
      },
    },
    substructureMaterials: {
      singular: "Materiale sottostruttura",
      plural: "Materiali sottostruttura",
      errors: {
        nameAlreadyPresent: "Esiste già un materiale sottostruttura con lo stesso nome.",
      },
    },
    supportFamilies: {
      singular: "Famiglia supporti",
      plural: "Famiglie supporti",
      maxLiveLoad: "Carico accidentale massimo",
      errors: {
        nameAlreadyPresent: "Esiste già una famiglia supporti con lo stesso nome.",
      },
    },
    supports: {
      singular: "Supporto",
      plural: "Supporti",
      errors: {
        inUseByConfiguration: "Il supporto è usato in un calcolo salvato e non può essere eliminato.",
        itemAlreadyPresent: "Esiste già un supporto con lo stesso elemento.",
      },
    },
    substuructures: {
      singular: "Sottostruttura",
      plural: "Sottostrutture",
    },
    substuructureMaterials: {
      singular: "Materiale sottostruttura",
      plural: "Materiali sottostruttura",
    },
    substuructureFamilies: {
      singular: "Famiglia sottostrutture",
      plural: "Famiglie sottostrutture",
    },
  },

  lockCalculatorModule: {
    preview3d: "Anteprima 3D",
    preDrillingLegend: {
      header: "Preforo trave principale",
      joist: "Preforo trave secondaria",
      yes: "sì",
      no: "no",
    },
    tabs: {
      codeNorm: "Normativa",
      structure: "Struttura",
      connectors: "Connettori",
      loading: "Carichi",
      designInput: "Dati di Input",
      configuration: "Configurazione",
      loads: "Carichi",
      results: "Risultati",
      calculationComparison: "Confronto dei calcoli",
      bom: "Distinta base",
      report: "Report PDF",
    },
    env: {
      title: "Configurazioni Ambientali e Normative",
      standard: "Normativa",
      serviceClass: "Classe di Servizio",
      loadDurationClass: "Classe di Durata Carico",
      continuousCtrl: "Controllo Continuativo",
      continuousCtrlLabel: "Produzione soggetta a controllo continuativo (COV < 15%)",
      coefficients: "Coefficienti di calcolo",
      kmod: "Coefficiente di modificazione (durata del carico e classe di servizio)",
      gammaM: "Coefficiente parziale di sicurezza della connessione (γM)",
      gammaMAluminium: "Coefficiente parziale di sicurezza dell'alluminio (γM)",
      customCoefficients: "Personalizzato",
      customCoefficientsHint: "Modifica kmod e γM solo per questo calcolo, al posto dei valori normativi",
      normativeValue: "Valore normativo",
      restoreNormative: "Ripristina",
    },
    structural: {
      title: "Configurazione Strutturale",
      configType: "Configurazione per resistenza laterale",
      connectionPosition: "Posizione del connettore",
      upliftResistanceConfig: "Configurazione resistenza a sollevamento",
      inclinedFastener: "Fissaggio inclinato",
      headerOrientation: "Orientamento trave principale",
      lateralResistance: {
        columnNotch: "Intaglio colonna",
        headerBeamNotch: "Intaglio trave principale",
        joistBeamNotch: "Intaglio trave secondaria",
        inclinedFasteners: "Fissaggi inclinati",
        lockstop: "LOCK STOP",
        exposed: "Esposta",
      },
      headerOrientationOptions: {
        beam: "Trave",
        column: "Pilastro",
      },
      generateAllConfigurations: "Genera tutte le configurazioni",
      goToManualConfiguration: "Vai alla configurazione manuale/semi-manuale",
    },
    connection: {
      title: "Configurazione Connessione",
      standard: "Normativa",
      serviceClass: "Classe di Servizio",
      loadDurationClass: "Classe di Durata Carico",
      connector: "Connettore",
      finish: "Finitura",
      connectorOne: "Connettore 1",
      connectorTwo: "Connettore 2",
      productType: "Tipo Prodotto",
    },
    fasteners: {
      headerFastener: "Fissaggio trave principale",
      joistFastener: "Fissaggio trave secondaria",
      upliftFastener: "Fissaggio Anti-Sollevamento",
      lockstop: "Lockstop",
    },
    customMaterial: {
      create: "Crea materiale personalizzato",
      edit: "Modifica materiale personalizzato",
      remove: "Elimina materiale",
      badge: "personalizzato",
      name: "Nome",
      baseGrade: "Basato su",
      keepCurrentBase: "Mantieni la base attuale",
      baseGradeHint: "Categoria, normativa e famiglia strutturale vengono ereditate dal materiale base",
      inherited: "Ereditato dalla base",
      family: "Famiglia strutturale",
      category: "Categoria",
      normative: "Normativa",
      properties: "Proprietà",
      advanced: "Avanzate",
      rhoK: "Densità caratteristica ρk",
      rhoM: "Densità media ρm",
      fc0K: "Compressione parallela fc,0,k",
      fc90K: "Compressione perpendicolare fc,90,k",
      fvK: "Taglio fv,k",
      frK: "Taglio da rotolamento fr,k",
      faxK: "Estrazione assiale fax,k",
      usedInCalculations: (n: number) => `Usato in ${n} calcoli: verranno aggiornati al prossimo ricalcolo.`,
      deleteConfirm: "Vuoi davvero eliminare questo materiale personalizzato?",
      deleteBlocked: "Non eliminabile: il materiale è usato in altri calcoli.",
    },
    timber: {
      sectionTitle: "Configurazione Geometrica",
      title: "Elementi Legno",
      header: "Trave principale",
      joist: "Trave secondaria",
      headerMaterialGrade: "Classe di resistenza trave principale",
      headerWidth: "Larghezza trave principale (b)",
      headerHeight: "Altezza trave principale (h)",
      joistMaterialGrade: "Classe di resistenza trave secondaria",
      joistWidth: "Larghezza trave secondaria (b)",
      joistHeight: "Altezza trave secondaria (h)",
      joistAngle: "Angolo trave secondaria α",
      joistAngleBeta: "Angolo trave secondaria β",
      connectorTopDistance: "Distanza connettore dal bordo superiore della trave secondaria",
      joistTopOffset: "Spostamento del top della trave secondaria rispetto alla trave principale",
      joistTopAlignment: "Posizione del top della trave secondaria",
      joistTopAligned: "Allineato al top della colonna",
      joistTopOffsetOption: "Spostato rispetto alla colonna",
    },
    connector: {
      sectionTitle: "Connettore e Viteria",
      setupTitle: "Configurazione Connessione",
      selectionTitle: "Scelta Connettore",
      fastenersTitle: "Viteria",
      connectorPairCount: "Numero di coppie di connettori",
      noCompatibleConnector: "Nessun connettore LOCK-T compatibile con le sezioni inserite.",
      noCompatibleSecondConnector: "Nessun secondo connettore compatibile con quello selezionato",
      generateFromData: "Genera configurazioni a partire dai dati compilati",
      generateManual: "Genera soluzione manuale",
    },
    loads: {
      sectionTitle: "Azioni di Progetto (Ed)",
      title: "Carichi (Ed)",
      fvD: "Taglio Verticale (Fv,d)",
      faxD: "Azione Assiale (Fax,d)",
      flatD: "Taglio Laterale (Flat,d)",
      fupD: "Taglio per Sollevamento (Fup,d)",
      alphaDeg: "Angolo α",
    },
    combined: {
      title: "Verifica Sollecitazioni Combinate",
      description: "Seleziona le direzioni di carico da includere nella verifica combinata:",
      includeFv: "Fv,d (Taglio Verticale)",
      includeFup: "Fup,d (Sollevamento)",
      includeFax: "Fax,d (Azione Assiale)",
      includeFlat: "Flat,d (Taglio Laterale)",
    },
    configuration: {
      title: "Configurazione",
      configType: "Tipo Configurazione",
      connectionCount: "Numero Connessioni",
    },
    errors: {
      fillRequiredFields: "Compila tutti i campi obbligatori.",
    },
    selectPlaceholder: "Seleziona...",
    helpTexts: {
      // Code & Norm
      standard:
        "Seleziona la normativa di riferimento per il calcolo (es. EC5 – Eurocodice 5, NTC). Determina i coefficienti di sicurezza e le formule utilizzate nella verifica.",
      serviceClass:
        "Definisce le condizioni ambientali di esercizio della struttura: Classe 1 (ambiente secco, umidità legno ≤12%), Classe 2 (ambiente umido, umidità ≤20%), Classe 3 (esposto alle intemperie). Influenza il coefficiente kmod.",
      loadDurationClass:
        "Indica la durata del carico applicato: Permanente (es. peso proprio), Lunga durata (es. carichi di magazzino), Media durata (es. neve), Breve durata (es. vento), Istantanea (es. urti). Influisce su kmod.",
      continuousCtrl:
        "Attiva/disattiva se la produzione è sottoposta a controllo di qualità continuo con coefficiente di variazione inferiore al 15%. Se attivo, riduce il coefficiente parziale di sicurezza γM del materiale.",
      // Structure
      lateralResistanceConfig:
        "Scegli la configurazione della connessione per il calcolo della resistenza a taglio laterale. Determina la formula di Johansen applicabile per il calcolo della capacità portante.",
      upliftResistanceConfig:
        "Scegli se usare il fissaggio inclinato per il calcolo della resistenza a sollevamento. Condivide lo stesso fissaggio selezionato nello step Connectors per la configurazione laterale.",
      headerOrientation:
        "Orientamento dell'elemento strutturale trave principale: orizzontale (trave) o verticale (pilastro). Determina il comportamento statico e le verifiche applicabili alla connessione.",
      headerMaterialGrade:
        "Seleziona la classe di resistenza del legno per la trave principale (es. GL24h, C24, LVL). Determina le proprietà meccaniche (resistenza a trazione, compressione, massa volumica) usate nei calcoli.",
      headerWidth:
        "Larghezza della sezione trasversale della trave principale, in mm. Dimensione perpendicolare all'asse del connettore. Se la trave principale è orientata come colonna, la larghezza del connettore deve rientrare in questa misura meno una tolleranza (17.5mm per Mini, 24mm per Midi).",
      headerHeight:
        "Profondità (o altezza) della sezione trasversale della trave principale, in mm. Dimensione parallela all'asse del connettore. Se la trave principale è orientata come trave, limita l'altezza massima del modulo connettore (assembly_height ≤ altezza trave principale − 15mm).",
      joistMaterialGrade:
        "Classe di resistenza del legno per la trave secondaria. Può differire da quella della trave principale. Se la densità (rho_k) supera 420 kg/m³ il legno richiede il preforo, il che riduce la larghezza massima ammessa per il connettore (vedi larghezza trave secondaria).",
      joistWidth:
        "Larghezza della sezione trasversale della trave secondaria, in mm. Dimensione perpendicolare all'asse del connettore. La larghezza del connettore deve rientrare in questa misura: esattamente se il legno richiede il preforo, con una tolleranza aggiuntiva di 8mm (Mini) o 6mm (Midi) se non lo richiede.",
      joistHeight:
        "Altezza della sezione trasversale della trave secondaria, in mm. Dimensione parallela all'asse del connettore. Limita l'altezza massima del modulo connettore (assembly_height ≤ altezza trave secondaria), senza tolleranza.",
      angleAlpha: "",
      joistTopAlignment:
        "Trave principale a colonna. Allineato: il top della trave secondaria è a filo col top della colonna e il c_min si verifica sulla distanza del connettore dal top della trave secondaria. Spostato: il top della trave secondaria sta più in basso della colonna (campo di spostamento) e il c_min si verifica sulla somma dello spostamento e della distanza del connettore.",
      joistTopOffset:
        "Di quanto il top della trave secondaria sta più in basso del top della colonna. Se vuoto si usa il c_min del lock scelto.",
      connectorTopDistance:
        "Distanza verticale dal bordo superiore della trave secondaria all'assieme connettore (la trave principale si sposta con esso). Se vuoto: con trave secondaria allineata alla colonna si usa il c_min del lock scelto; con trave secondaria spostata 0 (connettore a filo della trave secondaria). Per LOCKT135 senza preforo il minimo è 5 mm. Con trave principale a colonna deve essere raggiunto il c_min: dalla sola distanza se la trave secondaria è allineato, dalla somma con lo spostamento se è spostata.",
      angleBeta:
        "Angolo di scorrimento della trave secondaria nel piano X-Z, indipendente dall'angolo α. Determina la lunghezza massima ammessa per il fissaggio trave secondaria: entro 20° il limite è 48.35mm (Mini) / 65.7mm (Midi); oltre 20° nessun fissaggio risulta compatibile (limite 0).",
      // Connectors
      connectorPairCount:
        "Numero di connettori da installare sulla stessa connessione. Scegliere 2 per connettori accoppiati (maggiore capacità). Se si seleziona 2, apparirà un secondo menu di scelta per l'altro connettore.",
      headerLock:
        "Un lock maggiorato sul lato trave principale dimezza i connettori in distinta e aggiorna le viti lato trave principale. Sceglierlo o modificarlo può togliere il lock stop dalla configurazione: il risultato va poi ricalcolato. Lock maggiorato e lock stop si escludono: sceglierne uno toglie l'altro.",
      connectionCount:
        "Numero totale di connessioni LOCK necessarie per il progetto. Determina le quantità totali di prodotti e fissaggi da ordinare.",
      connectorOneLock:
        "Seleziona il modello di connettore a scomparsa LOCK desiderato. Ogni modello ha una capacità portante diversa. Consultare il catalogo tecnico per le specifiche.",
      connectorOneFinish:
        "Attiva se si desidera il trattamento superficiale C4 EVO, una protezione avanzata contro la corrosione per ambienti esterni o aggressivi (classe di corrosione C4).",
      connectorTwoLock:
        "Modello di connettore LOCK-T secondario, utilizzato quando si selezionano 2 connettori. Può essere diverso dal primario per ottimizzare la capacità della connessione.",
      connectorTwoFinish:
        "Finitura superficiale del connettore secondario. Stesse opzioni del primario: ALUMINIUM o EVO (C4).",
      headerFastener:
        "Seleziona il tipo e la quantità di fissaggi per fissare il connettore all'elemento primario (pilastro). I fissaggi devono essere certificati per uso strutturale.",
      joistFastener: "Seleziona il tipo di fissaggio per il lato trave secondaria della coppia di connettori.",
      upliftFastener:
        "Fissaggi inseriti con inclinazione per aumentare la resistenza al sollevamento (uplift) e/o al taglio laterale. Specificare tipo e numero di fissaggi.",
      lockstop:
        "Seleziona il modello di connettore LOCK STOP (dispositivo di sicurezza anti-sfilamento). Impedisce il sollevamento accidentale della trave in condizioni estreme.",
      lockstopWidth:
        "Regolazione della larghezza per l'elemento LOCK STOP, in mm. Permette di adattare il dispositivo alla geometria della connessione.",
      // Loads
      fvD: "Forza di taglio verticale agente sulla connessione, in kN. È il carico principale che sollecita il connettore nella direzione verticale (parallela alla gravità).",
      fupD: "Forza di sollevamento (uplift), in kN. Agisce in direzione opposta alla gravità, tendendo a separare gli elementi collegati. Tipica in presenza di vento o sismi.",
      faxD: "Forza assiale agente lungo l'asse della trave secondaria, in kN. Forza di trazione (+) o compressione (−). Per travi semplicemente appoggiate solitamente è trascurabile.",
      flatD:
        "Forza di taglio laterale (fuori piano), in kN. Agisce perpendicolarmente al piano principale della connessione. Tipica in zone sismiche o per azioni fuori piano.",
      combined:
        "Attiva per eseguire la verifica a sollecitazioni combinate (più azioni agenti simultaneamente). La formula di verifica è: (Fax,Ed / Rax,d)² + (Fv,Ed / Rv,d)² ≤ 1. Seleziona quali direzioni di carico includere nella combinazione.",
    },
    notices: {
      title: "Avvisi",
      goToField: "Vai al campo",
      goToAction: "Vai al pulsante",
      count: (n: number) => (n === 1 ? "1 avviso" : `${n} avvisi`),
      severity: { error: "Errore", warning: "Attenzione", info: "Info" },
      axes: {
        vertical: "verticale (Fv)",
        uplift: "sollevamento (Fup)",
        axial: "assiale (Fax)",
        lateral: "laterale (Flat)",
      },
      members: { header: "trave principale", joist: "trave secondaria" },
      reasons: {
        columnWidth: "la larghezza del pilastro",
        topDistance: "la distanza del connettore dal bordo superiore",
        joistWidth: "la larghezza della trave secondaria",
        assemblyHeight: "le altezze delle sezioni",
        evoRequired: "il requisito EVO della classe di servizio 3",
      },
      coefficients: { kmod: "kmod", gammaM: "γM (collegamento)", gammaMAluminium: "γM (alluminio)" },
      slots: {
        header: "vite sulla trave principale",
        joist: "vite sulla trave secondaria",
        inclined: "vite inclinata",
      },
      inputs: {
        standardId: "norma",
        serviceClassId: "classe di servizio",
        loadDurationClassId: "classe di durata del carico",
        "connectors[0].lockItemId": "primo connettore",
        "connectors[1].lockItemId": "secondo connettore",
        headerFastenerItemId: "vite sulla trave principale",
        joistFastenerItemId: "vite sulla trave secondaria",
        lockstopItemId: "lock stop",
        headerMaterialGradeId: "materiale della trave principale",
        joistMaterialGradeId: "materiale della trave secondaria",
        headerOrientation: "orientamento della trave principale",
        alphaDeg: "angolo α",
        headerH: "altezza della trave principale",
        headerB: "larghezza della trave principale",
        joistB: "larghezza della trave secondaria",
        joistH: "altezza della trave secondaria",
        anyLoad: "almeno un carico",
        inclinedFastenerItemId: "vite inclinata",
        lateralResistanceConfig: "resistenza laterale",
        kmod: "kmod",
        gammaM: "γM",
      },
      messages: {
        topDistanceBelowCMin: (p: NoticeParams) =>
          `Distanza del connettore ${p.distance} mm + offset della trave secondaria ${p.offset} mm sono sotto il c_min minimo del lock (${p.cMin} mm): la configurazione non è verificata. Aumenta la distanza o l'offset.`,
        topDistanceBelowLock135Min: (p: NoticeParams) =>
          `Il LOCKT135 su una trave secondaria non preforata richiede almeno ${p.min} mm dal bordo superiore (ora ${p.distance} mm): la configurazione non è verificata.`,
        serviceClassNotAllowed: (p: NoticeParams) =>
          `La classe di servizio ${p.classNumber} non è ammessa per il materiale della trave principale (ammesse: ${p.allowed}): la configurazione non è verificata.`,
        axisInputsMissing: (p: NoticeParams) =>
          `L'asse ${p.axis} ha un carico ma non si può calcolare: manca ${p.missing}.`,
        noCompatibleLock: (p: NoticeParams) =>
          `Nessun lock è adatto a questa struttura: ${p.rejected} su ${p.total} esclusi da ${p.reason}.`,
        edgeDistanceUnavailable: (p: NoticeParams) =>
          `Le distanze minime dai bordi (§11) non sono disponibili per la ${p.member}: la densità (${p.rhoK} kg/m³) supera il limite di 500 dell'EC5.`,
        customCoefficientsEnabled: () =>
          "Ora puoi modificare kmod e γM solo per questo calcolo: finché non cambi un valore, resta quello normativo.",
        coefficientOverrideDiffers: (p: NoticeParams) =>
          `${p.coefficient} vale ${p.value}; il valore normativo è ${p.normative}.`,
        topDistanceEqualsDefault: () => "Si sta usando il valore di default: il c_min del lock scelto.",
        resultStale: (p: NoticeParams) =>
          `I dati sono cambiati dall'ultimo calcolo: il risultato della configurazione finale potrebbe non essere aggiornato. Premi "${p.button}" per ricalcolare il risultato con i dati attuali.`,
        reportStale: (p: NoticeParams) =>
          `Gli input sono cambiati dopo l'ultimo report: il PDF mostrato non è aggiornato. Premi "${p.generateReport}" nella scheda "${p.reportTab}".`,
        draftsStale: (p: NoticeParams) =>
          `I dati sono cambiati dopo la generazione delle configurazioni: premi "${p.button}" per aggiornare l'elenco.`,
        noDraftWithinLimits: (p: NoticeParams) =>
          `I lock sono adatti alla struttura, ma nessuna configurazione generata rispetta i carichi. Controlla i carichi o i materiali, poi premi di nuovo "${p.button}".`,
        generationInputsMissing: (p: NoticeParams) =>
          `"${p.generateAll}" e "${p.generateFromData}" non sono disponibili: mancano ${p.missing}.`,
        manualGenerationInputsMissing: (p: NoticeParams) =>
          `"${p.generateManual}" non è disponibile: mancano ${p.missing}.`,
        noCompatibleFastener: () =>
          `I lock sono adatti alla struttura, ma nessuna vite è compatibile con essi: controlla le altezze delle travi, l'angolo e i materiali.`,
        noCompatibleLockstop: () =>
          `I lock sono adatti alla struttura, ma nessun lock stop è compatibile con essi: scegli un'altra resistenza laterale.`,
        combinedNeedsTwoAxes: (p: NoticeParams) =>
          `La verifica combinata richiede almeno due assi: è selezionato solo l'asse ${p.axis}. Selezionane un altro.`,
        lateralConfigCorrected: (p: NoticeParams) =>
          `La resistenza laterale è passata da "${p.fromConfig}" a "${p.toConfig}": con l'orientamento "${p.orientation}" della trave principale la prima non è disponibile.`,
        continuousControlCleared: () =>
          "Il controllo continuo della produzione è stato azzerato: non vale per l'Eurocodice 5.",
        secondConnectorCleared: () =>
          "Il secondo connettore è stato rimosso: è selezionata una sola coppia di connettori.",
        lockstopClearedByHeaderLock: () =>
          "Il lock stop e il tipo di resistenza laterale sono stati rimossi perché è stato scelto un lock maggiorato sulla trave principale: scegli un nuovo tipo di resistenza laterale e ricalcola il risultato.",
        headerLockClearedByLockstop: () =>
          "Il lock maggiorato sulla trave principale è stato rimosso perché è stato scelto un lock stop: ricalcola il risultato.",
        lockstopCleared: () => "Il lock stop è stato rimosso: la resistenza laterale non è più impostata su lock stop.",
        combinedAxisDisabled: (p: NoticeParams) =>
          `L'asse ${p.axis} è stato tolto dalla verifica combinata: non ha un carico.`,
        upliftConfigSetByLateral: () =>
          "La resistenza a sollevamento è stata impostata su viti inclinate, come per la resistenza laterale.",
        upliftConfigSetByLoad: () =>
          "La resistenza a sollevamento è stata impostata su viti inclinate perché è inserito un carico di sollevamento.",
        inclinedFastenerCleared: () =>
          "La vite inclinata è stata rimossa: né la resistenza laterale né quella a sollevamento usano viti inclinate.",
        inclinedFastenerNotAllowed: () =>
          "La vite inclinata è stata rimossa: il lock scelto non prevede viti inclinate.",
        fastenerCleared: (p: NoticeParams) =>
          `La ${p.slot} è stata rimossa: non è compatibile con la selezione attuale.`,
        finishForced: () => "La finitura è stata portata ad alluminio: il lock scelto non ha la versione EVO.",
        finishSetByServiceClass: () => "La finitura è stata impostata su EVO: la classe di servizio 3 la richiede.",
        secondLockCleared: () => "Il secondo lock è stato rimosso: non è più compatibile con il primo.",
        pairCountReduced: () =>
          "È stata mantenuta una sola coppia di connettori: nessun secondo lock è compatibile con il primo.",
        topDistanceFollowedDefault: (p: NoticeParams) =>
          `La distanza del connettore ha seguito il nuovo default del lock scelto: ${p.before} mm → ${p.after} mm.`,
        topOffsetFollowedDefault: (p: NoticeParams) =>
          `L'offset della trave secondaria ha seguito il nuovo default del lock scelto: ${p.before} mm → ${p.after} mm.`,
        connectorsInvalidated: () =>
          "I lock scelti sono stati azzerati: erano stati scelti per un'altra distanza dal bordo.",
      },
    },
    wizard: {
      stepOf: (current: number, total: number) => `Passo ${current} di ${total}`,
      completeFieldsToContinue: "Compila tutti i campi di questa pagina per proseguire",
    },
    finish: {
      aluminium: "Alluminio",
      evo: "EVO",
      aluminiumShort: "ALU",
      evoShort: "EVO",
    },
    continuousCtrl: {
      active: "Attivo",
      inactive: "Inattivo",
      standard: "Standard",
      cov: "COV",
      std: "STD",
    },
    results: {
      title: "Risultati Verifica",
      verified: "Verificato",
      utilizationRatio: "Rapporto di Utilizzo",
      notYetCalculated: "Compila il modulo per eseguire la verifica.",
      notVerified: "Non verificato",
      verifiedOk: "Verifica superata",
      axes: {
        vertical: "Verticale (Fv)",
        uplift: "Sollevamento (Fup)",
        axial: "Assiale (Fax)",
        lateral: "Laterale (Flat)",
      },
      load: "Carico",
      resistance: "Resistenza",
      eta: "η",
      failed: "NON VERIFICATO",
      noLoad: "NESSUN CARICO",
      formulaAltSingle: "η = Ed / Rd ≤ 1",
      formulaAltCombined: "Σ (Ed/Rd)² ≤ 1",
      finalConfiguration: "Configurazione finale",
      removeComparison: "Rimuovi dal confronto",
      slipModulus: "Modulo di scorrimento",
    },
    generatedConfigurations: {
      title: "Configurazioni generate",
      empty: "Nessuna configurazione ancora generata — compila Codice & Norma, Carichi e Struttura.",
      lock: "Lock",
      fastener: "Vite",
      combined: "Combinata",
      chosen: "Scelta",
      compare: "Confronta",
      downloadData: "Scarica dati",
      load: "Carica",
      selectAll: "Seleziona tutte le configurazioni",
      selectRow: "Seleziona questa configurazione",
      search: "Cerca configurazioni",
    },
    bom: {
      title: "Distinta base",
      noComponents: "Nessun componente configurato.",
      component: "Componente",
      code: "Codice",
      quantity: "Qtà",
      headerLock: "Lock trave principale maggiorato",
    },
    report: {
      title: "Report PDF",
      empty: "Scegli una configurazione per generare il report di calcolo.",
      error: "Non è stato possibile generare il report.",
      retry: "Riprova",
      generate: "Genera PDF",
      download: "Scarica PDF",
      fullscreen: "Schermo intero",
      exitFullscreen: "Esci da schermo intero",
      savingChanges: "Salvataggio delle modifiche in corso: il report parte dai dati salvati.",
      notGenerated: 'Clicca su "Genera PDF" per generare il report di calcolo.',
    },
    helpModal: {
      illustrationAlt: "Illustrazione aiuto",
    },
    aria: {
      calculationSteps: "Passi del calcolo",
      calculationResults: "Risultati del calcolo",
    },
    admin: {
      tabs: {
        reference: "Riferimento",
        catalog: "Catalogo",
        coefficients: "Coefficienti",
        connectors: "Lock",
      },
      catalog: {
        fasteners: "Viti",
        materialGrades: "Classi di Legno",
      },
      coefficients: {
        kmodValues: "Valori Kmod",
        gammaMValues: "Valori γM",
      },
      reference: {
        standards: "Normative",
        serviceClasses: "Classi di Servizio",
        loadDurationClasses: "Classi di Durata Carico",
      },
      fasteners: {
        singular: "Vite",
        diameter: "Ø (mm)",
        length: "Lunghezza (mm)",
        threadLength: "Lunghezza filetto (mm)",
        myRk: "My,Rk (kNm)",
        ftensK: "Ftens,K (kN)",
        headType: "Tipo Testa",
      },
      materialGrades: {
        singular: "Classe di Legno",
        rhoK: "ρk (kg/m³)",
        rhoM: "ρm (kg/m³)",
      },
      standards: {
        singular: "Normativa",
        jurisdictionCode: "Regione",
        normativeType: "Tipo di normativa",
        normativeTypes: {
          EUROCODE: "Eurocodice",
          NATIONAL_CODE: "Codice nazionale",
          HARMONIZED_STANDARD: "Norma armonizzata",
          ETA: "ETA",
          DOP: "DoP",
        },
      },
      serviceClasses: {
        singular: "Classe di Servizio",
        classNumber: "Classe #",
        nameIt: "Nome IT",
        nameDe: "Nome DE",
        nameFr: "Nome FR",
        nameEs: "Nome ES",
        description: "Descrizione",
        typicalExamples: "Esempi Tipici",
        order: "Ordine",
      },
      loadDurationClasses: {
        singular: "Classe di Durata Carico",
      },
      kmodValues: {
        singular: "Valore Kmod",
        serviceClass: "Classe di Servizio",
        loadDurationClass: "Classe di Durata Carico",
        kmod: "kmod",
      },
      gammaMValues: {
        singular: "Valore γM",
        standard: "Normativa",
        continuousCtrl: "Controllo Continuo",
        notApplicable: "Non applicabile (EC5)",
        gammaM: "γM",
      },
      connectors: {
        singular: "Connettore Lock",
        family: "Famiglia",
        nModules: "N. Moduli",
        nInclinedUplift: "Fissaggi Inclinati (Sollevamento)",
        nInclinedLateral: "Fissaggi Inclinati (Laterale)",
        width: "Larghezza (mm)",
        height: "Altezza (mm)",
        thickness: "Spessore (mm)",
        screwEdgeThickness: "Spessore lato vite (mm)",
        evoAvailable: "EVO Disponibile",
        lockModule: "Modulo",
        edgeDistance: "Distanza Bordo (mm)",
        assemblyHeight: "Altezza Assieme (mm)",
      },
      lockModules: {
        singular: "Modulo",
        assemblyHeight: "Altezza assieme (mm)",
        sideHeight: "Altezza lato (mm)",
        thickness: "Spessore (mm)",
        moduleWidth: "Larghezza Modulo (mm)",
        joistFastenersPerModule: "Fissaggi trave secondaria / modulo",
        headFastenersPerModule: "Fissaggi testa / modulo",
        kLockFactor: "kLock",
        f1AluRk: "F1,alu,Rk (kN)",
        f2AluRkPerModule: "F2,alu,Rk / modulo (kN)",
        kh2PerModule: "kh2 / modulo",
        contactAreaALock: "Area di contatto ALock (mm²)",
        cMin: "c_min (mm)",
        coefficients: "Coefficienti laterali/verticali",
      },
      lockStops: {
        singular: "Lock Stop",
        fyK: "fy,k (MPa)",
        area: "Area (mm²)",
        fLockstopRk: "FLockstop,Rk (kN)",
        isSingle: "Singolo",
      },
      connectorAccessories: {
        singular: "Accessorio Connettore",
        quantity: "Qtà",
        notes: "Note",
        accessorySku: "SKU Accessorio",
        connector: "Connettore",
      },
      fastenerUsages: {
        singular: "Utilizzo viti",
        fastener: "Vite",
        usage: "Utilizzo",
        usageStandard: "Standard (trave principale / trave secondaria)",
        usageInclined: "Inclinata",
        scope: "Seleziona per",
        scopeFamilies: "Intere famiglie",
        scopeFasteners: "Viti singole",
        families: "Famiglie",
        fasteners: "Viti",
        exceptions: "Eccezioni (da non aggiungere)",
        resultAlreadyPresent: "Elementi già aggiunti in precedenza",
        resultAdded: "Nuovi elementi aggiunti",
        resultNothingToDo: "Nessun elemento da aggiungere o eliminare",
        resultRemoved: "Elementi che esistevano e sono stati eliminati perché eccezioni",
      },
      lockLockstopMappings: {
        singular: "Lock → Lockstop",
        lock: "Lock",
        lockstop: "Lockstop",
        quantity: "Quantità",
        installationLevels: "Livelli di installazione",
      },
      lockPairLockstopMappings: {
        singular: "Coppia → Lockstop",
        lockA: "Lock A",
        lockB: "Lock B",
        lockstop: "Lockstop",
        quantity: "Quantità totale",
        installationLevels: "Livelli di installazione",
      },
      model3d: {
        singular: "Modello 3D",
        quantity: "Quantità",
      },
      emptyValue: "--",
    },
  },
  beamReinforcementModule: {
    tabs: {
      generalData: "Dati generali e carichi",
      beams: "Travi",
      screw: "Vite e connessione",
    },
    screwFamilies: {
      singular: "Famiglia vite",
      plural: "Famiglie vite",
      errors: {
        nameAlreadyPresent: "Esiste già una famiglia vite con lo stesso nome.",
      },
    },
    screws: {
      singular: "Vite",
      plural: "Viti",
      errors: {
        nameAlreadyPresent: "Esiste già una vite con lo stesso nome.",
      },
      fields: {
        family: "Famiglia",
        headDiameter: "Diametro testa",
        threadedLength: "Lunghezza filettata",
        yieldMoment: "Momento di snervamento",
        tensileCapacity: "Capacità a trazione",
      },
    },
    timbers: {
      singular: "Classe di legno",
      plural: "Classi di legno",
      errors: {
        nameAlreadyPresent: "Esiste già una classe di legno con lo stesso nome.",
      },
      fields: {
        density: "Densità rho",
        bendingStrength: "Resistenza a flessione fmk",
        shearStrength: "Resistenza a taglio fvk",
        modulusOfElasticity: "Modulo elastico E0,mean",
        isGlulam: "Lamellare",
      },
    },
    drawing: {
      view2D: "Vista 2D",
      view3D: "Vista 3D",
      enable3DHint: "Inserisci entrambe le lunghezze delle travi per abilitare la vista 3D.",
    },
    calculation: {
      serviceClass: "Classe di servizio",
      serviceClass1: "1 — u ≤ 10%",
      serviceClass2: "2 — 10% < u ≤ 16%",
      serviceClass3: "3 — u ≥ 16%",
      loadDuration: "Durata carico",
      loadDurationPerm: "Permanente",
      loadDurationLong: "Lunga durata",
      loadDurationMedium: "Media durata",
      loadDurationShort: "Breve durata",
      loadDurationInst: "Istantanea",
      tipTolerance: "Tolleranza punta",
      permanentLoad: "Carico permanente",
      liveLoad: "Carico variabile",
      beamLength: "Luce trave",
      existingBeamTitle: "Trave esistente",
      reinforcementBeamTitle: "Trave di rinforzo",
      strengthClass: "Classe di resistenza",
      base: "Base",
      height: "Altezza",
      lengthOptionalHint:
        "Opzionale. Se sono definite entrambe le lunghezze delle travi, l'anteprima può passare tra 2D e 3D.",
      existingLength: "Lunghezza trave esistente",
      reinforcementLength: "Lunghezza rinforzo",
      reinforcementSides: "Lati di rinforzo",
      reinforcementSidesOne: "1 — rinforzo su un lato",
      reinforcementSidesTwo: "2 — rinforzo su entrambi i lati",
      screw: "Vite",
      screwSpacing: "Interasse viti",
      preDrilled: "Foro pilota (preforo)",
    },
    results: {
      summaryTitle: "Riepilogo verifiche",
      bendingUtilization: "Tasso di lavoro a flessione",
      shearUtilization: "Tasso di lavoro a taglio",
      connectionUtilization: "Tasso di lavoro connessione",
      screwAdmissible: "Vite ammissibile",
      screwNotAdmissible: "Vite NON ammissibile",
      reasonGeometry: "geometria (l_ef / lunghezza / t₂)",
      reasonHeight: "h₂ < a₄,t + a₄,c",
      reasonSpacing: "interasse < a₁ minimo",
      reasonOverload: "η > 100%",
      incompleteWarning: "Compila tutti i campi per calcolare la verifica.",
      warningInsufficientPenetration:
        "Infissione del filetto insufficiente nella trave esistente (l_ef = 0). Scegliere una vite più lunga.",
      warningScrewTooLong: (max: number) =>
        `La vite supera la larghezza disponibile b₁+b₂−10 mm = ${max} mm: la punta fuoriesce dalla trave esistente.`,
      warningHeightInsufficient: (height: number, min: number) =>
        `Altezza insufficiente: h₂ = ${height} mm < a₄,t + a₄,c = ${min} mm. Aumentare h₂ o ridurre il diametro della vite.`,
      warningSpacingInsufficient: (spacing: number, min: number) =>
        `L'interasse adottato (${spacing} mm) è inferiore all'interasse minimo a₁ = ${min} mm.`,
      warningConnectionOverload: (eta: number) =>
        `Connessione non verificata: η = ${eta}% > 100%. Ridurre l'interasse o aumentare la vite.`,
    },
    details: {
      title: "Sviluppo del calcolo",
      loadsSection: "Coefficienti, carichi e profondità di infissione",
      kmod: "k_mod",
      selfWeight: "Peso proprio g₁",
      designLoad: "Carico di progetto q_d",
      spacingSection: "Distanze minime ed effettive (EC5 §8.3)",
      minimum: "minimo",
      effective: "effettivo",
      outcome: "esito",
      stiffnessSection: "Ripartizione delle rigidezze",
      bendingSection: "Verifica a flessione",
      shearSection: "Verifica a taglio",
      axialSection: "Resistenza assiale della vite",
      johansenSection: "Resistenza della connessione (Johansen)",
      connectionSection: "Verifica della connessione",
      existingBeamLabel: "Trave esistente (1)",
      reinforcementBeamLabel: "Trave di rinforzo (2)",
      designMoment: "Momento di progetto M_d",
      designShear: "Taglio di progetto V_d",
      bendingStress: "Tensione a flessione σ_m,d",
      bendingStrength: "Resistenza a flessione f_m,d",
      shearStress: "Tensione a taglio τ_d",
      shearStrength: "Resistenza a taglio f_v,d",
      screwCount: "Viti lungo la luce",
      designShearForce: "Taglio di progetto per vite V_Ed",
      connectionResistance: "Resistenza della connessione F_v,Rd",
      yes: "Sì",
      no: "No",
    },
  },
  screwConnectorModule: {
    tabs: {
      screwFamilies: "Famiglie di viti",
      screwDiameters: "Diametri viti",
      screws: "Viti",
      washers: "Rondelle",
      timberMaterials: "Materiali legnosi",
      normativeTables: "Tabelle normative",
    },
    screwFamilies: {
      singular: "Famiglia di viti",
      plural: "Famiglie di viti",
      errors: {
        nameAlreadyPresent: "Esiste già una famiglia di viti con lo stesso nome.",
      },
      fields: {
        description: "Descrizione",
        threadType: "Tipo di filettatura",
        material: "Materiale",
        coating: "Rivestimento",
        ccCode: "Codice CC",
        tcCode: "Codice TC",
        scEuCode: "Codice SC (EU)",
        ecUsCode: "Codice ESR / EC (US)",
        wdUsCode: "Codice WD (US)",
        standardReference: "Riferimento normativo",
        connectionTypes: "Tipi di collegamento",
        substrates: "Supporti",
      },
    },
    threadTypes: {
      partial: "Filettatura parziale",
      full: "Filettatura totale",
    },
    headTypes: {
      countersunk: "Testa svasata",
      cylindrical: "Testa cilindrica",
      hexagonal: "Testa esagonale",
      round_plate: "Testa tonda (fissaggio piastre)",
      pan_plate: "Testa troncoconica (fissaggio piastre)",
      reinforced_pan_plate: "Testa troncoconica rinforzata (fissaggio piastre)",
      hexagonal_pan_plate: "Testa troncoconica esagonale (fissaggio piastre)",
    },
    tipTypes: {
      three_thorns: "3 THORNS",
      self_drilling: "Punta autoforante",
      standard: "Punta standard",
      hardwood: "Punta per legni duri",
    },
    connectionTypes: {
      shear_screws: "Viti a taglio",
      steel_to_timber: "Acciaio-legno",
      timber_to_timber: "Legno-legno",
    },
    substrateCategories: {
      solidwood_softwood: "Legno massiccio - conifera",
      glulam_softwood: "Legno lamellare - conifera",
      clt_softwood: "CLT - conifera",
      lvl_softwood: "LVL - conifera",
      solidwood_hardwood: "Legno massiccio - latifoglia",
      lvl_hardwood: "LVL - latifoglia",
      panels: "Pannelli",
      steel: "Acciaio",
    },
    compatibilityLevels: {
      notCompatible: "Non compatibile",
      restricted: "Compatibilità limitata",
      compatible: "Compatibile",
    },
    washers: {
      singular: "Rondella",
      plural: "Rondelle",
      fields: {
        code: "Codice",
        innerDiameter: "Diametro interno",
        outerDiameter: "Diametro esterno",
        height: "Altezza",
      },
    },
    screwDiameters: {
      singular: "Diametro vite",
      plural: "Diametri viti",
      filterByFamily: "Filtra per famiglia di viti",
      selectFamilyHint: "Seleziona una famiglia di viti per gestirne i diametri.",
      fields: {
        screwFamily: "Famiglia di viti",
        diameter: "Diametro",
        coreDiameter: "Diametro nocciolo",
        threadOuterDiameter: "Diametro esterno filettatura",
        headDiameter: "Diametro testa",
        headType: "Tipo di testa",
        tipType: "Tipo di punta",
        bit: "Inserto",
        yieldMoment: "Momento di snervamento",
        tensileCapacity: "Resistenza a trazione",
        washers: "Rondelle compatibili",
        resistanceValues: "Valori di resistenza",
      },
    },
    resistanceValues: {
      fields: {
        resistanceParam: "Parametro",
        substrateCategory: "Categoria del supporto",
        substrateSpecies: "Specie del supporto",
        preDrilled: "Preforato",
        valueNMm2: "Valore",
        referenceDensity: "Densità di riferimento",
        densityRange: "Intervallo di densità",
      },
      params: {
        fax_k: "f_ax,k",
        fhead_k: "f_head,k",
      },
      addRow: "Aggiungi valore di resistenza",
    },
    screws: {
      singular: "Vite",
      plural: "Viti",
      filterByDiameter: "Filtra per diametro vite",
      selectDiameterHint: "Seleziona un diametro vite per gestirne le viti.",
      fields: {
        screwDiameter: "Diametro vite",
        length: "Lunghezza",
        partialThreadLength: "Lunghezza filettatura parziale",
        fullThreadExtraLength: "Lunghezza extra filettatura totale",
        tipLength: "Lunghezza punta",
      },
    },
    timberMaterials: {
      singular: "Materiale legnoso",
      plural: "Materiali legnosi",
      fields: {
        materialCategory: "Categoria del materiale",
        species: "Specie",
        class: "Classe",
        characteristicDensity: "Densità caratteristica ρk",
        meanDensity: "Densità media ρmean",
        compressionStrength0: "Resistenza a compressione f_c,0,k",
        compressionStrength90: "Resistenza a compressione f_c,90,k",
        rollingShearStrength: "Resistenza a taglio di rotolamento f_r,k",
        shearStrength: "Resistenza a taglio f_v,k",
        standardReference: "Riferimento normativo",
      },
    },
    normativeTables: {
      title: "Tabelle normative",
      loadDurationClasses: {
        singular: "Classe di durata del carico",
        plural: "Classi di durata del carico",
        fields: {
          code: "Codice",
          name: "Nome",
          order: "Ordine",
        },
      },
      serviceClasses: {
        singular: "Classe di servizio",
        plural: "Classi di servizio",
        fields: {
          code: "Codice",
          name: "Nome",
        },
      },
      kmod: {
        title: "Valori k_mod",
        hint: "Fattore di modificazione k_mod per classe di servizio e classe di durata del carico.",
      },
      gammaM: {
        title: "Coefficienti parziali di sicurezza γM",
        fields: {
          standard: "Normativa",
          columnA: "Colonna A",
          columnB: "Colonna B",
        },
      },
    },
    errors: {
      familyAlreadyExists: "Esiste già una famiglia di viti con questo nome.",
      familyMissingName: "Il nome della famiglia di viti è obbligatorio.",
      familyNotFound: "La famiglia di viti non è stata trovata.",
      gammaMValueNotFound: "Il valore γM non è stato trovato.",
      kmodValueNotFound: "Il valore kmod non è stato trovato.",
      loadDurationClassAlreadyExists: "Esiste già una classe di durata del carico con questo codice.",
      loadDurationClassMissingCode: "Il codice della classe di durata del carico è obbligatorio.",
      loadDurationClassNotFound: "La classe di durata del carico non è stata trovata.",
      screwAlreadyExists: "L'articolo è già una vite.",
      screwDiameterNotFound: "Il diametro vite non è stato trovato.",
      screwNotFound: "La vite non è stata trovata.",
      serviceClassAlreadyExists: "Esiste già una classe di servizio con questo codice.",
      serviceClassNotFound: "La classe di servizio non è stata trovata.",
      timberMaterialAlreadyExists: "Esiste già un materiale legnoso con questa categoria e classe.",
      timberMaterialNotFound: "Il materiale legnoso non è stato trovato.",
      washerAlreadyExists: "Esiste già una rondella con questo codice.",
      washerMissingCode: "Il codice della rondella è obbligatorio.",
      washerNotFound: "La rondella non è stata trovata.",
    },
    units: {
      unitMm2: "mm²",
      unitNMm2: "N/mm²",
      unitKgM3: "kg/m³",
    },
    calculation: {
      shell: {
        navLabel: "Sezioni del modulo",
        openMenu: "Apri menu",
        closeMenu: "Chiudi menu",
        inputsGroup: "Dati di progetto",
        resultsGroup: "Risultati",
        toolsGroup: "Strumenti",
        resultsView: "Vista 3D e verifiche",
        resultsSolutions: "Soluzioni compatibili",
        resultsPositioning: "Posizionamento viti",
        generateReport: "Genera report PDF",
        reportQuestion: "Quale versione del report?",
        reportCompact: "Report compatto",
        reportCompactHint: "senza note metodologiche",
        reportFull: "Report completo",
        reportFullHint: "con note metodologiche",
        exportDxf: "Esporta DXF (dettaglio)",
        dotOk: "Completo",
        dotBad: "Da verificare",
        dotPending: "In attesa",
        tutorial: "Tutorial",
      },
      tutorial: {
        dialogLabel: "Tutorial",
        stepOf: (step: number, total: number) => `Passo ${step} di ${total}`,
        done: "Fatto",
        redo: "Ripeti",
        skip: "Salta il tutorial",
        back: "Indietro",
        next: "Avanti",
        finish: "Fine",
        confirmTitle: "Avviare il tutorial?",
        confirmText:
          "Una breve dimostrazione delle principali funzioni su un esempio: ogni passo viene eseguito per te, prosegui con Avanti. I campi vengono puliti per la dimostrazione, che usa solo dati di esempio e mostra una soluzione verificata; alla chiusura i tuoi dati vengono ripristinati così come erano. Durante il tutorial il calcolo non viene salvato.",
        cancel: "Annulla",
        start: "Avvia il tutorial",
        steps: {
          intro: {
            title: "Come compilare il file",
            text: "Procedi dall'alto verso il basso: ad ogni passo vedrai il mouse andare sui campi del pannello e inserire un esempio. All'avvio θ, b1, d1, t1, b2, d2, t2, Fn, Fv e Fa sono vuoti: finché non li compili il calcolo non viene eseguito.",
            note: "I campi sono stati puliti: alla chiusura del tutorial i tuoi dati vengono ripristinati.",
          },
          connectionType: {
            title: "2 · Tipologia di connessione e angolo",
            text: "Scegli il tipo di unione cliccando una delle otto icone, ognuna con il nome sotto (legno-legno, legno-legno / taglio singolo su due lati (3 elementi), legno-legno / taglio doppio (3 elementi), acciaio-legno con la lamiera come elemento 1 oppure acciaio-legno / taglio singolo su due lati, con due lamiere e il legno al centro, pannello-legno / taglio singolo, con un pannello a base di legno come elemento 1, pannello-legno / taglio singolo su due lati, con due pannelli e il legno al centro, oppure pannello-legno / taglio doppio, con il pannello al centro e il legno ai lati) e inserisci l'angolo θ tra i due elementi (da 1° a 180°).",
          },
          element1: {
            title: "3 · Dati Elemento 1",
            text: "(Nel caso a 3 elementi è l'elemento centrale.) Scegli materiale, specie e classe (con pannello-legno solo pannelli a base di legno: compensato, OSB/3-OSB/4, particelle; per i pannelli CLT anche strati e lato di inserimento: l'elemento 1 solo lato wide). Inserisci larghezza b1, lunghezza d1 (o spunta «Elemento continuo» se non c'è un'estremità) e spessore t1, in mm.",
          },
          element2: {
            title: "4 · Dati Elemento 2",
            text: "Stessi dati per l'elemento 2 (nel caso a 3 elementi valgono anche per l'elemento 3): materiale, b2, d2 e spessore t2 in mm. Per i pannelli CLT puoi scegliere il lato wide o narrow (narrow: una sola fila di viti sull'asse).",
          },
          threeMembers: {
            title: "5 · Connessione a 3 elementi",
            text: "Con «legno-legno / taglio singolo su due lati» o «legno-legno / taglio doppio» l'elemento 1 diventa quello centrale (caricato, ruotato di θ); gli elementi 2 e 3 sono i due laterali, fissi e identici: i dati dell'elemento 2 valgono anche per il 3. Nel primo caso le viti entrano dai due lati (testa su 2 e su 3, punta nel centrale), la resistenza è doppia (due connessioni a taglio singolo, una per lato); le punte dei due lati possono sovrapporsi oppure, con la spunta «Sfalsa di 1d le viti dei due lati», il lato 3 viene sfalsato. Nel secondo una sola vite attraversa i tre elementi (testa sull'elemento 2, punta nell'elemento 3): taglio doppio EC5 eq. 8.7 e una sola griglia di viti. In entrambi compare la verifica a spacco dell'elemento 1. Con «acciaio-legno / taglio singolo su due lati» le due lamiere (elementi 1 e 3) stanno ai lati del legno centrale (elemento 2): viti dai due lati come nel primo caso, spacco sul legno. Il CLT (sempre sul lato wide) è ammesso in una sola posizione nel legno-legno su due lati (CLT-legno-CLT oppure legno-CLT-legno, mai tre CLT) e non è selezionabile nel taglio doppio.",
            note: "Nella prova la tipologia viene impostata per un momento e poi riportata a quella scelta da te.",
          },
          panelTimber: {
            title: "6 · Connessione pannello-legno",
            text: "Con «pannello-legno / taglio singolo» l'elemento 1 è un pannello a base di legno (compensato, OSB/3-OSB/4, pannelli di particelle) dal lato testa della vite, caricato e ruotato di θ; l'elemento 2 è il legno, fisso. Si usano solo viti HBS e TBS con testa ≥ 2·d. Il rifollamento del pannello segue EC5 §8.3.1.3 (l'ETA non lo fornisce), le distanze minime il Prospetto 8.2 con a1 e a2 ×0,85 su entrambi gli elementi, il kmod è la radice del prodotto fra quello del pannello e quello del legno. Con «pannello-legno / taglio singolo su due lati» i pannelli (elementi 1 e 3, identici) stanno ai lati del legno centrale (elemento 2): viti dai due lati, resistenza doppia, spacco sul legno, come per l'acciaio-legno su due lati. Con «pannello-legno / taglio doppio» il pannello è l'elemento 1 centrale e il legno sta ai lati (elementi 2 e 3, identici): una sola vite attraversa i tre elementi, come nel legno-legno a taglio doppio (eq. 8.7), senza verifica a spacco. Il calcolo si blocca, con il motivo, se lo spessore del pannello è sotto il minimo (6 mm compensato, 8 mm particelle e OSB, comunque ≥ 1,2·d), se la testa è inferiore a 2·d o se la classe di servizio non è ammessa per quel pannello. Nel taglio doppio il CLT non è selezionabile per i legni laterali; nella Visualizzazione 3D i pannelli sono disegnati in un legno più scuro di quello massiccio o lamellare.",
            note: "Nella prova la tipologia viene impostata per un momento e poi riportata a quella scelta da te.",
          },
          loads: {
            title: "7 · Carichi di progetto",
            text: "Inserisci Fn (forza lungo l'asse dell'elemento 1), Fv (taglio, perpendicolare) e Fa (estrazione, positiva se sfila la vite) in kN. Scegli classe di servizio e durata del carico: γM si compila da solo in base alla normativa.",
          },
          connection: {
            title: "8 · Scelta connessione",
            text: "Scegli famiglia, tipologia di foro (preforo o senza), diametro e lunghezza della vite: i menu propongono solo viti compatibili con spessori e materiali. Se l'ETA richiede il preforo (ρk oltre 550 kg/m³, 500 per l'inox, salvo le famiglie HARDWOOD, LBS e KKT) il campo si blocca su «Preforo» e una nota ne spiega il motivo. Con «Usa rondella» puoi allungare la vite. Se compare «nessuna vite compatibile», modifica gli spessori o cerca una soluzione.",
          },
          grid: {
            title: "9 · Griglia viti",
            text: "Imposta righe e colonne di viti: sono limitate al massimo consentito dalle distanze minime. Con «Distribuisci le viti nella zona ammissibile» (attiva di default, con le distanze minime automatiche) le file e le colonne scelte si portano ai limiti della zona ammissibile invece di restare ravvicinate: i passi effettivi compaiono sotto i campi s1 e s2. Puoi anche sfalsare le viti, spostare il centro della griglia o calcolare il numero massimo di viti.",
          },
          view: {
            title: "10 · Vista 3D e verifiche",
            text: "Controlla la Visualizzazione 3D e le verifiche (taglio ed estrazione): l'esito è «Ammissibile» o «Non ammissibile». I valori di calcolo e le formule sono nelle schede dei risultati.",
          },
          solutions: {
            title: "11 · Soluzioni compatibili",
            text: "Non sai quale vite usare? Con «Trova soluzioni compatibili» il programma propone le viti e il numero minimo che verificano tutto. Le spunte «Preforo» e «Rondella» restringono la ricerca; «Usa questa» applica la soluzione.",
          },
          positioning: {
            title: "12 · Posizionamento viti",
            text: "Qui vedi dove cadono le viti rispetto ai bordi di ciascun elemento: i disegni riportano le quote dal bordo all'asse della vite e la tabella elenca le distanze di ogni vite. Ora la scheda si apre e scorre verso il basso.",
            note: "La scheda torna com'era (aperta o chiusa) quando passi al passo successivo.",
          },
          reportPreview: {
            title: "13 · Anteprima del report",
            text: "Con «Visualizza report» vedi il report PDF che scarichi, nella tua lingua: ora lo vedi aprirsi e scorrere verso il basso, pagina dopo pagina. Con la barra in alto puoi cambiare zoom, passare alla pagina singola o a due pagine e vedere l'anteprima in bianco e nero.",
            note: "Se mancano dati, per la prova vengono inseriti i valori di esempio (rimossi alla chiusura).",
          },
          reportPdf: {
            title: "14 · Report PDF",
            text: "Da Strumenti › Genera report PDF scegli la versione compatta o completa; lo stesso comando è nella barra del titolo e nella scheda Report di calcolo. I dati del calcolo vengono salvati automaticamente: non serve salvarli a mano.",
          },
          dxf: {
            title: "15 · Esporta DXF",
            text: "Da Strumenti › Esporta DXF (dettaglio), o dal pulsante «Esporta DXF» della scheda Report di calcolo, scarichi il dettaglio costruttivo per il CAD: vista frontale degli elementi, viste con le quote dai bordi, viti con il diametro reale, cartiglio e tabella delle posizioni (anche per la connessione a 3 elementi: le viti sfalsate del lato 3 stanno su un layer a parte; il cartiglio riporta anche i passi effettivi quando le viti sono distribuite). Il disegno è in scala reale 1:1 e il comando è attivo solo a connessione verificata.",
            note: "Nel tutorial il file non viene scaricato: il cursore mostra solo dove si trova il comando.",
          },
        },
      },
      status: {
        pending: "In attesa dei dati",
        verified: "Verificato",
        notVerified: "Non verificato",
      },
      sections: {
        tipologia: "Tipologia di connessione e angolo",
        element1: "Dati Elemento 1",
        element1Panel: "Dati Elemento 1 (pannello)",
        element1Central: "Dati Elemento 1 (centrale)",
        element1PanelCentral: "Dati Elemento 1 (pannello centrale)",
        element13Steel: "Dati Elementi 1 e 3 (lamiere)",
        element13Panel: "Dati Elementi 1 e 3 (pannelli)",
        element2: "Dati Elemento 2",
        element2Central: "Dati Elemento 2 (legno centrale)",
        element23: "Dati Elementi 2 e 3 (laterali)",
        loads: "Carichi di progetto",
        connection: "Scelta connessione",
        grid: "Griglia viti",
      },
      engine: {
        a4Reduce: {
          notThin: (t: string, fiveD: string) => `— non applicata: spessore t=${t}mm non inferiore a 5d=${fiveD}mm.`,
          spacing: (twentyFiveD: string, values: string) =>
            `— non applicata: richiede a1, a3,t e a3,c ≥ 25d=${twentyFiveD}mm (attuali: ${values}).`,
        },
        block: {
          noScrew:
            "nessuna vite compatibile con gli spessori e i materiali inseriti: modifica gli spessori t1/t2 o i materiali, oppure scegli un'altra famiglia/diametro.",
          length: (L: number, rule: string, values: string, washerH: number) =>
            `la lunghezza della vite (L=${L} mm) non è compatibile con gli spessori: serve ${rule} (${values}${washerH > 0 ? `, rondella h=${washerH} mm` : ""}).`,
          partialThread: (rule: string, aRule: { rule: string; values: string } | null, values: string) =>
            `vite a filetto parziale non compatibile con gli spessori (regola di compatibilità): serve ${rule}${aRule ? ` e ${aRule.rule} (qui ${aRule.values})` : ""} (qui ${values}).`,
          penetrationRule: (screw: string) =>
            `vite non compatibile con gli spessori (regola di compatibilità su lunghezza/filetto/penetrazione per ${screw}).`,
          minSize: (screw: string) =>
            `${screw} non compatibile con spessore/larghezza minimi degli elementi (regola di compatibilità): aumenta gli spessori o scegli un diametro minore.`,
          emptyFields: (tutorial: string) => `compila i campi ancora vuoti. Vedi ${tutorial} per la compilazione guidata.`,
          cltNarrowPenetration: (lt1: string, pen: string, min: string, neededL: string) =>
            `penetrazione nel pannello CLT (lato narrow) L−${lt1} = ${pen} mm inferiore al minimo 10·d = ${min} mm richiesto dall'ETA-11/0030: il metodo di calcolo non è applicabile. Aumenta la lunghezza della vite (L ≥ ${lt1}+10d = ${neededL} mm) o scegli una vite di diametro minore.`,
          penetration: (ptN: number, lt1: string, pen: string, min: string, neededL: string) =>
            `penetrazione nell'elemento ${ptN} L−${lt1} = ${pen} mm non superiore al minimo 4·d = ${min} mm: il metodo di calcolo non è applicabile. Aumenta la lunghezza della vite (L > ${lt1}+4d = ${neededL} mm) o scegli una vite di diametro minore.`,
        },
        cltWideOnly: {
          panelSides:
            "Unione pannello-legno su due lati: le viti entrano dalle due facce larghe del legno centrale: ammesso solo il lato wide.",
          steelSides:
            "Unione acciaio-legno su due lati: le viti entrano dalle due facce larghe del legno centrale: ammesso solo il lato wide.",
          three:
            "Unione a 3 elementi: le viti attraversano gli elementi 2 e 3 dalla faccia larga: ammesso solo il lato wide.",
        },
        epsNote: {
          element1:
            '— angolo fra l\'asse della vite e la fibratura di elemento 1, usato SOLO per f<sub>h,1,k</sub> (rifollamento): diverso dall\'angolo forza/fibratura usato per le distanze minime a1/a2, che resta invariato. Deseleziona "automatico" per impostarlo manualmente.',
          element2: (elementRef: string) =>
            `— angolo fra l'asse della vite e la fibratura di <span id="lbEps2n">${elementRef}</span>, usato SOLO per f<sub>h,k</sub> (rifollamento): diverso dall'angolo forza/fibratura usato per le distanze minime a1/a2 (φ<sub>2</sub> sopra, legato a θ/Fn/Fv), che resta invariato. Deseleziona "automatico" per impostarlo manualmente in entrambi i casi.`,
          cltWide:
            'Pannello CLT, lato wide (ETA-11/0030): ε = angolo fra la forza e la fibratura dello strato esterno, usato in f<sub>h,k</sub> (rifollamento come per il legno massiccio con il 110% della densità minima degli strati). Automatico = calcolato da θ, F<sub>n</sub>/F<sub>v</sub> e orientamento dello strato esterno; deseleziona "automatico" per impostarlo manualmente.',
        },
        layout: {
          spacingBelowMin: (spacing: string, min: string) =>
            `⚠ ${spacing} mm inferiore al minimo richiesto (${min} mm).`,
          lengthBelowEndDistance: (name: string, current: number, required: string) =>
            `⚠ aumenta ${name} (attuale ${current} mm): deve essere ≥ ${required} mm — la lunghezza dell'elemento non può essere inferiore alla distanza di estremità richiesta.`,
          cltHalfThickness: (d2: string, required: string, minThickness: string) =>
            `⚠ d2 = b2/2 = ${d2} mm è inferiore a ${required} mm: aumenta lo spessore del pannello (serve uno spessore totale ≥ ${minThickness} mm) oppure riduci il diametro della vite.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— righe e/o colonne ridotte a ${rows}×${cols} per contenere entro 2·b1 = ${max} mm la distanza fra le viti poste agli estremi della griglia`,
        },
        maxScrews: {
          none: "<strong>Nessuna disposizione ammissibile</strong> — nemmeno una vite rispetta contemporaneamente le distanze minime di entrambi gli elementi.",
          max: (rows: number, cols: number, total: number) =>
            `<strong>Massimo teorico: ${rows} righe × ${cols} colonne = ${total} viti</strong>`,
          spacing: (s1: string, s2: string) =>
            ` (interasse s<sub>1</sub>=${s1} mm lungo l'asse elemento 1, s<sub>2</sub>=${s2} mm lungo l'asse elemento 2 — reticolo obliquo, minimo normativo per entrambi gli assi)`,
          offset: (x: string, y: string) =>
            `— griglia spostata dal centro (spostamento centro: orizz.=${x} mm, vert.=${y} mm) per starci per intero: una griglia REGOLARE (stesso passo uniforme, nessuno sfalsamento) centrata sull'incrocio degli assi elemento1/elemento2 ne conterrebbe di meno, perché l'inviluppo ammissibile non è simmetrico rispetto a quell'incrocio.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— angolo radente (θ≤20° o θ≥160°): righe e/o colonne ridotte a ${rows}×${cols} per contenere entro 2·b<sub>1</sub> = ${max} mm la distanza fra le viti poste agli estremi della griglia. Disattiva la spunta corrispondente per considerare tutte le righe/colonne geometricamente ammissibili.`,
          capped:
            "— ricerca limitata a un tetto pratico di 500×500: al passo minimo, in questa geometria (elemento 2 lato superiore infinito e/o elemento 1 lato F infinito), il numero di viti ammissibili potrebbe essere anche superiore.",
        },
        panel: {
          notAdmitted: (classe: string) =>
            `il pannello «${classe}» non è ammesso dall'ETA-11/0030 (ammessi compensato EN 636, OSB/3-OSB/4, pannelli di particelle EN 312): scegli un altro materiale per l'elemento 1.`,
          noEmbedment: (d: string) =>
            `f_h,k del pannello non determinabile per d = ${d} mm (EC5 §8.5.1.2: per d > 8 mm solo compensato, OSB e pannelli di particelle).`,
          noKmod: (classe: string, serviceClass: string) =>
            `k_mod del pannello «${classe}» non definito in EC5 Prospetto 3.1 per la classe di servizio ${serviceClass}: il pannello non è idoneo a questa classe di servizio.`,
          tooThin: (t1: string, minT: number, tMin: string) =>
            `spessore del pannello t1 = ${t1} mm inferiore al minimo max(${minT} mm; 1,2·d) = ${tMin} mm (ETA-11/0030): il metodo di calcolo non è applicabile.`,
          smallHead: (dk: string, twoD: string) =>
            `diametro della testa dk = ${dk} mm inferiore a 2·d = ${twoD} mm: le formule EC5 §8.3.1.3 per f_h,k dei pannelli richiedono testa ≥ 2·d.`,
          onlyHbsTbs: "nel modulo pannello-legno sono ammesse solo viti HBS e TBS.",
        },
        predrill: {
          reason: (rho: string, limit: number, element: number, hard: boolean, stainless: boolean) =>
            `ρk = ${rho} kg/m³ > ${limit} kg/m³ (elemento ${element}${hard ? ", latifoglia" : ""}, viti in ${stainless ? "acciaio inox" : "acciaio al carbonio"})`,
          forced: (reasons: string) => `— Preforo imposto (ETA-11/0030 pag. 8 e 14): ${reasons}.`,
        },
        resistance: {
          ropeCap: (rp: string) =>
            ` — F<sub>ax</sub>/4 limitato al 100% della parte di Johansen (${rp} N, EC5 §8.2.2(2))`,
          mode: (mode: string) => `modo (${mode})`,
          steelPlate: {
            thin: "lamiera sottile",
            thick: "lamiera spessa",
            interp: "lamiera spessore intermedio",
          },
        },
        results: {
          spreadNote: (s1: string, s2: string, s1Min: string, s2Min: string) =>
            `Viti distribuite nella zona ammissibile: s<sub>1</sub> = ${s1} mm, s<sub>2</sub> = ${s2} mm (minimi richiesti ${s1Min} / ${s2Min} mm); le viti esterne stanno alle distanze minime di bordo.`,
          staggered: (lEff: string, limit: string, d: string, dir: string, element: number) =>
            `ℹ viti dei due lati sfalsate: L−h = ${lEff} mm supera ${limit} mm, quindi le viti dell'elemento 3 sono spostate di 1·d = ${d} mm (direzione ${dir}) rispetto a quelle dell'elemento ${element}, per evitare che le punte si sovrappongano nel centrale.`,
          tipsOverlap: (steel3: boolean, lEff: string, limit: string, tMinus: string, fourD: string, gridTitle: string) =>
            `ℹ punte dei due lati sovrapposte nel ${steel3 ? "legno" : "membro"} centrale: L−h = ${lEff} mm supera ${limit} mm. È ammesso (EC5 §8.3.1.1(7)) perché (t − t2) = ${tMinus} mm > 4d = ${fourD} mm; per sfalsare le viti dei due lati di 1·d attiva la spunta nel pannello ${gridTitle}.`,
          washerNet: (h: string, lEff: string) => ` — al netto della rondella (h=${h} mm): L−h=${lEff} mm`,
          screwTooLong: (l: string, rule: string, margin: string, twoSides: boolean) =>
            `⚠ vite troppo lunga (L=${l}): deve essere L < ${rule} (almeno ${margin} mm dalla punta alla faccia opposta${twoSides ? " dell'elemento centrale, EC5: (t − t2) > 4d" : ""}).`,
          screwTooShortClt: (l: string, penetration: string, tenD: string, need: string) =>
            `⚠ vite troppo corta (L=${l}): penetrazione nel pannello CLT (lato narrow) ${penetration}, minimo 10·d = ${tenD} mm (ETA-11/0030) — serve ${need}.`,
          screwTooShort: (l: string, rule: string, element: number) =>
            `⚠ vite troppo corta (L=${l}): deve essere ${rule} (penetrazione minima di 4d nell'elemento ${element}).`,
          cltInfo: (thickness: string, rhok: string, classe: string) =>
            `Spessore totale pannello: ${thickness} mm · ρk = ${rhok} kg/m³ (classe ${classe})`,
          cltNarrowThin: (threshold: string) =>
            ` — ⚠ inferiore a 10·d = ${threshold} mm richiesti dall'ETA-11/0030 Tab. B1 per l'inserimento lato narrow.`,
        },
        screwLists: {
          noFamily: (el1: string, el2: string) =>
            `⚠ nessuna famiglia compatibile (tipo connessione/classe di servizio) ha una vite compatibile con gli spessori correnti (${el1}mm, ${el2}mm): ridurre lo spessore.`,
          noDiameter: (el1: string, el2: string) =>
            `⚠ nessun diametro di questa famiglia ha una lunghezza compatibile con gli spessori correnti (${el1}mm, ${el2}mm): ridurre lo spessore o scegliere un'altra famiglia.`,
          noLength: (lower: string, upper: string, washerH: number, el1: string, el2: string) =>
            `⚠ nessuna lunghezza di questa famiglia/diametro rientra fra ${lower} e ${upper} mm${washerH > 0 ? ` (con rondella h=${washerH} mm)` : ""} (spessori correnti ${el1}mm, ${el2}mm): ridurre lo spessore o scegliere un diametro diverso.`,
        },
        threeNotes: {
          twoSides:
            "Elemento 1 centrale (caricato, ruotato di θ). Elementi 2 e 3 laterali, fissi e identici: i dati dell'elemento 2 valgono anche per l'elemento 3. Viti inserite dai due lati esterni (testa sull'elemento 2 e sull'elemento 3, punta nell'elemento 1): stessa vite e griglia specchiata; il carico si ripartisce a metà sulle due connessioni (una per lato, ciascuna a taglio singolo con un solo piano di taglio).",
          double:
            "Elemento 1 centrale (caricato, ruotato di θ). Elementi 2 e 3 laterali, fissi e identici: i dati dell'elemento 2 valgono anche per l'elemento 3. Un'unica vite attraversa i tre elementi: testa sull'elemento 2, passaggio nell'elemento 1, punta nell'elemento 3 (taglio doppio, EC5 eq. 8.7): una sola griglia di viti, nessuno sfalsamento.",
          steelSides:
            "Elemento 2 in legno centrale e fisso. Lamiere d'acciaio ai lati: elemento 1 e la sua copia identica (elemento 3), caricate e ruotate di θ; i dati dell'elemento 1 valgono anche per l'elemento 3. Viti inserite dai due lati esterni (testa sulle lamiere, punta nel legno): stessa vite e griglia specchiata; il carico si ripartisce a metà sulle due connessioni (una per lato, ciascuna a taglio singolo con un solo piano di taglio).",
          panelDouble:
            "Elemento 1 = pannello a base di legno, centrale (caricato, ruotato di θ). Elementi 2 e 3 in legno, laterali, fissi e identici: i dati dell'elemento 2 valgono anche per l'elemento 3. Un'unica vite (solo HBS e TBS, testa ≥ 2·d) attraversa i tre elementi: testa sull'elemento 2, passaggio nel pannello, punta nell'elemento 3 (taglio doppio, EC5 eq. 8.7): una sola griglia di viti, nessuno sfalsamento. Rifollamento del pannello da EC5 §8.3.1.3 / §8.5.1.2; distanze con a1, a2 ×0,85; kmod = √(kmod,pannello·kmod,legno).",
          panelSides:
            "Elemento 2 in legno centrale e fisso. Pannelli a base di legno ai lati: elemento 1 e la sua copia identica (elemento 3), caricati e ruotati di θ; i dati dell'elemento 1 valgono anche per l'elemento 3. Solo viti HBS e TBS con testa ≥ 2·d. Viti inserite dai due lati esterni (testa sui pannelli, punta nel legno): stessa vite e griglia specchiata; il carico si ripartisce a metà sulle due connessioni (una per lato, ciascuna a taglio singolo con un solo piano di taglio). Rifollamento del pannello da EC5 §8.3.1.3 / §8.5.1.2; distanze con a1, a2 ×0,85; kmod = √(kmod,pannello·kmod,legno).",
        },
        violations: {
          edge2: (code: string) => `elemento 2: bordo (${code})`,
          end2: (code: string) => `elemento 2: estremità inferiore (${code})`,
          line1: (n: string, code: string) => `elemento 1: retta ${n} (${code})`,
          side3: (text: string) => `lato elemento 3 (sfalsate di 1d): ${text}`,
        },
      },
      elementRefs: {
        element1: "elemento 1",
        element1Central: "elemento 1, centrale",
        elements13Steel: "elementi 1 e 3, lamiere",
        elements13Panel: "elementi 1 e 3, pannelli",
        element2: "elemento 2",
        element2Central: "elemento 2, legno centrale",
        elements23: "elementi 2 e 3",
      },
      typeSection: {
        theta: "θ — angolo elemento 1",
        thetaUnit: "0°→F giù · 180°→F su · antiorario",
        connectionType: "Tipo connessione",
        types: {
          legno: "legno-legno / taglio singolo",
          legno3: "legno-legno / taglio singolo su due lati",
          legno3d: "legno-legno / taglio doppio",
          acciaio: "acciaio-legno / taglio singolo",
          acciaio3: "acciaio-legno / taglio singolo su due lati",
          pannello: "pannello-legno / taglio singolo",
          pannello3: "pannello-legno / taglio singolo su due lati",
          pannello3d: "pannello-legno / taglio doppio",
        },
      },
      // Names of the engine's material type keys (and of the species fallbacks of panels/steel)
      materialTypes: {
        solidwoodSoftwood: "Legno massiccio conifera",
        glulam: "Legno lamellare incollato",
        clt: "Pannelli CLT",
        lvl: "LVL (Laminated Veneer Lumber)",
        solidwoodHardwood: "Legno massiccio latifoglia",
        panels: "Pannelli a base di legno",
        steel: "Acciaio",
        panelsSpecies: "Pannelli OSB / cemento-legno",
        steelSpecies: "Acciaio da carpenteria",
      },
      element: {
        materialType: "Tipo di materiale",
        materialTypeUnit: "DB timber",
        species: "Specie legnosa",
        class: "Classe",
        customName: "Nome materiale",
        customNameUnit: "personalizzato",
        customNamePlaceholder: "es. Legno speciale XYZ",
        rhoHint:
          "ρ<sub>k</sub> e ρ<sub>mean</sub> si compilano dal database alla scelta del materiale e restano modificabili.",
        rhoMissing: "ρ<sub>k</sub> N.D. nel database, inserire manualmente",
        fiber: (n: number) => `φ${n === 1 ? "₁" : "₂"} — orientamento fibra`,
        fiberUnit1: "rispetto all'asse dell'elemento 1",
        fiberUnit2: "rispetto all'asse dell'elemento 2",
        fiberUnit23: "rispetto all'asse degli elementi 2 e 3",
        eps: (n: number) => `ε${n === 1 ? "₁" : "₂"} — angolo asse vite/fibratura`,
        epsUnit1: "solo per fₕ,ₖ elemento 1",
        epsUnit2: "solo per fₕ,ₖ elemento 2",
        epsUnit23: "solo per fₕ,ₖ elementi 2 e 3",
        epsAuto: (n: number) => `ε<sub>${n}</sub> automatico`,
        epsAutoUnit1: "sempre 90° (vite sempre ⟂ fibratura, indipendente da θ)",
        epsAutoUnit2: "90° per acciaio-legno (vite sempre ⟂ fibratura, indipendente da θ); θ-derivato per legno-legno",
        continuous: "Elemento continuo",
        continuousUnit: "nessuna estremità reale: prosegue oltre l'unione su entrambi i lati",
        thickness: (n: number) => `spessore t${n}`,
        rhomeanUnit: "kg/m³ — per Kₛₑᵣ",
        rhomeanMissing: "N.D. nel database per questa classe: inserire manualmente",
        douglas: "Specie Douglas",
        douglasUnit: "senza preforo: ETA-11/0030 richiede a1 e a3 ×1,5",
        a4reduce: (n: number) => `Riduci a4,c,${n} (bordo non caricato) a 3d`,
        a4reduceUnit1: "legno sottile, richiede a1 e a3,t,1 ≥ 25d",
        a4reduceUnit2: "legno sottile, richiede a1 e a3 ≥ 25d",
        a1e: (n: number) => `a1,elemento${n}`,
        a2e: (n: number) => `a2,elemento${n}`,
        a1Unit: "mm · passo min. lungo asse",
        a2Unit: "mm · passo min. perpendicolare",
        a3t1: "a3,t,1 (testa caricata)",
        a3t2Unit: "mm · estremità, 0°≤θ≤90°",
        a3c2Unit: "mm · estremità, 90°<θ≤180°",
      },
      clt: {
        lato: "Lato di inserimento",
        latoOptions: {
          narrow: "Narrow (bordo stretto, vite parallela al piano)",
          wide: "Wide (faccia larga, vite ortogonale al piano)",
        },
        latoHint1: "Elemento 1 in connessione legno-legno: ammesso solo il lato wide.",
        latoHint2:
          "Lato narrow: b<sub>2</sub> = spessore totale del pannello e d<sub>2</sub> = b<sub>2</sub>/2 (automatici); t<sub>2</sub> (profondità di inserimento) è editabile.",
        numStrati: "Numero di strati",
        disposizione: "Disposizione strati",
        disposizioni: {
          fisso: "Fissa (alternata 0°/90°)",
          alternati: "Alternati",
          accoppiati: "Accoppiati (0° accoppiati sugli strati esterni)",
          zeroDoppio: "Tutti gli strati a 0° doppi (solo 11 strati)",
          userDefined: "Definizione utente (per strato)",
        },
        spessoriMode: "Spessore strati",
        spessoriModes: {
          uniforme: "Uniforme (stesso spessore per tutti gli strati)",
          differenziato: "Differenziato (0° / 90°)",
        },
        spessoreSingle: "Spessore singolo strato",
        spessore0: "Spessore strati a 0°",
        spessore90: "Spessore strati a 90°",
        classe: "Classe legno massiccio conifera",
        userLayers: "Strati — spessore, direzione, classe",
        userLayersUnit: "dall'esterno verso l'interno",
        layer: (i: number) => `Strato ${i}`,
        layerThickness: "Spessore (mm)",
        layerDirection: "Direzione fibratura",
        layerClass: "Classe legno massiccio",
      },
      loadsSection: {
        fn: "Fn, forza assiale",
        fnUnit: "kN · lungo asse elemento 1",
        fv: "Fv, forza di taglio",
        fvUnit: "kN · perpendicolare asse elemento 1",
        fa: "Fa, forza di estrazione",
        faUnit: "kN · lungo l'asse della vite, positiva = estrazione",
        resultantAngle: "angolo risultante/fibratura",
        withdrawal: "estrazione",
        notYetVerified: "non ancora verificata a sfilamento",
        serviceClass: "Classe di servizio",
        loadDuration: "Durata del carico",
        gammaMRef: "Rif. γM",
        gammaMRefUnit: "connessioni/unioni",
        standards: {
          EC5: "EC5 (Prospetto 2.3 — connessioni)",
          NTC2018: "NTC2018 (Tab. 4.4.III — unioni)",
        },
        column: "Colonna",
        columns: {
          A: "A — produzione corrente",
          B: "B — con controllo FPC certificato",
        },
        gammaMAuto: "γ<sub>M</sub> automatico (da normativa selezionata)",
      },
      connectionSection: {
        family: "Famiglia vite",
        foro: "Tipologia foro",
        predrilled: "Preforo",
        nonPredrilled: "Senza preforo",
        diameter: "Diametro esterno filetto d",
        length: "Lunghezza vite L",
        lengthUnit: "mm · dal database viti",
        washer: "Usa rondella",
        washerUnit:
          "aumenta il diametro efficace di testa (d<sub>h</sub>) nella verifica a sfondamento testa (F<sub>ax,ε,Rk</sub>)",
        autoSpacing: "Distanze minime automatiche (a1, a2, a3, a4 da normativa)",
      },
      notes: {
        panel:
          "Elemento 1 = pannello a base legno (compensato, OSB/3-OSB/4, truciolari), lato testa della vite, caricato e ruotato di θ. Elemento 2 = legno, fisso. Solo viti HBS e TBS con testa ≥ 2·d. Infissione nel pannello da EC5 §8.3.1.3 (eq. 8.20–8.22) o §8.5.1.2 (eq. 8.36–8.37) per d &gt; 8 mm; distanze minime nel pannello da EC5 Prospetto 8.2 con α = 0 e senza dipendenza da ρ<sub>k</sub>, e a<sub>1</sub>, a<sub>2</sub> ×0,85 su pannello e legno (§8.3.1.3); k<sub>mod</sub> = √(k<sub>mod,pannello</sub>·k<sub>mod,legno</sub>) (§2.3.2.2).",
        continuous: (n: 1 | 2): string =>
          n === 1
            ? "— d1 e a3,t,1 non sono più richiesti (perdono significato: non esiste un'estremità reale). Il limite di posizionamento delle viti diventa direttamente un'estremità virtuale, interna e nascosta, pari a b2/(2·|cos(θ)|), misurata dall'intersezione degli assi dei due elementi."
            : "— d2 e a3,t,2/a3,c,2 non sono più richiesti (perdono significato: non esiste un'estremità reale). Il limite di posizionamento delle viti diventa direttamente un'estremità virtuale, interna e nascosta, pari a b1/(2·|cos(θ)|), misurata dall'intersezione degli assi dei due elementi.",
        continuousDisabled:
          "— non disponibile a θ=0°/180°: gli assi dei due elementi coincidono (elemento 1 ed elemento 2 sarebbero allineati sulla stessa retta).",
        steel1Spacing:
          "— elemento 1 in piastra d'acciaio: una piastra non ha fibra propria, quindi a1/a2 dell'elemento 1 non sono un requisito fisico e non influenzano il passo della griglia (EC5 §8.3.1.4): s1/s2 dipendono solo da a1,elemento2/a2,elemento2 (già ×0,7).",
        steel1Dist:
          "— elemento 1 in piastra d'acciaio: a4,t,1 = a4,c,1 = 1,5·1,1·d, a3,t,1 = 1,2·1,1·d (distanza minima foro nella piastra, non Prospetto 8.2 legno)",
        a3t1Rule:
          "— regola: nessuna vite può essere più vicina di a3,t,1 all'estremità reale dell'elemento 1 (linea 3 nel disegno), sia nel legno sia nella piastra d'acciaio — le viti in quella zona sono segnate in rosso e non contano per la resistenza.",
        foro:
          '— "Senza preforo": la formula delle distanze minime è ricavata automaticamente dalla famiglia di viti selezionata sopra (KKT per le famiglie KKT, formula HBS per le famiglie HBS con d = 6…12 mm, EC5 Prospetto 8.2 per tutte le altre viti e diametri, come da ETA-11/0030).',
        plateHw:
          "— famiglia HBS PLATE / LBS HARDWOOD EVO per unione legno-legno: ETA-11/0030 richiede a1 e a2 ×1,5 su entrambi gli elementi.",
        myk:
          "— My,k non presente nel database viti per questa famiglia/diametro: alcuni modi di rottura Johansen non sono considerati, il risultato può non essere conservativo. Verificare o completare i dati in Screw.xlsx.",
        limitGrid:
          "— limite disattivato: puoi inserire più viti di quelle geometricamente ammissibili; quelle fuori dall'inviluppo restano segnate in rosso e nel pannello sotto, a tuo rischio",
      },
      gridSection: {
        rows: "file (lungo asse elemento 1)",
        rowsUnit: "max da θ/vite",
        cols2: "colonne (lungo asse elemento 2)",
        cols23: "colonne (lungo asse elementi 2 e 3)",
        cltRowNote: "Elemento 2 in CLT, lato narrow: una sola fila di viti, in asse con lo spessore dell'elemento 2.",
        s1Unit: "mm · lungo asse elemento 1",
        s2Unit2: "mm · lungo asse elemento 2",
        s2Unit23: "mm · lungo asse elementi 2 e 3",
        offsetX: "Spostamento centro, orizzontale",
        offsetY: "Spostamento centro, verticale",
        spread: "Distribuisci le viti nella zona ammissibile (passo massimo, solo con distanze minime automatiche)",
        stagger: "Sfalsa le viti nella fila parallela alla fibratura di 1d (evita la riduzione di n<sub>ef</sub>)",
        staggerTwoSides:
          "Sfalsa di 1d le viti dei due lati se le punte si sovrappongono (altrimenti restano sovrapposte, purché (t − t<sub>2</sub>) &gt; 4d)",
        maxScrews: "Calcola numero massimo di viti",
        applyMax: "Applica righe/colonne",
        limitGrid: "Limita righe/colonne al numero massimo ammissibile (da θ e diametro vite)",
        limitGrazing:
          "Ad angolo radente (θ≤20° o θ≥160°) limita a 2×b<sub>1</sub> la distanza fra le viti poste agli estremi della griglia",
      },
      view: {
        title: "Visualizzazione 3D",
        showDims: "Mostra quote",
        branchMeta: (theta: string, branch: string) => `θ = ${theta}°  ·  ramo ${branch}`,
        branchPending: "ramo —",
        hintControls:
          "Trascina = ruota · rotellina = zoom · tasto destro = sposta · menu Vista = viste predefinite · menu Elementi = mostra o nascondi i componenti.",
        hintTail: "il contesto geometrico; le viti sono il prodotto verificato dal calcolo. Le viti fuori inviluppo sono in rosso.",
        hintSubjects: {
          two: "I due elementi in legno sono",
          three: "I tre elementi in legno sono",
          panel: "Il pannello e il legno sono",
          steel3: "I tre elementi (due lamiere e il legno) sono",
          panel3: "I tre elementi (due pannelli e il legno) sono",
        },
        legend: {
          el2: "elemento 2 (legno, fisso)",
          el2Central: "elemento 2 (legno centrale, fisso)",
          el23: "elementi 2 e 3 (legno, fissi, identici)",
          el1: "elemento 1 (ruotato di θ)",
          el1Panel: "elemento 1 (pannello, ruotato di θ)",
          el1Central: "elemento 1 (centrale, ruotato di θ)",
          el1PanelCentral: "elemento 1 (pannello centrale, ruotato di θ)",
          el13Steel: "elementi 1 e 3 (lamiere, ruotati di θ)",
          el13Panel: "elementi 1 e 3 (pannelli, ruotati di θ)",
          screwOk: "vite ammissibile",
          screwBad: "vite non ammissibile",
          dims: "quote (valori di input)",
          fn: "Fn (assiale)",
          fv: "Fv (taglio)",
          fa: "Fa (estrazione, lungo asse vite)",
        },
        toolbar: {
          views: {
            iso: "Vista 3D",
            front: "Prospetto",
            top: "Pianta",
            side: "Lato",
          },
          menuView: "Vista",
          menuElements: "Elementi",
          resetView: "Reimposta vista",
          elements: {
            element1: "Elemento 1",
            elements13: "Elementi 1 e 3",
            element2: "Elemento 2",
            elements23: "Elementi 2 e 3",
            screws: "Viti",
            forces: "Forze",
            grid: "Griglia",
            solid: "Solido",
            ghost: "Semi-trasparente",
            hidden: "Nascosto",
          },
        },
        capture: {
          legibilityWarning: (lines: string) =>
            `Attenzione: alcune quote nelle viste del report potrebbero non essere leggibili (${lines}).`,
          legibilityLine: (view: string, clipped: number, overlapping: number) =>
            `${view}: ${clipped} quote tagliate, ${overlapping} sovrapposte`,
        },
      },
      stats: {
        layout: "Layout viti",
        layoutOk: "Ammissibile",
        layoutBad: "Non ammissibile",
        combined: "Carico combinato (EC5 8.28)",
        fvRkScrew: "F<sub>v,Rk</sub> per vite",
        nefAxial: "n efficace (assiale)",
        nefShear: "n efficace (taglio)",
        admissibleScrews: (ok: number, total: number, twoSides: boolean) =>
          `viti amm. ${ok}/${total}${twoSides ? " per lato (×2 lati)" : ""}`,
        fvRkUnion: "F<sub>v,Rk</sub> unione",
        totalKn: "kN totali",
        faxRd: "F<sub>ax,Rd</sub>",
        kmodGammaM: "k<sub>mod</sub> · γ<sub>M</sub>",
        fvEd: "F<sub>v,Ed</sub>",
        fvRdUnion: "F<sub>v,Rd</sub> unione",
        kser: "K<sub>ser</sub> per piano di taglio",
        kserUnit: "(SLE)",
        notAvailable: "N.D.",
        shearCheck: "Verifica a taglio F<sub>v,Ed</sub>/F<sub>v,Rd</sub>",
        axialCheck: "Verifica a estrazione F<sub>ax,Ed</sub>/F<sub>ax,Rd</sub>",
        splitCheck: "Verifica a spacco elemento centrale",
        verified: "verificato",
        notVerified: "NON verificato",
      },
      violations: {
        title: "Viti fuori inviluppo",
        allOk: "Tutte le viti rispettano le distanze minime richieste.",
        blockedPrefix: "Calcolo non eseguito: ",
      },
      solutions: {
        title: "Soluzioni compatibili",
        intro:
          "Per ogni vite attualmente compatibile (famiglia/diametro/lunghezza, dopo i filtri di spessori/classe di servizio/tipo connessione) cerca il numero minimo di viti (righe×colonne, griglia centrata) che soddisfa tutte le verifiche attive (taglio; carico combinato ed estrazione solo se F<sub>a</sub>&gt;0). Calcolo pesante: parte su richiesta; dopo la prima ricerca si aggiorna da sola ad ogni modifica dei dati di input (la tabella precedente viene cancellata). Durante la ricerca vengono temporaneamente forzati \"Distanze minime automatiche\" e \"Spostamento centro\"=0 per tutte le viti candidate, poi ripristinato lo stato corrente.",
        predrilled: "Preforo",
        washer: "Rondella",
        optionsHint:
          "Senza spunte: solo soluzioni senza preforo e senza rondella (le impostazioni del form non contano; le viti per cui l'ETA impone il preforo sono comunque verificate con preforo). Con una spunta: solo soluzioni che usano quell'opzione (con \"Rondella\" provate tutte le rondelle compatibili).",
        search: "Trova soluzioni compatibili",
        preparing: "Preparazione...",
        progress: (processed: number, total: number, found: number) =>
          `Valutazione in corso: ${processed}/${total} viti candidate (${found} soluzioni trovate finora)...`,
        missingTitle: (count: number) =>
          count === 1
            ? "Impossibile generare le soluzioni: compilare il campo indicato."
            : "Impossibile generare le soluzioni: compilare i campi indicati.",
        missingPanel: (panel: string) => `— pannello «${panel}»`,
        none: (candidates: number) =>
          `Nessuna soluzione trovata su ${candidates} viti candidate: nessuna combinazione famiglia/diametro/lunghezza/griglia soddisfa tutte le verifiche entro il numero massimo di viti geometricamente ammissibile.`,
        summary: (found: number, candidates: number, shown: number) =>
          `${found} soluzioni trovate su ${candidates} viti candidate valutate — prime ${shown} mostrate, ordinate per % taglio (poi % combinato, poi % estrazione).`,
        selectNew:
          "La configurazione attuale non è più valida con i dati inseriti: <b>selezionare la nuova soluzione compatibile</b> dalla tabella (pulsante «Usa questa»).",
        columns: {
          family: "Famiglia",
          grid: "Righe×colonne (tot.)",
          options: "Opzioni",
          shear: "η taglio",
          combined: "η combinato",
          withdrawal: "η estrazione",
        },
        predrilledOption: "preforo",
        apply: "Usa questa",
      },
      formulas: {
        noDiameter:
          'dato non disponibile (nessun diametro vite valido selezionato — vedi l\'avviso sopra "Diametro esterno della filettatura").',
        staggerNef: (n: number) =>
          `Sfalsamento ≥1d attivo (EC5 Prospetto 8.1, nota 8): nessuna riduzione, n<sub>ef</sub> = n = ${n} (viti ammissibili).`,
        nefAxial: (n: number, pow: string, ninety: string, result: string) =>
          `n<sub>ef,assiale</sub> = max(n<sup>0,9</sup>; 0,9·n) — ETA-11/0030 (viti autofilettanti, fila di n viti parallela al carico) — n=${n} → max(${pow}; ${ninety}) = ${result}`,
        staggerNefShear: (n: number) =>
          `Sfalsamento ≥1d attivo (EC5 Prospetto 8.1, nota 8): nessuna riduzione, n<sub>ef,taglio</sub> = n = ${n}.`,
        rowParallel: (
          nRow: number,
          a1: string,
          a1d: string,
          element: number,
          reps: number,
          extra: string,
        ) =>
          `Fila di ${nRow} viti parallela al carico (passo effettivo a1=${a1}mm=${a1d}d, elemento ${element}), ripetuta ${reps} volte (file indipendenti)${extra}`,
        table81Steel: (a1Eff: string, ratio: string) =>
          ` (per il Prospetto 8.1, acciaio-legno: a<sub>1</sub>/0,7 = ${a1Eff}mm=${ratio}d)`,
        kefLine: (kef: string, n: number, nef0: string) =>
          `k<sub>ef</sub> = ${kef} (EC5 Prospetto 8.1) → n<sub>ef,0</sub> = n<sup>k<sub>ef</sub></sup> = ${n}<sup>${kef}</sup> = ${nef0}`,
        angleLine: (angle: string, panel: string, nef0: string, nRow: number, result: string) =>
          `angolo effettivo forza/fibra = ${angle}°${panel} → n<sub>ef,taglio</sub> = n<sub>ef,0</sub> + (n − n<sub>ef,0</sub>)·(angolo/90°) = ${nef0} + (${nRow} − ${nef0})×(${angle}/90) = ${result}`,
        panelNoInterp: " (pannello-legno: nessuna interpolazione con l'angolo, come nel report di riferimento)",
        totalShear: (reps: number, result: string, crit: string) =>
          `n<sub>ef,taglio</sub> (totale) = ${reps} × ${result}${crit}`,
        critElement: (element: number) => ` (elemento ${element}, maggior numero di viti in fila)`,
        doubleNote: "taglio doppio: la stessa vite attraversa i tre elementi, 2 piani di taglio",
        steelNote: (sides: string) => `due unioni a taglio singolo, una per lato: ${sides} con legno centrale 2`,
        threeNote: "due unioni a taglio singolo, una per lato: elementi 2 e 3 con elemento 1",
        sidePanels: "pannello 1 e pannello 3",
        sidePlates: "piastra 1 e piastra 3",
        govPlane: "governante",
        kserNoRho:
          "ρ<sub>mean</sub> non disponibile (N.D. nel database per questa classe): inserire manualmente ρ<sub>mean</sub> per calcolare K<sub>ser</sub>.",
        kserLvlNote: (family: string) =>
          `<span class="report-formula">ETA-11/0030 — vite ${family} in legno LVL di conifera, 5≤d≤10mm, indipendente dall'angolo ε</span>`,
        kserPredrilled: "preforato",
        kserNotPredrilled: "non preforato",
        kserPanelRho: (rhok: string, rhoMean: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>pannello</sub>·ρ<sub>mean,2</sub>) = √(${rhok}×${rhoMean}) = ${rhoM} kg/m³ (ρ<sub>mean</sub> del pannello assunto pari a ρ<sub>k</sub>)`,
        kserMean2: (rhoM: string) => `ρ<sub>mean,2</sub> = ${rhoM} kg/m³`,
        kserTimberRho: (rho1: string, rho2: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>mean,1</sub>·ρ<sub>mean,2</sub>) = √(${rho1}×${rho2}) = ${rhoM} kg/m³`,
        kserSteelNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Prospetto 7.1, come per i chiodi ${predrilled} — ${rho}; ×2,0 per unioni acciaio-legno (EC5 §7.1(3))</span>`,
        kserTimberNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Prospetto 7.1, come per i chiodi ${predrilled} — ${rho}</span>`,
      },
      positioning: {
        title: "Posizionamento viti rispetto ai bordi",
        meta: "elemento 1 e elemento 2, quote in mm",
        metaThree: "elemento 1 (centrale) ed elementi 2 e 3, quote in mm",
        metaSteel3: "elementi 1 e 3 (lamiere) ed elemento 2 (legno centrale), quote in mm",
        metaPanel3: "elementi 1 e 3 (pannelli) ed elemento 2 (legno centrale), quote in mm",
        notAvailable: "Posizionamento viti non disponibile: calcolo non eseguito.",
        notAvailableSee: "Posizionamento viti non disponibile: calcolo non eseguito (vedi l'avviso a destra).",
        none: "Nessuna vite ammissibile da posizionare: cambia righe/colonne o i dati di input.",
        missingDims: "Completa i dati geometrici per vedere il posizionamento delle viti.",
        captionElements13Plates: "Elementi 1 e 3 (lamiere identiche)",
        captionElements13Panels: "Elementi 1 e 3 (pannelli identici)",
        captionElement1: "Elemento 1",
        suffixPlate: " (lamiera)",
        suffixPanel: " (pannello)",
        suffixCentral: " (centrale)",
        captionView1: " — vista nel proprio piano: estremità in basso (opposta a F), F verso l'alto",
        captionView2: " — vista nel proprio piano: estremità inferiore in basso",
        captionElements23: "Elementi 2 e 3 (identici)",
        captionElement2Timber: "Elemento 2 (legno centrale)",
        captionElement2: "Elemento 2",
        continuousNoEnd: " · elemento continuo: nessuna quota di estremità",
        axisElement1: "elemento 1",
        axisElements23: "elementi 2 e 3",
        axisElement2: "elemento 2",
        note: (p: {
          s1: string;
          s2: string;
          axis2: string;
          spread: boolean;
          s1Min: string;
          s2Min: string;
          rows: number;
          cols: number;
          staggered: boolean;
          nShow: number;
          nTotal: number;
          threeD: boolean;
          twoSides: boolean;
          steel3: boolean;
          panel1: boolean;
          stagger: boolean;
          d: string;
          dir: string;
        }) =>
          `Quote in mm, dal bordo dell'elemento all'asse della vite. Passi della griglia: s<sub>1</sub> = ${p.s1} mm (lungo l'asse dell'elemento 1), s<sub>2</sub> = ${p.s2} mm (lungo l'asse dell'${p.axis2})${p.spread ? ` — viti distribuite ai limiti dell'area ammissibile (passi minimi richiesti ${p.s1Min} / ${p.s2Min} mm)` : ""} · ${p.rows} × ${p.cols} viti${p.staggered ? ", sfalsate" : ", allineate (non sfalsate)"} · ${p.nShow} ammissibili${p.nShow < p.nTotal ? ` su ${p.nTotal}; le viti fuori dall'inviluppo sono in rosso e non vanno installate` : ""}${p.threeD ? " · elementi 2 e 3 identici: la stessa vite attraversa i tre elementi (un'unica griglia)" : ""}${p.twoSides ? ` · elementi 2 e 3 identici: le viti dell'elemento 3 sono la copia speculare di quelle dell'elemento 2 (${p.nShow} per lato, ${2 * p.nShow} in totale)${p.stagger ? `, sfalsate di 1·d = ${p.d} mm (direzione ${p.dir}) perché L−h supera t2 + t1/2: cerchi vuoti = viti dell'elemento 3 (non quotate)` : ""}` : ""}${p.steel3 ? ` · ${p.panel1 ? "pannelli 1 e 3 identici" : "lamiere 1 e 3 identiche"}: le viti del lato 3 sono la copia speculare di quelle del lato 1 (${p.nShow} per lato, ${2 * p.nShow} in totale)${p.stagger ? `, sfalsate di 1·d = ${p.d} mm (direzione ${p.dir}) perché L−h supera t1 + t2/2: cerchi vuoti = viti della lamiera 3 (non quotate)` : ""}` : ""}.`,
        tableNo: "N.",
        tableElements13: (plates: string) => `Elementi 1 e 3 (${plates})`,
        tableElement1: "Elemento 1",
        tableElements23: "Elementi 2 e 3",
        tableElement2: "Elemento 2",
        tableDistance: "distanza dal bordo [mm]",
        tableEnd: "estremità",
        tableLeft: "sinistro",
        tableRight: "destro",
        tableMinMaxTitle: "Distanza minima/massima dal bordo [mm]",
        tableMin: "minima",
        tableMax: "massima",
        tableFromEnd1: "Elemento 1 — dall'estremità",
        tableFromLeft1: "Elemento 1 — dal bordo sinistro",
        tableFromLeft2: (el2: string) => `${el2} — dal bordo sinistro`,
        tableFromBottom2: (el2: string) => `${el2} — dall'estremità inferiore`,
        tableMoreThan40:
          "Con più di 40 viti non si elencano le coordinate di ogni vite: valgono le quote d'ingombro in figura e i passi della griglia s<sub>1</sub>/s<sub>2</sub>.",
        platesWord: "lamiere",
        panelsWord: "pannelli",
      },
      report: {
        title: "Report di calcolo",
        note: "Anteprima del report PDF che viene stampato (dati, viste, risultati, note metodologiche).",
        versionLabel: "Versione del report",
        full: "Completa",
        fullHint: "(con note metodologiche)",
        compact: "Compatta",
        compactHint: "(senza note metodologiche)",
        show: "Visualizza report",
        hide: "Nascondi report",
        generating: "Genero il report…",
        generatePdf: "Genera report PDF",
        exportDxf: "Esporta DXF",
        viewer: {
          toolbar: "Controlli del visualizzatore del report",
          previousPage: "Pagina precedente",
          pageNumber: "Numero di pagina",
          nextPage: "Pagina successiva",
          zoomOut: "Riduci zoom",
          zoom: "Zoom",
          zoomIn: "Ingrandisci zoom",
          fitWidthOption: (pct: number) => `Adatta larghezza (${pct} %)`,
          fitPageOption: (pct: number) => `Adatta pagina (${pct} %)`,
          fitWidth: "Adatta alla larghezza",
          fitPage: "Adatta alla pagina",
          continuous: "Scorrimento continuo",
          singlePage: "Pagina singola",
          twoPages: "Due pagine affiancate",
          blackWhite: "Anteprima in bianco e nero",
          blackWhiteShort: "B/N",
          paginating: "Impagino il report…",
        },
        previewError: "Impossibile generare l'anteprima del report: riprova.",
      },
    },
  },

  envelopeDesignerModule: {
    resultCodes: {
      errors: {
        INVALID_REQUEST_BODY: "Corpo della richiesta non valido.",
        INVALID_REQUEST_UNKNOWN_ACTION: "Richiesta non valida: azione sconosciuta.",
        INVALID_REQUEST_NO_LAYERS: "Richiesta non valida: nessuno strato da calcolare.",
        INVALID_REQUEST_NO_MATERIALS: "Richiesta non valida: mancano i dati dei materiali.",
        INVALID_REQUEST_NO_MONTHLY_CLIMATE: "Richiesta non valida: manca il clima mensile (12 temperature e pressioni di vapore) - carichi un file EPW.",
        INVALID_REQUEST_OUTSIDE_SIDE: "Glaser mensile: la richiesta non indica quale sia il lato esterno.",
        LAYERS_TOO_MANY: (p: { max: number }) => `Richiesta non valida: al massimo ${p.max} strati.`,
        MONTHLY_OUTSIDE_TEMPERATURE_INVALID: "Dati non validi: una temperatura esterna media mensile è fuori dall'intervallo da -60 a 60 °C.",
        MONTHLY_OUTSIDE_VAPOUR_PRESSURE_INVALID: "Dati non validi: una pressione di vapore esterna media mensile non è compresa tra 0 e 10000 Pa.",
        MONTHLY_METHOD_UNKNOWN: "Dati non validi: metodo di calcolo sconosciuto.",
        MONTHLY_CONDENSATION_TOLERANCE_INVALID: "Dati non validi: la tolleranza di condensazione deve essere 0 (esatta) o 1e-9.",
        MONTHLY_SERVER_ERROR: "Glaser mensile: l'analisi non è riuscita sul server.",
        CALCULATION_SERVER_ERROR: "Il calcolo non è riuscito sul server.",
        HUMIDITY_CLASS_INVALID: "Dati non validi: la classe di umidità UNI EN ISO 13788 deve essere 1, 2, 3, 4 o 5.",
        INSIDE_TEMPERATURE_INVALID: "Dati non validi: la temperatura interna deve essere un numero compreso tra -10 e 50 °C.",
        INSIDE_RELATIVE_HUMIDITY_INVALID: "Dati non validi: l'umidità relativa interna deve essere un numero maggiore di 0 e al massimo 100 %.",
        EN15026_MONTHLY_VALUES_MISSING: "Dati non validi: il clima interno EN 15026 Annex D richiede i valori mensili calcolati dal file climatico (ricarichi il file EPW).",
        ASHRAE160_UNAVAILABLE: "Clima interno ASHRAE 160: non ancora disponibile - i valori del suo metodo semplificato non sono verificati.",
        INSIDE_MODEL_UNKNOWN: "Dati non validi: modello del clima interno sconosciuto.",
      },
      warnings: {} as Record<string, { title: string; detail: string }>,
    },
    inputErrors: {
      layer: (position: number, material: string) => `Strato ${position} (${material})`,
      stud: (layer: string, material: string) => `${layer}, montante ${material}`,
      bad: { nan: "non è un numero", infinite: "infinito", negative: (value: number) => `negativo (${value})`, zero: "0" },
      messages: {
        layerThickness: (subject: string, got: string) => `${subject}: lo spessore deve essere maggiore di 0 (valore: ${got}).`,
        layerLambda: (subject: string, got: string) => `${subject}: la conduttività termica λ deve essere maggiore di 0 (valore: ${got}).`,
        layerMu: (subject: string, got: string) => `${subject}: il fattore di resistenza al vapore μ deve essere maggiore di 0 (valore: ${got}).`,
        studWidth: (subject: string, got: string) => `${subject}: la larghezza deve essere maggiore di 0 (valore: ${got}).`,
        studSpacing: (subject: string, got: string) => `${subject}: l'interasse deve essere maggiore di 0 (valore: ${got}).`,
        studDepth: (subject: string, got: string) => `${subject}: la profondità deve essere maggiore di 0 (valore: ${got}).`,
        studDepthExceeds: (subject: string, _got: string, depthMM: number, thicknessMM: number) =>
          `${subject}: la profondità (${depthMM} mm) supera lo spessore del suo strato (${thicknessMM} mm).`,
        studLambda: (subject: string) => `${subject}: il materiale non ha una conduttività termica λ valida.`,
        studMu: (subject: string) => `${subject}: il materiale non ha un fattore di resistenza al vapore μ valido.`,
        rsi: (_subject: string, got: string) => `La resistenza superficiale interna Rsi deve essere maggiore di 0 (valore: ${got}).`,
        rse: (_subject: string, got: string) => `La resistenza superficiale esterna Rse deve essere maggiore di 0 (valore: ${got}).`,
        tInt: (_subject: string, got: string) => `La temperatura interna deve essere un numero (valore: ${got}).`,
        tExt: (_subject: string, got: string) => `La temperatura esterna deve essere un numero (valore: ${got}).`,
        rhInt: (_subject: string, got: string) => `L'umidità relativa interna deve essere compresa tra 0 e 100 % (valore: ${got}).`,
        rhExt: (_subject: string, got: string) => `L'umidità relativa esterna deve essere compresa tra 0 e 100 % (valore: ${got}).`,
      },
    },
    admin: {
      materials: "Materiali",
      examples: "Stratigrafie di esempio",
      manufacturer: "Produttore",
      variable: "variabile",
      deleteMaterialQuestion: "Vuoi davvero eliminare questo materiale? Un materiale usato da un calcolo o da un esempio non si può eliminare: nascondilo.",
      material: "materiale",
      subcategory: "Sottocategoria",
      category: "Categoria",
      categories: "Categorie",
      subcategories: "Sottocategorie",
      condCategories: "Categorie di condensa",
      rule: "Regola",
      condCategory: "Categoria di condensa (UNI EN ISO 13788)",
      hatch: "Retino",
      thicknessMode: "Modalità spessore",
      defaultThickness: "Spessore di riferimento",
      knownThicknesses: "Spessori disponibili (separati da virgola)",
      minThickness: "Spessore minimo",
      maxThickness: "Spessore massimo",
      sdCurveHint: "Solo membrane a Sd variabile: punti con UR come frazione 0-1 e Sd in metri.",
      sectionMembrane: "Membrana",
      sectionTestDatabase: "Database di prova (valori igrici del file)",
      sectionCurves: "Curve misurate",
      sectionProvenance: "Fonti dei dati",
      sdVariableMin: "Sd variabile, min [m]",
      sdVariableMax: "Sd variabile, max [m]",
      dryCupPerm: "Permeanza dry-cup [US perm]",
      curvesHint: "Ogni curva è un elenco JSON di punti, con le chiavi indicate nel suo campo (vuoto: nessuna curva).",
      moistureCurve: "Curva di assorbimento w(UR) [UR %, w kg/m³]",
      lambdaMoistureCurve: "λ(w) [w m³/m³, λ W/(m·K)]",
      muMoistureCurve: "μ(w) [w m³/m³, μ -]",
      sdHumidityCurve: "Curva Sd variabile con l'umidità Sd(UR) [UR 0-1, Sd m]",
      suctionCurve: "Curva di suzione [log10 p_C Pa, w kg/m³]",
      liquidConductivityCurve: "Conduttività liquida [w kg/m³, log10 K_l s]",
      datasheetUrl: "Scheda tecnica (URL)",
      productPageUrl: "Pagina del prodotto (URL)",
      datasheetChecked: "Scheda verificata il",
      formerlyNamed: "Nome precedente",
      sourceDatabase: "Database di origine",
      sourceLaboratory: "Laboratorio",
      sourceDate: "Data della misura",
      sourceFile: "File di origine",
      assumedFields: "Valori assunti (non presenti nella scheda)",
      derivedFields: "Valori ricavati (calcolati dalla scheda)",
      dataNote: "Nota sulla scheda",
      dataNoteLambda: "λ della scheda [W/(m·K)]",
      invalidCurve: (curve: string) => `${curve}: non è un elenco di punti valido.`,
      label: "Nome",
      element: "Elemento",
      structure: "Struttura",
      regions: "Regioni",
      layerCount: "Strati",
      default: "predefinito",
      deleteExampleQuestion: "Vuoi davvero eliminare questo esempio?",
      example: "esempio",
      description: "Descrizione",
      layersInsideOut: "Strati (interno → esterno)",
      addBridge: "Aggiungi montante/travetto",
      width: "larghezza",
      spacing: "interasse libero",
      fullDepth: "intero spessore",
      addLayer: "Aggiungi strato",
    },
    errors: {
      materialAlreadyExists: "Esiste già un materiale con lo stesso nome e produttore.",
      materialInUse: "Il materiale è usato da un calcolo o da un esempio: nascondilo invece di eliminarlo.",
      exampleNotFound: "Esempio non trovato.",
      invalidExample: "Stratigrafia di esempio non valida.",
      exampleAlreadyExists: "Esiste già un esempio con lo stesso codice.",
      invalidMaterial: "Materiale personalizzato non valido.",
      materialNotFound: "Uno strato usa un materiale non presente nel catalogo.",
      customMaterialNotFound: "Uno strato usa un materiale personalizzato inesistente.",
      invalidLayer: "Strato non valido.",
      invalidBridge: "Ponte termico non valido.",
      condCategoryNotFound: "Categoria di condensa sconosciuta.",
      categoryAlreadyExists: "Esiste già una categoria con lo stesso codice.",
      categoryInUseByMaterial: "Ci sono materiali di questa categoria: nascondila invece di eliminarla.",
      categoryInUseBySubcategory: "Ci sono sottocategorie di questa categoria: eliminale o spostale prima.",
      subcategoryAlreadyExists: "Esiste già una sottocategoria con lo stesso codice.",
      subcategoryInUseByMaterial: "Ci sono materiali di questa sottocategoria: nascondila invece di eliminarla, e spostala solo nella categoria dei suoi materiali.",
    },
    nav: {
      project: "Progetto",
      data: "Dati",
      dataTitle: "Dati: progetto, sito, elemento, analisi e modalità di calcolo",
      stratigraphy: "Stratigrafia",
      stratigraphyTitle: "Stratigrafia: strati e materiali dell'elemento",
      report: "Report",
      reportTitle: "Report: anteprima e stampa",
      general: "Generale",
      materialsDatabase: "Database materiali",
      materialsDatabaseTitle: "Database materiali: consultare, filtrare e aggiungere materiali",
      settings: "Impostazioni",
      settingsTitle: "Impostazioni: unità, normative, calcolo e scorciatoie da tastiera",
      tutorial: "Tutorial",
      tutorialTitle: "Tutorial - una dimostrazione delle funzioni principali",
      toggleSidebar: "Comprimi/espandi la barra laterale",
      analysisResults: "Analisi e risultati",
      analysisResultsTitle: "Analisi e risultati: l'analisi statica e quella quasi-dinamica",
      quasiDynamic: "Quasi-dinamica",
      quasiDynamicLockedTitle: "Analisi quasi-dinamica: non disponibile in questa beta",
      openQuasiDynamicTitle: "Aprire anche l'analisi quasi-dinamica?",
      openQuasiDynamicMessage: "Nella scheda Dati è stata selezionata l'analisi statica. Desidera procedere anche con l'analisi quasi-dinamica?",
      openQuasiDynamicConfirm: "Sì, la apra",
      reportLockedNote: "Disponibile dopo il calcolo dell'analisi statica per questa stratigrafia",
      reportLockedToast: "Il report: disponibile dopo il calcolo dell'analisi statica per questa stratigrafia.",
      completeDataFirst: "Completi prima la scheda Dati (progetto, elemento, analisi)",
      addLayerFirst: "Aggiunga prima almeno uno strato alla stratigrafia",
      openStratigraphyFirst: "Apra prima la Stratigrafia",
      statusTodo: "Da completare",
      statusOk: "Completo",
      statusWarn: "Soddisfatto, con un avviso",
      statusBad: "Almeno una verifica non soddisfatta",
      statusBusy: "Calcolo in corso…",
      undoPrevious: "Annulla l'azione precedente",
      showRecentActions: "Mostra le azioni recenti",
      redoNext: "Ripristina l'azione successiva",
      recentUndoHistory: "Cronologia recente delle modifiche",
      undoMultiple: "Annulla più azioni",
      noActions: "Nessuna azione",
      historyEmpty: "Le Sue modifiche recenti compariranno qui.",
      historyFoot: "Selezioni un'azione per tornare allo stato immediatamente precedente. Passi con il mouse sopra per i dettagli completi della modifica.",
    staticAnalysis: "Statica",
    staticAnalysisTitle: "Analisi statica: risultati (1D o 2D), Glaser mensile, impostazioni",
    quasiDynamicTitle: "Analisi quasi-dinamica: dati di input e risultati",
    },
    preferences: {
      preferences: "Preferenze",
      preferencesTitle: "Preferenze: unità e normative",
      units: "Unità di misura",
      unitsTitle: "SI: sistema metrico. Imperial: ogni valore convertito (diffusione del vapore come permeanza in US perm). UK: SI, tranne la diffusione del vapore, espressa come resistenza al vapore in MN·s/g. I calcoli si svolgono sempre in SI: cambia solo ciò che viene visualizzato e stampato.",
      ukTitle: "Le stesse unità SI di tutto il resto, tranne la diffusione del vapore, espressa come resistenza al vapore in MN·s/g, l'unità usata nella pratica della fisica tecnica nel Regno Unito",
      unitsHint: "Salvate con il calcolo; un nuovo calcolo parte dalle unità scelte per ultime (altrimenti SI).",
      standards: "Normative",
      standardEuTitle: "I menu mostrano le opzioni UNI / EN (EN 15026, UNI EN ISO 13788)",
      standardNaTitle: "I menu mostrano le opzioni ASHRAE (ASHRAE 160)",
      standardsHint: "I menu con alternative in base alla normativa (pioggia nell'analisi quasi-dinamica, clima interno dell'analisi Glaser mensile) mostrano solo le opzioni della normativa scelta.",
    },
    settings: {
      changesPending: "Modifiche non ancora applicate - Fine le applica",
      changesApplyOnDone: "Le modifiche vengono applicate premendo Fine",
      done: "Fine",
      title: "Impostazioni",
      keyboardShortcuts: "Scorciatoie da tastiera",
      solver2d: "Solutore 2D",
      climate: "Clima",
      quasiDynamic: "Quasi-dinamica",
      quasiDynamicLockedTitle: "Analisi quasi-dinamica: non disponibile in questa beta",
      quasiDynamicLockedLabel: "Quasi-dinamica: non disponibile in questa beta",
      about: "Informazioni",
      shortcutsHint: "Faccia clic su un campo, poi prema la combinazione di tasti desiderata.",
      resetToDefaults: "Ripristina predefiniti",
      solver2dHeading: "Metodo igrotermico stazionario 2D — mesh, iterazioni e tolleranze",
      solver2dIntro: "Regola la risoluzione accoppiata a volumi finiti di temperatura e pressione di vapore della modalità mesh 2D. Non ha effetto sul metodo di Glaser 1D.",
      solvingMethod: "Metodo di risoluzione",
      solverCg: "Gradiente coniugato (precondizionato Jacobi)",
      solverCgHint: "L'analisi statica 2D usa sempre il gradiente coniugato precondizionato Jacobi: converge anche con barriere al vapore, dove Gauss-Seidel e il V-cycle si fermano prima che il bilancio energetico sia soddisfatto.",
      meshRows: "Mesh (righe nello spessore)",
      meshHint: "Auto (prima posizione) ricalcola con 20→40→70→100→160→250→300 righe e si ferma quando il flusso termico varia di meno dello 0,1% tra due mesh e il solutore stesso è giunto a convergenza (veda la scheda Diagnostica). Sposti il cursore a destra per fissare la mesh: 20, 40, 70, 100, 160, 250 o 300 righe; più è fine, più è precisa e lenta.",
      maxIterationsBefore: "Iterazioni max (T e p",
      maxIterationsAfter: ", ciclo accoppiato)",
      epsU: "εU — tolleranza su valore U / trasmittanza [-]",
      epsRH: "εRH — tolleranza su pressione di vapore / RH [-]",
      toleranceHintBefore: "La tolleranza si applica solo alle due grandezze che contano davvero per il risultato progettuale: εU è la variazione relativa del valore U/flusso termico tra due cicli accoppiati (poiché il campo di temperatura deriva dalla stessa risoluzione termica, regola anche quanto strettamente si stabilizzano le temperature); εRH è la variazione dell'umidità relativa tra due cicli accoppiati (RH=p",
      toleranceHintAfter: "(T), quindi resta stabile solo quando si sono assestati sia il campo della pressione di vapore sia la temperatura da cui dipende). Una mesh più grossolana, tolleranze più larghe o meno iterazioni convergono più rapidamente, ma risolvono con minore precisione il campo attorno a un elemento dell'orditura: le schede di stato della mesh sopra il pulsante Esempi indicano se le impostazioni attuali raggiungono davvero la convergenza. Gauss-Seidel e il gradiente coniugato risolvono direttamente la mesh fine; il multigrid V-cycle usa inoltre una gerarchia di mesh più grossolane, aggregate algebricamente, per accelerare la stessa risoluzione: tutti e tre convergono allo stesso campo.",
      aboutNotice: "© 2026 Rotho Blaas Srl · Envelope Designer. Beta - versione non ufficiale, ancora in fase di sviluppo.",
      methodology: "Metodologia e limiti",
      aboutBuild: "Informazioni su questa versione",
      climateSources: "Fonti climatiche",
      climateSourcesHint: "Scelga, grandezza per grandezza, da dove proviene il clima (PVGIS, il Suo file EPW, stime), veda quali dati offre ciascun fornitore e scarichi l'anno combinato in formato .epw.",
      epwAdvanced: ".epw avanzato…",
      epwWhereToGet: "Dove trovare i file climatici EPW",
      epwHint: "Scelga la stazione più vicina al sito, con quota ed esposizione simili (EN 15026 §5.2.1): un aeroporto costiero può differire molto da un sito nell'entroterra o in montagna. Usi un anno tipo per il caso di progetto e l'opzione −2 K del pannello per le criticità invernali. Il file deve contenere un anno completo di valori orari (8760 righe); il pannello lo verifica e segnala le lacune. Un file caricato viene inviato con ogni calcolo e non viene memorizzato (il registro delle richieste conserva solo un'impronta).",
      epwOneBuilding: "anni tipo gratuiti (TMYx) per migliaia di stazioni meteorologiche in tutto il mondo, un file ZIP per stazione con l'EPW all'interno. Rispetti le condizioni d'uso dell'archivio; non ridistribuisca i file.",
      epwMap: "una mappa delle stesse stazioni: faccia clic su quella più vicina al sito per scaricarla.",
      epwPvgis: "lo strumento \"Typical meteorological year\" esporta in formato EPW lo stesso anno tipo che la fonte PVGIS scarica qui per il punto della mappa.",
      epwEnergyPlus: "la pagina di EnergyPlus dedicata ai file climatici, che rimanda all'archivio precedente.",
      epwNsrdb: "anni tipo da dati satellitari per il Nord America (registrazione gratuita); scaricati in CSV, da convertire in EPW.",
      epwLicensed: "set di dati su licenza, con controllo di qualità (Meteonorm: meteonorm.com), quando un progetto li richiede.",
      qdHeading: "Analisi quasi-dinamica — calcolo",
      qdIntro: "Si applica al prossimo calcolo del pannello Quasi-dinamica.",
      timeStep: "Passo temporale",
      timeStepAuto: "Automatico (1D: 5 min, 2D: 1 h)",
      timeStepHint: "Integrazione temporale implicita: ogni passo è stabile, ma i passi superiori a 1 h usano il clima mediato sul passo, per cui il ciclo giornaliero (raffrescamento notturno, sole di mezzogiorno) viene attenuato. La EN 15026 lavora con dati climatici orari: per i risultati di progetto mantenga 1 h o meno. Misurato su una parete a montanti 2x6 (2D, 20 righe, un anno): il picco di condensa è stato di 94 g/m² con 1 h, 100 con 6 h, 112 con 24 h.",
      solutionMethod: "Metodo di risoluzione (mesh 2D)",
      qdMethods: {
        pcg: "Gradiente coniugato, precondizionato per linee (PCG)",
        "pcg-jacobi": "Gradiente coniugato, precondizionato Jacobi",
        gs: "Gauss-Seidel",
        jacobi: "Jacobi",
        thomas: "Diretto tridiagonale (Thomas)",
      },
      defaultDash: " - predefinito",
      solutionMethodHint: "Solutore dei sistemi termico e igrometrico di ogni passo temporale, tutti arrestati sullo stesso residuo relativo. Il PCG precondizionato per linee risolve esattamente ogni colonna della griglia a ogni iterazione (membrane sottili e resistenze superficiali accoppiano le celle nello spessore molto più che lateralmente) ed è il più veloce; il PCG precondizionato Jacobi è l'algoritmo di riferimento del rapporto di validazione 05. Gauss-Seidel e Jacobi richiedono molte più iterazioni (su una parete 2x6 un anno ha richiesto più di 12 minuti, oltre i 300 s del server): li usi per verificare simulazioni brevi. La sezione Convergenza mostra ogni ora. L'analisi 1D usa sempre il solutore diretto tridiagonale (Thomas).",
      tolerance: "Tolleranza",
      defaultParen: " (predefinito)",
      maxIterations: "Iterazioni max.",
      toleranceQdHint: "Una risoluzione è giunta a convergenza quando ‖b − Ax‖ / ‖b‖ è inferiore alla tolleranza. Con 1e−10 le griglie 2D con righe sottili di membrana lasciavano residui del bilancio di massa di circa 1e−5; 1e−12 li mantiene sotto 1e−6. Iterazioni max.: per risoluzione.",
      gridRows: "Righe della griglia 2D",
      gridRowsOption: (rows: number) => `${rows} righe`,
      gridRowsHint: "Solo in modalità mesh 2D. Griglie più fini risolvono meglio le interfacce ma richiedono più tempo; il server interrompe un calcolo dopo 300 s. Misurato su una parete a montanti 2x6, un anno simulato con il PCG precondizionato per linee: 20 righe 12 s, 40 righe 18 s, 70 righe circa 4 min (un solo anno). Griglie più fini non terminerebbero in tempo.",
      restoreDefaults: "Ripristina predefiniti",
      lockedNote: "Non disponibile in questa beta",
    notAppliedTitle: "Impostazioni non applicate",
    notAppliedMessage: "Ha modificato impostazioni non ancora applicate: nessuna scheda viene aggiornata finché non preme Fine. Come desidera procedere?",
    keepEditing: "Continua a modificare",
    discardChanges: "Annulla le modifiche",
    applyDone: "Applica (Fine)",
    meshAuto: "Auto",
    meshRowsReadout: (rows: number) => `${rows} righe`,
    },
    confirm: {
      cancel: "Annulla",
    },
    data: {
      stepLabel: (n: number, title: string) => `Passo ${n}: ${title}`,
      stepSite: "Sito",
      stepElement: "Elemento",
      stepAnalysis: "Analisi",
      stepCalculation: "Calcolo",
      useThisSite: "Usa questo sito",
      elements: {
        wall: "Parete",
        roof: "Copertura",
        ground: "Contro terra",
      },
      analyses: {
        static: "Analisi statica",
        dynamic: "Analisi quasi-dinamica",
      },
      wallDesc: "Parete esterna",
      roofDesc: "Copertura o ultimo solaio",
      groundDesc: "Solaio a contatto con il terreno",
      groundDynamicNote: "L'analisi quasi-dinamica non modella il contatto con il terreno: la superficie esterna è esposta all'aria esterna.",
      staticOption: "Statica",
      staticDesc: "Condizione di progetto e Glaser mensile",
      quasiDynamic: "Quasi-dinamica",
      quasiDynamicDesc: "Ora per ora su più anni",
      simplified: "Semplificato",
      simplifiedDesc: "1D attraverso gli strati",
      meshMode: "Modalità mesh",
      meshModeDesc: "Mesh 2D, montanti risolti",
      meshNoteLabel: "Modalità mesh 2D: metodi numerici (nota)",
      numericalMethods: "Metodi numerici.",
      meshNote: "La modalità mesh 2D utilizza metodi numerici: la verifica della loro validità e dell'idoneità di ogni materiale e prodotto selezionato nella stratigrafia, per il caso specifico, è responsabilità esclusiva del progettista incaricato.",
      missing: {
        site: "sito",
        element: "elemento",
        analysis: "analisi",
      },
      stillToChoose: (list: string) => `Ancora da scegliere: ${list}.`,
      continueToStratigraphy: "Prosegua con la stratigrafia →",
      continueToAnalysis: {
        static: "Prosegua con l'analisi statica →",
        dynamic: "Prosegua con l'analisi quasi-dinamica →",
      },
      backToData: "← Dati",
      howToStart: "Come desidera iniziare?",
      fromExample: "Da un esempio",
      fromExampleDesc: (n: number, element: string) => `Scelga una delle ${n} stratigrafie (${element}) e la adatti`,
      fromScratch: "Da zero",
      fromScratchDesc: "Una stratigrafia vuota: aggiunga gli strati dalla libreria dei materiali",
      back: "← Indietro",
    },
    examples: {
      close: "Chiudi",
      examplesTitle: (element: string) => `Esempi: ${element}`,
      elementName: {
        wall: "parete",
        roof: "copertura",
        ground: "contro terra",
      },
      exampleLoaded: (name: string) => `Esempio caricato: ${name}`,
      changeExample: "Cambia esempio",
      changeExampleTitle: (element: string) => `Sostituisce gli strati con un altro esempio (${element}); Annulla li ripristina`,
      structures: {
        timber: "Telaio in legno",
        mass_timber: "Legno massiccio",
        masonry: "Muratura",
        concrete: "Calcestruzzo",
        steel: "Acciaio",
        other: "Altro",
      },
      types: {
        wall: "Parete",
        roof: "Copertura / solaio",
        ground: "Solaio contro terra",
      },
      searchPlaceholder: "Cerca esempi...",
      exampleSingular: "esempio",
      examplePlural: "esempi",
      filtersLabel: "Filtri degli esempi",
      region: "Regione",
      yourLocation: (country: string) => `la Sua posizione: ${country}`,
      type: "Tipo",
      structure: "Struttura",
      resetFilters: "Azzera filtri",
      noMatch: "Nessuna stratigrafia corrisponde alla ricerca o ai filtri selezionati.",
      noneInRegion: "Nessuna stratigrafia di questo tipo nella regione selezionata.",
      backToTypes: "Torna ai tipi",
    },
    climate: {
      loadError: "Non è stato possibile caricare i dati climatici.",
      suggestedCorrections: "Correzioni suggerite",
      applyAndReload: "Applica e ricarica",
      correctionsHint: "Nulla viene corretto se non lo sceglie Lei; le correzioni applicate sono elencate con i risultati.",
      correctionsChosen: (list: string) => `Correzioni scelte: ${list} (nulla da correggere per questa fonte).`,
      undo: "Annulla",
      cardTitle: "Clima del sito",
      autoEpwTitle: "Il clima del punto sulla mappa, generato automaticamente da un fornitore (PVGIS, ERA5 tramite Open-Meteo, stazione TMYx).",
      autoEpw: "EPW automatico",
      importEpwTitle: "Carichi un file EPW (TMYx, NSRDB, Meteonorm o qualsiasi altra fonte).",
      importEpw: "Importa EPW",
      yearType: "Tipo di anno",
      yearTypical: "Anno tipo, ripetuto",
      yearMinus2: "Anno tipo −2 K (problemi invernali, EN 15026 §5.2.1 c)",
      yearPlus2: "Anno tipo +2 K (problemi estivi)",
      provider: "Fornitore dei dati climatici",
      providerPvgis: "Anno tipo PVGIS (JRC UE) - senza pioggia",
      providerOpenMeteo: "ERA5 tramite Open-Meteo - pioggia, temperature del suolo",
      providerTmyx: "Stazione meteorologica TMYx (climate.onebuilding.org)",
      era5Year: "Anno ERA5",
      era5YearTitle: "Il primo anno tipo di un sito scarica 10 anni di dati orari (circa 10-20 s); poi viene conservato per quella cella della griglia.",
      era5Typical: "Anno tipo (EN ISO 15927-4, ultimi 10 anni)",
      actualYear: (year: number) => `Anno reale ${year}`,
      tmyxStation: "Stazione TMYx",
      tmyxStationTitle: "Distanza e differenza di quota rispetto al sito. La corrispondenza migliore conta 100 m di dislivello come 10 km; la temperatura è corretta per la differenza di quota.",
      bestMatch: (station: string) => `Corrispondenza migliore: ${station}`,
      lat: "Lat",
      latitude: "Latitudine [°]",
      lon: "Lon",
      longitude: "Longitudine [°]",
      elevTitle: "Quota del sito [m]. Vuoto: la quota dei dati climatici (in grigio). Inserisca un valore per correggere la temperatura in base al sito (0,65 K ogni 100 m).",
      elev: "Quota",
      elevAuto: "auto",
      siteElevation: "Quota del sito [m]",
      viewClimateData: "Visualizza i dati climatici",
      viewClimateDataLoading: "Visualizza i dati climatici (caricamento)",
      climateDataLoading: "Caricamento dei dati climatici…",
      loadingClimateData: "Caricamento dati climatici",
      climateData: "Dati climatici",
      backToMap: "Torna alla mappa",
      map: "Mappa",
      epwFile: "File EPW",
      fileUnreadable: (name: string) => `Non è stato possibile leggere il file ${name}.`,
      weatherStationHint: "Una stazione meteorologica vicina al sito è la fonte più affidabile (EN 15026 §5.2.1).",
      elevationFromEpw: (elevation: number) => `${elevation} (dall'EPW)`,
      fromEpw: "dall'EPW",
      epwElevationTitle: "Vuoto: la quota dell'EPW. Inserisca un valore per correggere la temperatura in base al sito (0,65 K ogni 100 m).",
      notEpwFile: (name: string) => `${name} non è un file EPW: scelga un file con estensione .epw.`,
      readingClimate: "lettura del clima…",
      loaded: "caricato",
      replace: "Sostituisci…",
      remove: "Rimuovi",
      dropEpwHere: "Trascini qui un file EPW",
      epwSources: "TMYx, NSRDB, Meteonorm o qualsiasi altra fonte",
      chooseEpw: "Scegli file EPW…",
      noWindData: "Nessun dato sul vento in questo file climatico.",
      loadingTheClimateData: "Caricamento dei dati climatici…",
      insideBasis: "UNI EN ISO 13788 classe 3",
      ashraeNotAvailable: ", ASHRAE 160 non ancora disponibile",
      heatingDesignBasis: "temperatura di progetto invernale 99,6 %",
      coldestHour: (day: number, month: string, time: string) => `ora più fredda, ${day} ${month} ${time}`,
      aboveSaturation: (rh: string) => ` Tale pressione di vapore supera la saturazione a Te (UR ${rh} %): viene proposto 100 %.`,
      climateOfSite: "il clima del sito",
      suggestionTitle: (method: string, source: string, notes: string) => `${method}. Clima: ${source}.${notes} I campi restano modificabili.`,
      fromTheClimate: (basis: string) => `Dal clima (${basis}):`,
      inUse: "in uso",
      use: "Usa",
      searchFailed: "La ricerca non è riuscita.",
      noPlaceFound: "Nessun luogo trovato.",
      searchNotSent: "Non è stato possibile inviare la ricerca.",
      mapLabel: "Mappa: faccia clic per scegliere il sito",
      searchPlaceholder: "Cerchi una città, una via o un luogo",
      searchLabel: "Cerca un luogo",
      searching: "Ricerca in corso",
      usZonesTitle: "Zone climatiche IECC degli Stati Uniti (PNNL Building America Solution Center)",
      usZones: "Zone climatiche USA",
      addToFavourites: "☆ Aggiungi ai preferiti",
      favouritesFull: "Preferiti pieni (10): ne rimuova prima uno.",
      favouriteName: "Nome del preferito",
      cancel: "Annulla",
      add: "Aggiungi",
      renameFavourite: (name: string) => `Nuovo nome di ${name}`,
      favouriteHint: "trascini per riordinare, doppio clic per rinominare",
      removeFavourite: (name: string) => `Rimuovi ${name} dai preferiti`,
      morePlaces: (n: number) => `Altri ${n} luoghi`,
      more: (n: number) => `Altri ${n}`,
    monthsShort: ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"],
    chartLabel: "Anno di riferimento: media giornaliera e intervallo giornaliero di ogni grandezza",
    quantitiesLabel: "Grandezze mostrate (clic per nascondere o mostrare)",
    allHidden: "Tutte le grandezze sono nascoste.",
    legendTitle: (name: string, hidden: boolean) => `${name} - clic per ${hidden ? "mostrare" : "nascondere"}`,
    series: {
      outsideTemperature: "Temperatura dell'aria esterna",
      insideTemperature: "Temperatura interna",
      outsideRh: "Umidità relativa esterna",
      insideRh: "Umidità relativa interna",
      solar: "Radiazione solare globale sul piano orizzontale",
      wind: "Velocità del vento a 10 m",
      rain: "Pioggia sul piano orizzontale",
    },
    windRoseLabel: "Rosa dei venti: quota di ore per direzione e velocità",
    windClassesLabel: "Classi di velocità del vento (clic per nascondere o mostrare)",
    windSpeed: "velocità del vento",
    windFrom: (direction: string, percent: string) => `Da ${direction}: ${percent} % delle ore`,
    windMean: (value: string, unit: string) => `media ${value} ${unit}`,
    windMax: (value: string, unit: string) => `max ${value} ${unit}`,
    },
    stratigraphy: {
      flipInsideOutsideTitle: "Inverti interno/esterno - scambia quale lato è Interno/Esterno (e Ti/RHi/Rsi con Te/RHe/Rse) senza riordinare gli strati",
      resetZoomTitle: "Ripristina lo zoom (scorra sul disegno per ingrandire/ridurre, trascini per spostarsi mentre è ingrandito)",
      clearLayersTitle: "Elimina tutti gli strati (chiede conferma; Annulla li ripristina)",
      condensationToggleTitle: "Mostra/nascondi la condensa interstiziale sul disegno - strati umidi evidenziati in blu, con indicatori a goccia nei punti di condensazione",
      fluxVectorsTitle: "Mostra i vettori del flusso termico sulla sezione (solo modalità igrotermica 2D) - la lunghezza della freccia segue l'intensità locale del flusso, la direzione il gradiente di temperatura (perpendicolare alle isoterme)",
      overlayTemperatureTitle: "Sovrapponi il profilo di temperatura al disegno (trascini la maniglia per impostare quanta parte della larghezza copre)",
      overlayHumidityTitle: "Sovrapponi il profilo di umidità (RH) al disegno (trascini la maniglia per impostare quanta parte della larghezza copre)",
      meshGridTitle: "Mostra le singole celle della mesh di calcolo igrotermico 2D, con o senza sovrapposizione di temperatura/umidità (solo modalità mesh 2D, estensione impostata con la stessa maniglia)",
      thickness: "Spessore",
      editMaterial: "Modifica materiale…",
      changeMaterial: "Cambia materiale…",
      removeBridge: "Rimuovi ponte termico",
      resizeHandleTitle: "Trascini per ridimensionare questo riquadro - doppio clic per ripristinare",
      layerTable: "Tabella strati",
      layerTableHide: "nascondi",
      layerTableShow: "mostra le proprietà calcolate di ogni strato",
      layerTableHint: "Proprietà termiche calcolate per la stratigrafia corrente, dall'interno all'esterno. Trascini la maniglia di una riga per riordinare, faccia clic sul nome di un materiale (o sulla sua icona a matita) per visualizzarlo/modificarlo - oppure usi la sezione grafica sopra per altre azioni (clic destro per inserire/duplicare/eliminare/ponti termici).",
      layerTable2DNote: "Modalità igrotermica 2D: i valori seguenti sono campionati dal campo 2D a convergenza lungo la colonna a metà campata (senza elemento dell'orditura), non sono un vero valore 1D per strato - per il campo completo veda il pannello Igrotermico 2D della pagina Risultati.",
      colMaterial: "Materiale",
      colTempMin: "Temp min",
      colTempMinTitle: "Temperatura sulla faccia più fredda di questo strato, nella condizione di progetto",
      colTempMax: "Temp max",
      colTempMaxTitle: "Temperatura sulla faccia più calda di questo strato, nella condizione di progetto",
      colSdTitle: "Spessore d'aria equivalente alla diffusione del vapore (μ × spessore)",
      colCondensate: "Condensa",
      colCondensateTitle: "Tasso di condensa interstiziale, se presente, sulla faccia interna di questo strato",
      colWeight: "Peso",
      colWeightTitle: "Massa superficiale per unità di area",
      colHeatCapacity: "Capacità termica",
      colHeatCapacityTitle: "Capacità termica specifica",
      makeSideInside: "Faccia clic per rendere interno questo lato",
      makeSideOutside: "Faccia clic per rendere esterno questo lato",
      sideSwitched: (top: boolean, outside: boolean) => `Lato ${top ? "superiore" : "inferiore"}: ${outside ? "ESTERNO" : "INTERNO"}`,
      differsFromLibrary: "Diverso dal materiale della libreria",
      variable: "variabile",
      thicknessField: "spessore",
      checkPositive: (fields: string) => `Verifichi ${fields}: è necessario un numero positivo.`,
      stud: "Montante",
      material: "Materiale",
      thermalHygrometric: "Termico e igrometrico",
      acoustic: "Acustico",
      permeance: "Permeanza",
      membraneLocked: "Membrana Rothoblaas: valori da scheda tecnica, non modificabili.",
      layerValuesHint: "Valori solo di questo strato, applicati con Invio o all'uscita da un campo.",
      studValuesHint: "Valori solo di questo montante, applicati con Invio o all'uscita da un campo.",
      checkedOn: (date: string) => `Verificata il ${date}`,
      dataSheet: "Scheda tecnica",
      resetToLibrary: "Ripristina i valori della libreria",
    outside: "ESTERNO",
    inside: "INTERNO",
    noLayersDrawing: "Nessuno strato - aggiunga uno strato o carichi un esempio.",
    noLayersTable: "Nessuno strato - trascini un materiale dalla libreria sulla sezione grafica qui sopra.",
    moveUp: "Sposta in alto (verso l'esterno)",
    moveDown: "Sposta in basso (verso l'interno)",
    excludedClickToInclude: "Escluso dal calcolo - faccia clic per includerlo",
    includedClickToExclude: "Incluso nel calcolo - faccia clic per escluderlo",
    exteriorResistance: "Resistenza superficiale esterna (Rse)",
    interiorResistance: "Resistenza superficiale interna (Rsi)",
    dragToReorder: "Trascini per riordinare",
    viewEditMaterial: "Visualizza/modifica materiale",
    customMaterialRow: "Questo strato usa un materiale personalizzato/modificato, non un riferimento standard della libreria",
    supersaturated: "UR>100%",
    editStud: "Modifica montante/travetto",
    removeStud: "Rimuovi montante/travetto",
    studSummary: (name: string, width: string, spacing: string, depth: string, unit: string) => `${name} · ${width}×${spacing}${unit}, profondità ${depth}${unit}`,
    wholeComponent: "Intero componente",
    wholeComponentMidBay: "Intero componente (colonna a metà campata)",
    sdName: {
      sdValue: "valore sd",
      permeance: "permeanza",
      vapourResistance: "resistenza al vapore",
    },
    insertInterior: "↑ Inserisci strato (lato interno)",
    insertExterior: "↓ Inserisci strato (lato esterno)",
    placeStuds: "Inserisci montanti o travetti…",
    removeStuds: "Rimuovi montanti o travetti",
    duplicateLayer: "Duplica strato",
    disableLayer: "Disattiva strato (escludi dal calcolo)",
    enableLayer: "Attiva strato",
    deleteLayer: "Elimina strato",
    changeMaterialTitle: "Cambia materiale",
    clearAllTitle: "Eliminare tutti gli strati?",
    clearAllMessage: (n: number) => `${n === 1 ? "L'unico strato" : `Tutti i ${n} strati`} di questa stratigrafia ${n === 1 ? "verrà eliminato" : "verranno eliminati"}. Può ripristinarli con Annulla (Ctrl+Z).`,
    clearAllConfirm: "Elimina tutti",
    ashrae160Note: "ASHRAE 160 non ancora disponibile (i suoi valori sono in verifica): si usa il modello UNI / EN",
    },
    materialLibrary: {
      title: "Libreria materiali",
      materials: "Materiali",
      collapseExpand: "Comprimi/espandi la libreria materiali",
      openLibrary: "Apri la libreria materiali",
      searchPlaceholder: "Filtra materiali...",
      materialSingular: "materiale",
      materialPlural: "materiali",
      noMatches: "Nessun materiale corrispondente.",
      backTo: (name: string) => `Torna a ${name}`,
      backToCategories: "Torna alle categorie",
      dragToInsert: "Trascini nella tabella strati per inserire",
      dragHint: "Trascini un materiale sulla sezione grafica per inserirlo come nuovo strato (Esc annulla).",
      chooseCategoryHint: "Scelga una categoria, poi trascini un materiale sulla sezione grafica per inserirlo come nuovo strato.",
      generic: "Generico",
      filtersLabel: "Filtri materiali",
      min: "min",
      max: "max",
      minLambda: "Conduttività termica minima",
      maxLambda: "Conduttività termica massima",
      membranes: "Membrane",
      vapourDiffusionAs: "Diffusione del vapore come",
      minimumOf: (name: string) => `${name}: minimo`,
      maximumOf: (name: string) => `${name}: massimo`,
      permeance: "Permeanza",
      vapourResistance: "Resistenza al vapore",
      manufacturer: "Produttore",
      resetFilters: "Azzera i filtri",
      thickness: "Spessore",
      variable: "variabile",
      subcategories: {
        custom: "Personalizzati",
      },
    },
    shell: {
      calculating: "Calcolo in corso…",
      cancelCalculation: "Annulla il calcolo",
    },
    shortcuts: {
      undo: "Annulla",
      redo: "Ripristina",
      flip: "Inverti interno/esterno",
      toggleMode: "Cambia la modalità di calcolo",
      changeMaterial: "Cambia il materiale dello strato selezionato",
      toggleLibrary: "Apri/chiudi la libreria dei materiali",
      togglePanels: "Comprimi/espandi la barra laterale e la barra dei risultati",
      pressKeys: "Premere i tasti…",
      none: "(nessuna)",
    },
    history: {
      saved: (time: string) => `Salvato alle ${time}`,
      undoTitle: (label: string) => `Annulla: ${label}`,
      undoLabel: (label: string) => `Annulla ${label}`,
      nothingToUndo: "Nulla da annullare",
      redoTitle: (label: string) => `Ripristina: ${label}`,
      redoLabel: (label: string) => `Ripristina ${label}`,
      nothingToRedo: "Nulla da ripristinare",
      menuCount: (shown: number, available: number) => `${shown} di ${available}`,
      steps: (steps: number) => `${steps} ${steps === 1 ? "passo" : "passi"}`,
      actionTitle: (detail: string, steps: number) =>
        `${detail} Faccia clic per annullare ${steps === 1 ? "1 azione" : `${steps} azioni`}.`,
      initialState: "Stato iniziale",
      initialStateDetail: "Stato caricato all'avvio di questa sessione.",
      sessionStart: "Inizio sessione",
      fieldLabels: {
        thickness: "spessore",
        materialId: "materiale",
        enabled: "stato di calcolo",
        widthMM: "larghezza",
        spacingMM: "interasse",
        depthMM: "profondità",
        mode: "modalità di calcolo",
        stratFlipped: "orientamento interno/esterno",
        Rsi: "resistenza superficiale interna",
        Rse: "resistenza superficiale esterna",
        patm: "pressione atmosferica",
        meshRows: "righe della mesh semplificata",
        meshRowsHygro2D: "righe della mesh 2D",
        maxIterHygro2D: "limite di iterazioni dell'accoppiamento",
        epsU2D: "tolleranza sulla trasmittanza",
        epsRH2D: "tolleranza su pressione di vapore / UR",
        solverMethod2D: "solutore 2D",
        autoMesh: "mesh automatica",
        meshTolerance: "precisione della mesh",
        Ti: "temperatura interna",
        Te: "temperatura esterna",
        RHi: "umidità relativa interna",
        RHe: "umidità relativa esterna",
        name: "nome",
        notes: "note",
      } as Record<string, string>,
      unknownMaterial: "Materiale sconosciuto",
      layerName: (position: number, material: string) => `strato ${position} (${material})`,
      enabled: "attivo",
      disabled: "disattivato",
      notSet: "non impostato",
      scopeChanged: (scope: string) => `${scope}: modificato.`,
      scopeDiff: (scope: string, changes: string) => `${scope}: ${changes}.`,
      layerAdded: "Strato aggiunto",
      layerAddedDetail: (layer: string, position: number, thickness: string) =>
        `Aggiunto ${layer} in posizione ${position}, spessore ${thickness} mm.`,
      layerDeleted: "Strato eliminato",
      layerDeletedDetail: (layer: string, position: number, thickness: string) =>
        `Eliminato ${layer} dalla posizione ${position}, spessore ${thickness} mm.`,
      stratigraphyReplaced: "Stratigrafia sostituita",
      stratigraphyReplacedDetail: (from: number, to: number, added: number, removed: number) =>
        `Numero di strati passato da ${from} a ${to}; ${added} aggiunti e ${removed} rimossi.`,
      layersReordered: "Strati riordinati",
      layersReorderedDetail: (layer: string, from: number, to: number) => `Spostato ${layer} dalla posizione ${from} alla ${to}.`,
      bridgeAdded: "Ponte termico aggiunto",
      bridgeAddedDetail: (material: string, layer: string, width: string, spacing: string, depth: string) =>
        `Aggiunto elemento in ${material} a ${layer}; larghezza ${width} mm, interasse ${spacing} mm, profondità ${depth} mm.`,
      bridgeDeleted: "Ponte termico eliminato",
      bridgeDeletedDetail: (material: string, layer: string) => `Eliminato elemento in ${material} da ${layer}.`,
      bridgeMoved: "Ponte termico spostato",
      bridgeMovedDetail: (material: string, from: string, to: string) => `Spostato elemento in ${material} da ${from} a ${to}.`,
      bridgeModified: "Ponte termico modificato",
      bridgeScope: (material: string, layer: string) => `Elemento in ${material} in ${layer}`,
      layerMaterialChanged: "Materiale dello strato cambiato",
      layerMaterialChangedDetail: (position: number, from: string, to: string) =>
        `Materiale dello strato ${position} cambiato da ${from} a ${to}.`,
      layerThicknessChanged: "Spessore dello strato cambiato",
      layerThicknessChangedDetail: (layer: string, from: string, to: string) =>
        `Spessore di ${layer} cambiato da ${from} mm a ${to} mm.`,
      layerDisabled: "Strato disattivato",
      layerDisabledDetail: (layer: string) => `Escluso ${layer} dai calcoli.`,
      layerEnabled: "Strato attivato",
      layerEnabledDetail: (layer: string) => `Incluso ${layer} nei calcoli.`,
      layerModified: "Strato modificato",
      flipped: "Interno/esterno invertiti",
      flippedDetail: "Invertito l'orientamento interno/esterno visualizzato della stratigrafia.",
      modeChanged: "Modalità di calcolo cambiata",
      modeChangedDetail: (from: string, to: string) => `Modalità di calcolo: ${from} → ${to}.`,
      projectDetailsModified: "Dati del progetto modificati",
      projectDetailsScope: "Dati del progetto",
      customMaterialAdded: "Materiale personalizzato aggiunto",
      customMaterialAddedDetail: (name: string) => `Aggiunto il materiale personalizzato “${name}” alla libreria dei materiali.`,
      customMaterialDeleted: "Materiale personalizzato eliminato",
      customMaterialDeletedDetail: (name: string) => `Eliminato il materiale personalizzato “${name}” dalla libreria dei materiali.`,
      customMaterialModified: "Materiale personalizzato modificato",
      customMaterialModifiedDetail: (name: string) => `Modificato il materiale personalizzato “${name}”.`,
      materialLibraryModified: "Libreria dei materiali modificata",
      materialLibraryModifiedDetail: "Modificati più materiali personalizzati.",
      climateModified: "Clima di progetto modificato",
      climateScope: "Clima di progetto",
      settingsModified: "Impostazioni di calcolo modificate",
      settingsScope: "Impostazioni di calcolo",
      projectModified: "Progetto modificato",
      projectModifiedDetail: "Più valori del progetto modificati in questa azione salvata.",
    },
    materials: {
      panelDesc: "Materiali predefiniti e personalizzati. I materiali personalizzati sono salvati nella memoria locale di questo browser.",
      newMaterial: "+ Nuovo materiale",
      searchPlaceholder: "Cerca per nome...",
      allCategories: "Tutte le categorie",
      allManufacturers: "Tutti i produttori",
      sortName: "Ordina: Nome",
      sortDensity: "Ordina: Densità ρ",
      sortLambda: "Ordina: λ",
      sortMu: "Ordina: μ",
      sortWcap: "Ordina: wcap",
      sortDirectionTitle: "Inverti ordine crescente/decrescente",
      sortAsc: "▲ Cresc.",
      sortDesc: "▼ Decresc.",
      colSymbol: "Simbolo",
      colName: "Nome",
      colManufacturer: "Produttore",
      customBadge: "personalizzato",
      edit: "Modifica",
      delete: "Elimina",
      deleteConfirm: "Eliminare questo materiale personalizzato?",
      variableSdTitle: (min: number, max: number) => `Sd variabile: ${min}–${max} m (scheda tecnica)`,
      navHint: "Scelga una categoria oppure cerchi per nome qui sopra.",
      sourceAssumed: "Non presente nella scheda tecnica: valore ipotizzato",
      sourceDerived: "Non presente nella scheda tecnica: ricavato dai suoi valori",
      dataNotes: {
        lambdaBelowAir: (lambda: number) => `la λ = ${lambda} W/(m·K) della scheda tecnica è inferiore a quella dell'aria ferma: non utilizzata`,
        densityDerived: "densità ricavata dai valori della scheda tecnica",
      },
    },
    materialModal: {
      titleMembrane: "Membrana Rothoblaas",
      titleNew: "Nuovo materiale",
      titleEditCopy: "Modifica materiale (salvato come copia personalizzata)",
      titleEdit: "Modifica materiale",
      unnamed: "Materiale senza nome",
      membraneLockedHint: "Le membrane Rothoblaas sono mostrate con i valori della scheda tecnica e non possono essere modificate né copiate.",
      builtinHint: "I materiali predefiniti non possono essere modificati: il salvataggio crea una copia personalizzata e gli strati di questa stratigrafia che usano questo materiale passano alla copia.",
      technicalDataSheet: "Scheda tecnica ↗",
      productPage: "Pagina del prodotto ↗",
      checked: (date: string) => `verificato il ${date}`,
      laboratoryNotStated: "laboratorio non indicato",
      propertiesLabel: "Proprietà del materiale",
      tabIdentity: "Identità",
      tabGraphics: "Grafica",
      tabThermal: "Termica",
      tabMoisture: "Umidità",
      tabAcoustic: "Acustica",
      name: "Nome",
      manufacturer: "Produttore",
      optional: "facoltativo",
      category: "Categoria",
      categoryPlaceholder: "es. insulation",
      thickness: "Spessore",
      thicknessVariable: "Spessore variabile",
      thicknessKnown: "Spessori noti (dal produttore)",
      reference: "Riferimento",
      referencePlaceholder: "es. 100",
      min: "Min",
      max: "Max",
      knownThicknesses: "Spessori noti",
      knownThicknessesHint: ", separati da virgola - il primo è quello di riferimento",
      knownThicknessesPlaceholder: "es. 40, 60, 80, 100, 120",
      source: "Fonte",
      laboratory: "Laboratorio",
      date: "Data",
      file: "File",
      sectionSymbolCaption: "Simbolo di sezione (retino):",
      automaticFromCategory: " - automatico, dalla categoria",
      sectionSymbol: "Simbolo di sezione",
      auto: "Auto",
      hatches: {
        "": "Auto (dalla categoria)",
        concrete: "Calcestruzzo / massetto (punteggiato)",
        masonry: "Muratura piena (corsi di mattoni)",
        "masonry-hollow": "Muratura forata (corsi di mattoni + fori)",
        render: "Intonaco / malta (puntini fini)",
        board: "Lastra rigida, tipo gesso (liscio)",
        wool: "Isolante fibroso (anelli)",
        foam: "Isolante in schiuma rigida (cerchi)",
        fiberboard: "Pannello in fibra di legno / granulare (macchiettato)",
        wood: "Legno (venature)",
        membrane: "Membrana / foglio (linea marcata)",
        stone: "Pietra naturale (screpolature irregolari)",
        metal: "Metallo (diagonale fine)",
        generic: "Generico (tratteggio diagonale)",
      },
      thermalHint: "Una sola conduttività termica per materiale (nessuna λ(w) nella versione web).",
      sdValue: "Valore Sd",
      vapourResistanceZ: "Resistenza al vapore Z",
      variableSd: "Sd variabile",
      variableSdRange: (min: number, max: number) => `: ${min}–${max} m nella scheda tecnica`,
      variableSdCurve: " (curva Sd(UR) misurata: vedere Proprietà igrotermiche)",
      admissibleCondensate: "Condensa ammissibile",
      admissibleCondensateTitle: "Condensa ammissibile dell'analisi di Glaser mensile (tabella legacy frmInfoVAP)",
      condDefault: (label: string) => `Predefinito: ${label}`,
      vapourNote: (thickness: string, unit: string, mu: string) => `Per ${thickness} ${unit}: μ = ${mu}`,
      vapourNoteNeedsThickness: "Serve uno spessore (di riferimento o spessori noti).",
      porosity: "Porosità",
      saturation: "Saturazione",
      capillary: "Capillare",
      waterAbsorption: "Assorbimento d'acqua",
      airflow: "Flusso d'aria",
      acousticHint: "Facoltativo: usato dagli strumenti acustici, non dai calcoli igrotermici.",
      errorMembrane: "Le membrane sono solo prodotti Rothoblaas: un materiale personalizzato non può appartenere alla categoria Membrana.",
      errorSdThickness: (label: string) => `Per inserire la diffusione del vapore come ${label}, assegni prima uno spessore al materiale (spessore di riferimento o spessori noti).`,
      errorSdPositive: (label: string) => `${label} deve essere un numero maggiore di 0.`,
      save: "Salva materiale",
      cancel: "Annulla",
      close: "Chiudi",
      density: "Massa volumica ρ",
      lambdaDry: "Conduttività termica a secco λ",
      cp: "Capacità termica specifica c",
      mu: "Fattore di resistenza al vapore μ",
      wCap: "Contenuto d'acqua capillare wcap",
      emissivity: "Emissività ε",
    },
    materialHygro: {
      title: "Proprietà igrotermiche",
      showTitle: "Mostra le proprietà igrotermiche",
      hideTitle: "Nascondi le proprietà igrotermiche",
      tabSorption: "Assorbimento",
      tabSuction: "Suzione",
      thickness: "Spessore",
      sdNamePermeance: "permeanza",
      sdNameVapourResistance: "resistenza al vapore",
      dryCup: "Dry cup [US perm]",
      muDry: "μ secco [-]",
      w80ClassAssumed: (cls: string) => `Nessuna curva di assorbimento misurata: valore della classe “${cls}” del modello quasi-dinamico`,
      noSorptionCurve: "Nessuna curva di assorbimento misurata",
      porosity: "Porosità [%]",
      sdChartTitle: (thickness: string, unit: string) => `Sd in funzione dell'umidità relativa (misurato, ${thickness} ${unit})`,
      sorptionMeasured: "Isoterma di assorbimento (misurata)",
      sorptionTable: "Isoterma di assorbimento (tabella del materiale)",
      sorptionLinear: "Contenuto d'acqua: retta del modello quasi-dinamico",
      suctionTitle: "Curva di suzione (accumulo di umidità)",
      klTitle: "Conduttività liquida k(θ)",
      muwTitle: "Fattore di resistenza al vapore μ(w)",
      membraneVariableNote: "Sd variabile nella scheda tecnica, ma nessuna curva Sd(UR) misurata: i calcoli usano lo stato più chiuso (Sd fisso).",
      membraneFixedNote: "Membrana: film non igroscopico (w = 0), Sd indipendente dall'umidità.",
      footSdCurve: "Sd(φ): misurato secondo EN ISO 12572, letto in modo log-lineare tra i punti (come nelle analisi 1D, mensile e quasi-dinamica); l'analisi 2D usa lo stato più chiuso.",
      footTable: "w(φ) e μ(w) come li usano le analisi 2D e quasi-dinamica.",
      footTableMeasured: "w(φ) e μ(w) come li usano le analisi 2D e quasi-dinamica (assorbimento dalla funzione di accumulo, Kelvin a 20 °C).",
      footNoCurve: (cls: string) => `Nessuna curva di assorbimento misurata: l'analisi 2D non usa alcun contenuto d'acqua; l'analisi quasi-dinamica accumula w = ξ·φ (classe “${cls}”).`,
      footLambda: "λ è un valore unico (nessuna λ(w)).",
      assumedNoData: "Ipotizzato: nessun dato misurato per questo materiale",
      assumedTag: "ipotizzato",
      sorptionClasses: {
        membrane: "Membrana",
        woodBased: "Legno, materiali a base di legno e fibre naturali",
        gypsum: "Lastre e intonaci di gesso",
        nonHygroInsulation: "Isolanti non igroscopici",
        aac: "Calcestruzzo aerato autoclavato",
        lightweightConcrete: "Calcestruzzo alleggerito",
        concrete: "Calcestruzzo",
        concreteBlock: "Blocchi di calcestruzzo",
        calciumSilicate: "Silicato di calcio",
        firedClayBrick: "Laterizio",
        naturalStone: "Pietra naturale",
        metal: "Metallo",
        mineralRender: "Intonaci, malte e lastre minerali",
        unclassified: "Non classificato",
      },
    },
    calculation: {
      panelDesc: "Impostazioni delle analisi statiche: resistenze superficiali e pressione atmosferica. Le resistenze superficiali si impostano anche sul disegno della stratigrafia.",
      advancedSettings: "Impostazioni avanzate",
      showHide: "mostra / nascondi",
      atmosphericPressure: "Pressione atmosferica [Pa]",
      modelsHint: "Modello della pressione di saturazione: approssimazione di Magnus-Tetens (fisso). Modello di diffusione: metodo di Glaser stazionario / diffusione del vapore 2D alla condizione di progetto (fisso). Modello di condensazione: discontinuità del flusso di vapore al piano di condensazione (1D) / segnalazione locale UR>100% (2D) (fisso). Sono indicati qui per trasparenza; non sono ancora sostituibili dall'utente. Le impostazioni di mesh, iterazioni e tolleranze del solutore igrotermico 2D sono state spostate in Impostazioni (icona dell'ingranaggio, in alto a destra) → scheda Solutore 2D.",
      diagnosticsTitle: "Diagnostica del solutore (mesh 2D)",
      diagnosticsIntro: "Il dettaglio completo di come è stato ottenuto l'ultimo risultato igrotermico 2D - metodo, dimensione della mesh, convergenza, storico dei residui - separatamente per la soluzione del campo termico e di quello della pressione di vapore (possono differire molto: il campo del vapore è di solito molto peggio condizionato). La convergenza del solutore (questa scheda) e l'accuratezza ingegneristica (la tabella di convergenza della mesh qui sotto, con la mesh automatica) sono due questioni distinte - una soluzione pienamente convergente su una mesh grossolana può comunque essere una risposta imprecisa.",
      thermal: "Termico",
      vapourPressure: "Pressione di vapore",
      vapour: "Vapore",
      addLayerForDiagnostics: "Aggiunga almeno uno strato per vedere qui la diagnostica del solutore.",
      noDiagnosticsYet: "Nessuna diagnostica disponibile.",
      methodUsed: "Metodo usato",
      autoSuffix: " (auto)",
      solverAuto: "automatico",
      meshCells: "Celle della mesh",
      iterationsCycles: "Iterazioni / cicli",
      residualInitial: "Residuo (iniziale)",
      residualFinal: "Residuo (finale)",
      reductionFactor: "Fattore di riduzione",
      solverFlux: "Flusso del solutore q",
      energyBalanceError: "Errore del bilancio energetico",
      spdSystem: "Sistema SPD",
      conductivityContrast: "Contrasto di conduttività",
      finalStatus: "Stato finale",
      yes: "Sì",
      no: "No",
      residualChartTitle: "Residuo per iterazione (scala logaritmica)",
      compareSolvers: "Confronta CG e V-cycle su questo problema",
      runCalculationFirst: "Esegua prima un calcolo.",
      solvingBothMethods: "Calcolo con entrambi i metodi…",
      colMethod: "Metodo",
      colQThermal: "q (termico)",
      colIterations: "Iterazioni",
      colResidual: "Residuo",
      colStatus: "Stato",
      colTime: "Tempo",
      meshLadder: "Sequenza di convergenza della mesh (questo calcolo):",
      colRows: "Righe",
      colSolverConverged: "Solutore a convergenza",
      colAccepted: "Accettata",
    },
    charts: {
      noDataYet: "Nessun dato",
      legendToggle: (label: string) => `${label} (clic per nascondere o mostrare)`,
    },
    search: {
      clearSearch: "Cancella la ricerca",
      filters: "Filtri",
    },
    monthly: {
      title: "Glaser mensile",
      panelDesc: "Analisi mensile quasi-stazionaria (1D), il metodo dell'applicazione Thermal precedente: Glaser a ogni interfaccia mese per mese, con la condensa che si accumula ed evapora da un mese all'altro, alimentata dalle medie mensili del clima del sito. Viene eseguita a ogni calcolo, insieme ai risultati Semplificato e Mesh 2D.",
      inputsTitle: "Dati di calcolo",
      summaryClass: (cls: number) => `classe di umidità ${cls}`,
      summaryConstant: (ti: string, unit: string, rhi: string) => `Ti ${ti} ${unit}, RHi ${rhi} %`,
      climateOfSite: "clima del sito",
      summaryDrying: "tempo di asciugatura",
      outsideClimate: "Clima esterno",
      outsideClimateTip: "Il clima del sito scelto nella scheda Dati (un sito sulla mappa o un file EPW), lo stesso delle altre analisi. Medie mensili dei valori orari: Te = media di T, pv,e = media di RH·psat(T).",
      loadingClimate: "Caricamento del clima…",
      change: "modifica →",
      noClimate: "Nessun clima ancora.",
      insideClimate: "Clima interno",
      tipIso13788: "Classi di umidità, UNI EN ISO 13788: Δp = 270 · classe · clamp((20 − Te) / 20; 0; 1) Pa, pi = pe + 1.10 · Δp, Ti = 20 °C.",
      tipEn15026: "EN 15026 Annex D: ora per ora dalla media mobile sulle 24 h della temperatura esterna del file EPW, poi mediata per mese.",
      tipConstant: "Temperatura e umidità relativa interne costanti in ogni mese.",
      insideModel: "Modello del clima interno",
      ashraeOption: "ASHRAE 160 - non ancora disponibile: i suoi valori sono in fase di verifica",
      modelIso13788: "Classi di umidità (UNI EN ISO 13788)",
      untilAshrae: " - finché ASHRAE 160 non sarà disponibile",
      modelConstant: "Ti e RHi costanti",
      modelEn15026: "EN 15026 Annex D",
      humidityClass: "Classe di umidità",
      classOption: (cls: number) => `Classe ${cls}`,
      moistureLoad: "Carico igrometrico",
      loads: {
        "normal+5": "Carico igrometrico normale + 5 % (caso di progetto)",
        normal: "Carico igrometrico normale",
        low: "Carico igrometrico basso",
        high: "Carico igrometrico alto",
      },
      dryingTime: "Tempo di asciugatura",
      dryingTip: "Mesi necessari per asciugare 1 kg/m² d'acqua posto al centro dello strato scelto, a partire da ottobre, con il clima mensile. Metodo dell'intera analisi: UNI EN ISO 13788, Glaser mensile con accumulo.",
      dryingLayer: "Strato del calcolo di asciugatura",
      chooseLayer: "Scelga lo strato…",
      monthlyClimate: "Clima mensile",
      monthlyClimateUsed: "Clima mensile e clima interno utilizzato",
      monthlyClimateTip: (utcOffset: string) => `Medie mensili dei valori orari in ora locale (UTC${utcOffset}): Te = media di T; pe = media di RH·psat(T); RHe = pe / psat(Te).`,
      statusEmpty: "La stratigrafia è vuota: aggiunga prima degli strati nel pannello Stratigrafia.",
      statusInternalWall: "Nessun lato esterno (INTERNO su entrambi i lati: una parete interna): l'analisi mensile richiede un clima esterno e uno interno.",
      statusBothOutside: "Nessun lato esterno (ESTERNO su entrambi i lati): l'analisi mensile richiede un clima esterno e uno interno.",
      statusChooseClimate: "Scelga il clima del sito nella scheda Dati (un sito sulla mappa o un file EPW).",
      failed: "L'analisi mensile non è riuscita.",
      verified: "Verificato",
      notVerified: (n: number) => `Non verificato (${n} ${n > 1 ? "verifiche" : "verifica"})`,
      checkResidual: "bilancio annuale",
      checkAdmissible: "condensa ammissibile",
      checkMould: "muffa (f_Rsi)",
      allPass: "Nessuna condensa residua a fine ciclo, ogni quantità accumulata entro il proprio valore ammissibile, f_{Rsi} superiore a f_{Rsi,min}.",
      allPassBoth: "Nessuna condensa residua a fine ciclo, ogni quantità accumulata entro il proprio valore ammissibile, f_{Rsi} superiore a f_{Rsi,min}, in entrambe le sezioni.",
      settingsUsed: "Impostazioni utilizzate",
      startOfCycle: "Inizio del ciclo",
      subLayers: "Sottostrati",
      subLayersValue: (rMax: string) => `ogni strato con R > ${rMax} m²K/W suddiviso in sottostrati uguali`,
      humidityDependentSd: "Sd dipendente dall'umidità",
      condensationTest: "Verifica di condensa",
      surfaces: "Superfici",
      surfacesValue: (rsi: string, rse: string) => `R_{si} = ${rsi} m²K/W, R_{se} = ${rse} m²K/W (intercapedini d'aria secondo EN ISO 6946, come nelle altre analisi)`,
      sections: "Sezioni",
      sectionsValue: (list: string) => `${list} - sezioni 1D separate, nessun metodo combinato`,
      admissibleCondensate: "Condensa ammissibile",
      mould: "Muffa",
      uValue: (section: string) => `Trasmittanza U (${section})`,
      interfaces: (n: number) => `${n} interfacce`,
      maxAccumulated: "Condensa accumulata max.",
      interfacesCondensing: (n: number) => `${n} interfacce`,
      noCondensation: "nessuna condensa",
      leftAtEnd: "Residua a fine ciclo",
      mustBeZero: "deve essere 0",
      mouldStat: "Muffa: f_{Rsi} / f_{Rsi,min}",
      criticalMonth: (month: string) => `mese critico ${month}`,
      noMonthAtRisk: "nessun mese a rischio",
      months: "mesi",
      dryingTag: (layer: string) => `1 kg/m² in ${layer}, da ottobre`,
      monthByMonth: (section: string) => `Mese per mese - ${section}`,
      month: "Mese",
      maTotal: "M_{a} totale",
      noInterfaceCondenses: "Nessuna interfaccia condensa in alcun mese.",
      monthsNote: (unit: string) => `G_{c}: condensazione (+) o evaporazione (−) del mese; M_{a}: accumulata a fine mese [${unit}]. Interfacce numerate dalla superficie esterna.`,
      admissibleTitle: (section: string) => `Condensa ammissibile - ${section}`,
      interface: "Interfaccia",
      maxMa: (unit: string) => `M_{a} max. [${unit}]`,
      admissibleColumn: (unit: string) => `Ammissibile [${unit}]`,
      materialAndRule: "Materiale e regola",
      notAssessed: "non valutata",
      within: "entro il limite",
      exceeded: "⚠ superata",
      admissibleNote: "A un'interfaccia tra due materiali, la minore delle loro quantità ammissibili.",
      glaserDiagram: "Diagramma di Glaser",
      abscissa: "Ascissa",
      thickness: "Spessore",
      diagramMonth: "Mese del diagramma",
      condensationOverCycle: "Condensa nel ciclo",
      condensationNote: (n: number, months: string, maxG: string, unit: string, at: string) => `⚠ Si forma condensa in ${n} ${n === 1 ? "mese" : "mesi"} (${months}): al massimo ${maxG} ${unit} in ${at};`,
      evaporatedBy: (month: string) => `è evaporata entro la fine di ${month}.`,
      evaporatesWithin: "evapora entro il ciclo.",
      notAllEvaporates: "non evapora completamente entro il ciclo.",
      mouldTitle: (section: string) => `Muffa - UNI EN ISO 13788 - ${section}`,
      mouldNote: (rh: string, rsi: string, fRsi: string, rMin: string) => `RH_{si} ≤ ${rh} %, R_{si} = ${rsi} m²K/W: f_{Rsi} della sezione = ${fRsi}${rMin}. Mesi con T_{i} ≤ T_{e}: non valutati (—); f_{Rsi,min} ≤ 0: nessun requisito in quel mese.`,
      mouldRmin: (value: string, unit: string) => `; resistenza termica minima degli strati per il mese critico: ${value} ${unit}`,
      curvesShown: "Curve mostrate (clic per nascondere o mostrare)",
      legendP: "pressione di vapore p",
      legendPsat: "pressione di saturazione p_{sat} (● faccia di uno strato, ○ interno a uno strato)",
      legendWet: "interfaccia con condensa",
      glaserAria: (month: string) => `Diagramma di Glaser, ${month}`,
      depthAxis: (unit: string) => `profondità dall'interno [${unit}]`,
      sdAxisSi: "Sd dall'interno [m]",
      sdAxisUk: "resistenza al vapore dall'interno [MN·s/g]",
      sdAxisImperial: "resistenza al vapore dall'interno [rep = 1/perm]",
      inside: "interno",
      outside: "esterno",
      pressureAxis: "pressione [Pa]",
      surface: "superficie",
      node: (k: number) => `nodo ${k}`,
      psatTip: (point: string, psat: number) => `${point}: p_sat ${psat} Pa`,
      legendCondensed: "condensata nel mese",
      legendEvaporated: "evaporata nel mese",
      legendAccumulated: "accumulata a fine mese",
      noCondensateAnyMonth: "nessuna condensa in alcun mese",
      accumulationAria: "Acqua condensata, evaporata e accumulata per mese",
      condensed: "condensata",
      evaporated: "evaporata",
      accumulated: "accumulata",
      sectionLabels: {
        bay: "Campata (tra i montanti)",
        stud: "Montante",
        whole: "Intera parete",
      },
      interfaceOuter: "superficie esterna",
      interfaceInner: (material: string) => `${material} | superficie interna`,
      interfaceWithin: (material: string) => `all'interno di ${material}`,
      insideIso13788: (cls: number, fs: string, ti: number) =>
        `Classe di umidità UNI EN ISO 13788 ${cls}${cls === 3 ? " (DM 26/06/2015)" : ""}: Δp = 270·${cls}·clamp((20 − Te)/20; 0; 1) Pa, pi = pe + ${fs}·Δp (al massimo psat(Ti)), Ti = ${ti} °C (Te se più calda)`,
      insideConstant: (ti: number, rhi: number) => `Costante: Ti = ${ti} °C, RHi = ${rhi} %`,
      insideEn15026: (load: string) => `EN 15026 Annex D, ${load}: valori orari dalla media mobile sulle 24 h della temperatura esterna, mediati per mese`,
      startNotes: {
        OCTOBER_LEGACY: "ottobre, come nel metodo legacy",
        NONE_CONDENSES: "nessun mese condensa partendo da asciutto: ciclo da ottobre",
        ALL_CONDENSE: "ogni mese condensa partendo da asciutto: ciclo da ottobre",
        FIRST_CONDENSING: (month: string) => `primo mese con condensa dopo un mese senza condensa: ${month}`,
      },
      failureMould: (fRsi: string, fRsiMin: string, month: string) => `f_Rsi = ${fRsi} inferiore a f_Rsi,min = ${fRsiMin} (mese critico: ${month})`,
      failureResidual: (g: string, at: string) => `condensa residua a fine ciclo: ${g} g/m² in ${at}`,
      failureAdmissible: (g: string, at: string, admissible: string, material: string, rule: string) =>
        `${g} g/m² in ${at}, oltre il valore ammissibile di ${admissible} g/m² (${material}: ${rule})`,
      warningPicardCapped: (section: string, passes: number, tolM: number) =>
        `${section}: l'iterazione dell'Sd dipendente dall'umidità ha raggiunto ${passes} passaggi in almeno un mese senza stabilizzarsi entro ${tolM} m; sono mostrati i valori dell'ultimo passaggio.`,
      warningOutsideRhAbove100: "In almeno un mese la pressione di vapore esterna media supera la pressione di saturazione alla temperatura media (RHe > 100 %); viene usata così com'è.",
      presetLabels: {
        legacy: "Thermal legacy",
        iso13788: "UNI EN ISO 13788 (predefinito)",
      },
      startRules: {
        legacy: "ottobre (legacy)",
        iso13788: "primo mese con condensa dopo un mese senza condensa (UNI EN ISO 13788), per sezione",
      },
      sdRules: {
        legacy: "Sd dipendente dall'umidità dalla RH della faccia interna del suo sottostrato (regola legacy)",
        iso13788: "Sd dipendente dall'umidità dalla RH media delle due facce del suo sottostrato",
      },
      picardRule: (tolM: number, max: number) => `finché la somma di |ΔSd| ≤ ${tolM} m, al massimo ${max} passaggi`,
      pTolExact: "p ≥ psat esatto (legacy)",
      pTolRule: (pTol: number) => `p ≥ psat·(1 − ${pTol})`,
      mouldRule: (rh: number, rsi: number) => `UNI EN ISO 13788: f_Rsi,min per mese con RH_si ≤ ${rh} %, R_si = ${rsi} m²K/W`,
      admissibleRule: "tabella legacy frmInfoVAP per materiale; a un'interfaccia la minore quantità ammissibile dei materiali a contatto; \"30 d\" letto come 30·ρ·d",
    },
    dynamic: {
      // Inputs tab
      desc: "Ora per ora su più anni con dati climatici reali, nel quadro della EN 15026: temperature, umidità relativa, condensazione e asciugatura della stratigrafia corrente.",
      preview: "Anteprima",
      why: "Perché \"quasi-dinamica\"?",
      hide: "nascondi",
      settingsTitle: "Impostazioni di calcolo (Impostazioni → Quasi-dinamica)",
      whyDynamicTerm: "Dinamica",
      whyDynamic: ": il tempo è simulato ora per ora, quindi gli strati accumulano e rilasciano calore e umidità - inumidimento invernale e asciugatura estiva, umidità di costruzione che asciuga nel corso degli anni. È ciò che i calcoli stazionari (Glaser, 2D, mensile) non possono mostrare.",
      whyQuasiTerm: "Quasi",
      whyQuasi: ": le proprietà dei materiali restano costanti. λ, ρc e µ non variano con l'umidità; l'accumulo di umidità è lineare, w = ξ·φ con ξ la secante della curva di sorbimento all'80 % di UR; l'acqua si muove solo come vapore (nessun trasporto liquido/capillare). Il calore latente e la capacità termica dell'acqua contenuta sono facoltativi (Effetti inclusi nel modello, sotto). Le membrane igrovariabili (CLIMA CONTROL) variano invece il loro Sd con l'UR, e la condensa è seguita mentre si forma e asciuga.",
      whyFullBefore: "Il modello ",
      whyFullTerm: "dinamico completo",
      whyFull: " della EN 15026 (come in WUFI) aggiunge accumulo e permeabilità dipendenti dall'umidità, conduzione liquida e λ(w), e l'entalpia del vapore e del liquido nel bilancio energetico; richiede dati igrici completi dei materiali ed è validato sul benchmark dell'Annex B della norma. È il passo successivo. Il modello quasi-dinamico è affidabile per i pacchetti governati dalla diffusione del vapore (pacchetti leggeri in legno e isolati con membrane) ed è uno strumento di verifica preliminare, non una certificazione.",
      tabsLabel: "Analisi quasi-dinamica",
      tabInputs: "Dati di input",
      tabResults: "Risultati",
      runFirst: "Esegua prima l'analisi quasi-dinamica",
      outerSurface: "Superficie esterna",
      absorptance: "Assorbanza solare",
      emissivity: "Emissività",
      insideLoad: "Clima interno (EN 15026 Annex D)",
      loads: {
        "normal+5": "Carico igrometrico normale + 5 % (caso di progetto)",
        normal: "Carico igrometrico normale",
        low: "Carico igrometrico basso",
        high: "Carico igrometrico alto",
      },
      ashrae160Note: "ASHRAE 160 non ancora disponibile (i suoi valori sono in fase di verifica): si usa il modello UNI / EN.",
      modelAndRun: "Modello e calcolo",
      run: "Calcolo",
      years: "Anni",
      woodCriterion: "Criterio legno [M.-%]",
      woodCriterionTitle: "Fisso: contenuto di umidità dei materiali a base di legno ≤ 16 M.-%",
      construction: "Umidità di costruzione (UR iniziale 95 %, altrimenti 80 %)",
      stepAuto: (step: string) => `automatico (${step})`,
      solver2d: (method: string, tolerance: string, rows: number) => `${method} · tolleranza ${tolerance} · ${rows} righe`,
      direct1d: "solutore 1D diretto",
      calcSummary: (step: string, solver: string) => `Calcolo: passo ${step} · ${solver} ·`,
      changeInSettings: "modifichi in Impostazioni ⚙",
      runButton: (mode: string) => `Esegui l'analisi quasi-dinamica (${mode})`,
      calculating: "Calcolo in corso…",
      hint2d: "Modalità mesh 2D (intestazione): montanti e isolante formano un'unica griglia, accoppiata. Circa 10-20 s per anno simulato con 20-40 righe; griglie più fini richiedono più tempo, e il server interrompe un calcolo dopo 300 s.",
      hint1d: "Modalità semplificata (intestazione): una colonna 1D attraverso gli strati; i montanti non sono inclusi (per includerli passi alla mesh 2D nell'intestazione).",
      emptyStratigraphy: "La stratigrafia è vuota: aggiunga prima gli strati nel pannello Stratigrafia.",
      noClimate: "Scelga prima il clima: scheda Dati, Clima del sito (un sito sulla mappa o un file EPW).",
      noOutdoorSide: "Nessun lato esterno: l'analisi quasi-dinamica richiede un lato ESTERNO e un lato INTERNO sul disegno (scheda Stratigrafia).",
      analysisFailed: "L'analisi non è riuscita.",
      cancelled: "Il calcolo è stato annullato; resta visualizzato il risultato precedente (se presente).",
      unreadable: "Non è stato possibile leggere la risposta del server.",
      unreachable: "Non è stato possibile raggiungere il server.",
      timeout: "Il calcolo ha richiesto più tempo di quanto il server consenta (300 s). Simuli meno anni, usi una griglia 2D più grossolana o un passo temporale più lungo.",
      statusError: (status: number) => `Il server ha risposto con lo stato ${status}.`,
      stopped: "Il calcolo si è interrotto prima della fine (il server potrebbe aver raggiunto il suo limite di 300 s). Simuli meno anni, usi una griglia 2D più grossolana o un passo temporale più lungo.",
      lastResult: (mode: string, years: number, source: string) => `Ultimo risultato: ${mode}, ${years} anno/i, ${source} ·`,
      openResults: "apri Risultati →",
      mode2d: "Mesh 2D",
      mode1d: "1D",
      modeSimplified: "Semplificata",
      modeSimplified1d: "Semplificata (1D)",
      // effects included in the model
      effectsLegend: "Effetti inclusi nel modello",
      effects: {
        dynamic: {
          label: "Sistema dinamico",
          hint: "Accumulo di calore e umidità (i termini ∂/∂t). Disattivato: ogni passo temporale è lo stato stazionario del suo clima (Glaser ora per ora); si accumula solo la condensa.",
        },
        waterHeatCapacity: {
          label: "Capacità termica dell'acqua contenuta",
          hint: "ρc + w·c_w, con c_w = 4190 J/(kg·K) e il contenuto d'acqua w del passo temporale precedente.",
        },
        latentHeat: {
          label: "Calore latente",
          hint: "Calore rilasciato dove il vapore è assorbito o condensa e sottratto dove evapora: h_v · afflusso netto di vapore di ogni cella, h_v = 2500 kJ/kg (EN 15026:2007), dal campo del vapore del passo temporale precedente.",
        },
        variableSd: {
          label: "Resistenza al vapore variabile µ(w), Sd(UR)",
          hint: "Sd(UR) delle membrane con una curva misurata (CLIMA CONTROL 80 e 160) e µ(w) dei materiali con una curva µ dipendente dall'umidità, aggiornati a ogni passo temporale. Disattivato: i loro valori fissi.",
        },
        rain: {
          label: "Pioggia",
          hint: "Pioggia battente sulla superficie esterna, dalla pioggia e dal vento del clima; l'acqua che entra esce per evaporazione (nessun trasporto capillare).",
        },
        conductivity: {
          label: "Conduttività variabile λ(w)",
          hint: "Richiede dati di λ dipendenti dall'umidità per ogni materiale.",
        },
        capillarity: {
          label: "Capillarità",
          hint: "Trasporto liquido: richiede la curva di sorbimento completa e i coefficienti di trasporto liquido di ogni materiale.",
        },
      },
      availableSoon: "disponibile a breve",
      needsDynamic: "Richiede il sistema dinamico (accumulo).",
      noRain: "Il clima scelto non contiene pioggia (PVGIS non ne fornisce): scelga ERA5 (Open-Meteo), una stazione TMYx o un file EPW con pioggia.",
      rainSettings: "Impostazioni della pioggia",
      waterEntering: "Acqua che entra nella parete",
      rainModeTitle: "Stabilito dalle norme dei menu (Impostazioni → Generale)",
      rainAbsorb: "Facciata assorbente (EN 15026): 70 % nello strato esterno",
      rainDrain: "Rivestimento con drenaggio (ASHRAE 160): 1 % dietro di esso",
      rainAbsorbHint: "Intonaco, muratura a vista: il 70 % della pioggia battente entra come acqua liquida nelle celle più esterne dello strato esterno, fino alla saturazione capillare del materiale (w_cap); il resto scorre via.",
      rainDrainHint: "Rivestimenti o pannelli su intercapedine drenata: l'1 % della pioggia battente si deposita nella parte esterna del primo strato dietro il rivestimento (la perdita di progetto dell'ASHRAE 160).",
      exposure: "Esposizione (coefficiente di pioggia battente R2)",
      exposures: {
        low: "Edificio basso (fino a 10 m)",
        "tall-low": "Edificio alto, parte inferiore",
        "tall-mid": "Edificio alto, parte centrale",
        "tall-top": "Edificio alto, parte superiore",
      },
      exposureOption: (label: string, r2: number) => `${label} — R2 = ${r2} s/m`,
      rainFormula: "Pioggia sulla parete = R2 × velocità del vento × pioggia orizzontale × cos(angolo tra il vento e la facciata), solo dal lato sopravvento; le coperture ricevono anche la pioggia orizzontale × cos(inclinazione).",
      // orientation of the outer surface
      directions: ["N", "NE", "E", "SE", "S", "SO", "O", "NO"],
      orientLabel: (tilt: number, direction: string, azimuth: number) => `Superficie esterna: inclinazione ${tilt}°, orientata a ${direction} ${azimuth}°. Trascini o usi i tasti freccia per ruotarla.`,
      orientValue: (tilt: number, azimuth: number) => `inclinazione ${tilt}°, orientamento ${azimuth}°`,
      outside: "esterno",
      inside: "interno",
      turning: "↔ rotazione (orientamento)",
      tilting: "↕ inclinazione",
      tiltTitle: (now: string) => `Inclinazione: 0° copertura piana, 90° parete verticale (ora: ${now})`,
      flatRoof: "copertura piana",
      wall: "parete",
      roof: "copertura",
      azimuthTitle: (now: string) => `Orientamento: direzione della superficie esterna, gradi da nord, in senso orario (ora: ${now})`,
      anyFlat: "qualsiasi, piana",
      orientHint: (wallMinTilt: number) => `Trascini la parete: sinistra/destra la ruota (orientamento), in basso la alza verso la verticale, in alto la abbassa - una alla volta, scelta dal primo movimento di ogni trascinamento. Tasti freccia: passi di 1°, Maiusc 5°. Un'inclinazione ≥ ${wallMinTilt}° è trattata come parete, inferiore come copertura.`,
      // progress window
      running: "Analisi quasi-dinamica in corso",
      cancelling: "Annullamento…",
      sending: "Invio della stratigrafia al server",
      comparison: " (confronto)",
      simulatedUpTo: (day: string, years: number) => `simulato fino al ${day} di ${years} anno/i`,
      elapsed: (time: string) => `trascorso ${time}`,
      timeLeft: (time: string) => ` · circa ${time} rimanenti`,
      // Results tab
      resultsDesc: "Risultato dell'ultimo calcolo. Trascini su un grafico temporale per selezionare un periodo, faccia doppio clic per l'intero calcolo.",
      noResult: "Nessun risultato: esegua l'analisi in Analisi quasi-dinamica.",
      checksNotSatisfied: (failed: number, total: number) => `⚠ ${failed} di ${total} verifiche non soddisfatte`,
      allSatisfied: "tutte le verifiche soddisfatte",
      noCheckApplicable: "nessuna verifica applicabile",
      verdictLine: (mode: string, years: number, source: string) => `${mode} · ${years} anno/i · ${source} · verifica preliminare, non certificazione · faccia clic su una verifica per i dettagli`,
      modeMismatch: (resultMode: string, headerMode: string) => `Questo risultato è stato calcolato in modalità ${resultMode}; l'intestazione è ora su ${headerMode}. Esegua di nuovo il calcolo per aggiornarlo.`,
      notApplicable: "n/a",
      satisfied: "soddisfatta",
      notSatisfied: "non soddisfatta",
      notSatisfiedWarn: "⚠ non soddisfatta",
      sectionsLabel: "Sezioni",
      secRun: "Calcolo e dati climatici",
      secPeriod: "Risultati nel periodo",
      secPeriodTitle: "Risultati nel periodo selezionato",
      secConv: "Convergenza",
      secConvTitle: "Convergenza nel periodo selezionato",
      secCharts: "Grafici",
      secChartsTitle: "Grafici nel periodo selezionato",
      secFields: "Campi 2D",
      secFieldsTitle: "Campi 2D in ore significative",
      secHourly: "Ora per ora",
      secHourlyTitle: "Ora per ora (ultimo anno simulato)",
      secLayers: "Strati del modello",
      resultBadge: (mode: string) => `risultato ${mode}`,
      source: "Fonte",
      sourceDbs: (radiation: string, weather: string) => ` (radiazione ${radiation}, meteo ${weather})`,
      location: "Località",
      period: "Periodo",
      insideClimate: "Clima interno",
      attribution: "Attribuzione",
      runLine: (years: number, month: string, step: string, grid: string, cells: number, seconds: string, mass: string, energy: string) =>
        `Calcolo: ${years} anni dal 1 ${month}, passo temporale ${step}, ${grid}${cells} celle; calcolato in ${seconds} s; residui di bilancio: massa ${mass}, energia ${energy}.`,
      gridText: (rows: number, columns: number) => `griglia 2D ${rows} × ${columns} = `,
      rhInterfaces: "Umidità relativa alle interfacce (media giornaliera)",
      rhAxis: "UR [%]",
      tInterfaces: (unit: string) => `Temperatura alle interfacce (media giornaliera) [${unit}]`,
      outsideAir: "aria esterna",
      membraneSd: "Sd delle membrane (media giornaliera, banda = escursione giornaliera)",
      condensateTitle: "Condensa (media giornaliera) [g/m²]",
      total: "totale",
      waterTitle: "Acqua totale nel componente [kg/m²]",
      totalWater: "acqua totale",
      rainTitle: "Pioggia sulla superficie esterna ed entrante nella parete (al giorno) [kg/m²]",
      rainAxis: "kg/m² al giorno",
      rainOnSurface: "pioggia battente sulla superficie",
      rainEntering: "entrante nella parete",
      woodTitle: "Contenuto di umidità degli strati a base di legno [M.-%]",
      rhMapTitle: "Umidità relativa attraverso il componente, giorno per giorno",
      fieldColdest: "Nell'ora più fredda del calcolo",
      fieldPeak: "Al picco di condensa",
      inPeriod: "nel periodo",
      outsidePeriod: "fuori dal periodo",
      fieldLine: (date: string, hour: string, te: string, unit: string) => `${date}, ${hour}:00 (ora del calcolo) · esterno ${te} ${unit}`,
      fieldCondensate: (value: string) => ` · condensa ${value} g/m²`,
      noCondensateRun: "Nessuna condensa nell'intero calcolo.",
      hourlyTitle: "Ora per ora: UR alle 4 interfacce più umide dell'ultimo anno",
      hourlyLastYearOnly: (from: string) => `Le serie orarie coprono solo l'ultimo anno simulato (dal ${from}).`,
      selectLastYear: "Selezioni l'ultimo anno",
      membraneVariable: (min: number, max: number) => `membrana, Sd ${min}–${max} m secondo l'UR`,
      membraneFixed: (sd: string) => `membrana, Sd ${sd} m`,
      xiText: (xi: string, source: string) => `ξ = ${xi} kg/m³ (${source})`,
      // period filter
      dayLabel: (day: number, month: string, year: number) => `${day} ${month}, anno ${year}`,
      yearN: (year: number) => `Anno ${year}`,
      days: "giorni",
      presetPeriods: "Periodi predefiniti",
      wholeRun: "Intero calcolo",
      season: (season: string, from: string, to: string, year: number) => `${season} (${from}–${to}), anno ${year}`,
      winter: "Inverno",
      summer: "Estate",
      from: "Dal",
      to: "al",
      periodHint: "Trascini sulla striscia o su un grafico qui sotto per selezionare un periodo; faccia doppio clic su un grafico per l'intero calcolo.",
      day: "Giorno",
      month: "Mese",
      simulatedYear: "Anno simulato",
      overviewLabel: "Periodo selezionato nell'intero calcolo: trascini per modificarlo",
      overviewTitle: "temperatura dell'aria esterna, intero calcolo",
      // results in the period
      surfaceTmin: "Superficie interna, temperatura min.",
      fRsiMin: "Fattore di temperatura f_Rsi, min.",
      fRsiTag: (day: string, limit: number) => `${day} · limite ${limit}`,
      noHour10K: "nessuna ora con Ti − Te ≥ 10 K",
      condMax: "Condensa, max.",
      condTag: (day: string, days: number) => `${day} · ${days} giorno/i con condensa`,
      noneInPeriod: "nessuna nel periodo",
      condLastDay: "Condensa nell'ultimo giorno",
      driedOut: "asciugata",
      stillPresent: "ancora presente",
      woodMax: "Contenuto di umidità del legno, max.",
      rhMax: "UR a un'interfaccia, max. (oraria)",
      rainIn: "Pioggia entrante nella parete",
      rainTag: (onSurface: string) => `su ${onSurface} kg/m² di pioggia battente sulla superficie`,
      waterChange: "Acqua nel componente, variazione",
      waterTag: (from: string, to: string, trend: string) => `${from} → ${to} kg/m² · ${trend}`,
      netWetting: "inumidimento netto",
      netDrying: "asciugatura netta",
      noNetChange: "nessuna variazione netta",
      outsideMean: "Aria esterna, media",
      outsideTag: (rh: string, min: string, max: string, unit: string) => `UR ${rh} % · da ${min} a ${max} ${unit} (medie giornaliere)`,
      checksInPeriod: "Verifiche nel periodo",
      screening: "verifica preliminare, non certificazione",
      checkCondensate: (limit: number) => `Condensa ≤ ${limit} g/m² per tutto il periodo`,
      checkCondensateDetail: (max: string, day: string, days: number, end: string) => `max ${max} g/m² il ${day}; ${days} giorno/i con condensa; ${end} g/m² nell'ultimo giorno`,
      noCondensatePeriod: "Nessuna condensa nel periodo.",
      checkWood: (limit: number) => `Materiali a base di legno: contenuto di umidità ≤ ${limit} M.-%`,
      checkWoodDetail: (name: string, max: string, day: string, daysOver: number) => `${name}: max ${max} M.-% il ${day}${daysOver ? `, ${daysOver} giorno/i oltre il limite` : ""}`,
      noWood: "Nessun materiale a base di legno.",
      checkFrsi: (limit: number) => `Fattore di temperatura f_Rsi ≥ ${limit} (ore con Ti − Te ≥ 10 K)`,
      checkFrsiDetail: (value: string, day: string) => `f_Rsi minimo ${value} il ${day}`,
      noHour10KPeriod: "Nessuna ora con Ti − Te ≥ 10 K nel periodo.",
      accumulationNote: "La verifica di accumulo da un anno all'altro richiede anni interi: veda le verifiche di fine calcolo sopra.",
      membranesWood: "Membrane e materiali a base di legno",
      colMembrane: "Membrana",
      colSdMean: "Sd medio [m]",
      colSdMin: "Sd min [m]",
      colSdMax: "Sd max [m]",
      byRh: "secondo l'UR",
      fixedSd: "fisso",
      noMembrane: "Nessuna membrana nella stratigrafia.",
      colWood: "Materiale a base di legno",
      colUMean: "u medio [M.-%]",
      colUMax: "u max [M.-%]",
      colDaysOver: (limit: number) => `Giorni > ${limit} M.-%`,
      woodNote: "u = picco giornaliero della cella più umida del materiale.",
      layersInPeriod: "Strati nel periodo",
      colLayer: "Strato",
      colTmin: (unit: string) => `T min [${unit}]`,
      colTmean: (unit: string) => `T media [${unit}]`,
      colTmax: (unit: string) => `T max [${unit}]`,
      colRhMean: "UR media [%]",
      colRhMax: "UR max [%]",
      colWaterMean: "Acqua media [kg/m²]",
      colCondMax: "Condensa max [g/m²]",
      membraneFace: "membrana: in 1D una faccia tra le celle (veda il suo Sd e le interfacce sui due lati)",
      layersNote: "Strati dall'interno verso l'esterno. I valori di ogni strato sono medie di volume giornaliere; minimo e massimo si riferiscono a queste medie giornaliere.",
      interfacesInPeriod: "Interfacce nel periodo",
      colInterface: "Interfaccia",
      colRhMaxHourly: "UR max, oraria [%]",
      colDays80: "Giorni con UR media > 80 %",
      interfacesNote: "T minima e massima delle medie giornaliere; UR massima sulle ore del periodo.",
      profileTitle: "Umidità relativa attraverso il componente nel periodo",
      // convergence
      convSettings: (method: string, tolerance: string, maxIter: number, step: string) => `${method} · tolleranza ${tolerance} · max. ${maxIter} iterazioni per soluzione · passo temporale ${step}`,
      hoursConverged: "Ore a convergenza",
      everyHour: "ogni ora",
      hoursNotConverged: (hours: number) => `${hours} ora/e non a convergenza`,
      maxIterations: "Soluzione lineare, iterazioni max.",
      limitN: (limit: number) => `limite ${limit}`,
      directSolver: " · solutore diretto",
      maxResidual: "Soluzione lineare, residuo max.",
      toleranceTag: (tolerance: string) => `tolleranza ${tolerance}`,
      energyResidual: "Bilancio energetico, residuo max.",
      relativeLimit: (limit: string) => `relativo · limite ${limit}`,
      massResidual: "Bilancio di massa, residuo max.",
      massTag: (limit: string, max: string) => `relativo · limite ${limit} · max. sull'intero calcolo ${max} kg/m² per passo`,
      notConvergedTitle: "Ora per ora: ore non a convergenza evidenziate",
      causes: ["soluzione termica", "soluzione del vapore", "insieme attivo di condensazione", "Sd delle membrane"],
      causeHours: (cause: string, hours: number) => `${cause} ${hours} h`,
      notConvergedList: (list: string) => `Non a convergenza: ${list}.`,
      allConverged: "Ogni ora del periodo è andata a convergenza: tutte le soluzioni lineari hanno raggiunto la tolleranza, l'insieme attivo di condensazione si è stabilizzato e l'iterazione sull'Sd delle membrane è andata a convergenza.",
      residualNote: "I residui di bilancio sono mostrati a parte: un residuo di massa relativo superiore a 1e−6 in un'ora con flusso quasi nullo attraverso le superfici può corrispondere a una quantità assoluta minima.",
      iterationsTitle: (perHour: boolean) => `Iterazioni della soluzione lineare (${perHour ? "per ora" : "ora peggiore di ogni giorno"})`,
      residualsTitle: (perHour: boolean) => `Residui, log10 (${perHour ? "per ora" : "ora peggiore di ogni giorno"})`,
      iterations: "iterazioni",
      maxIterSeries: "iterazioni max. (calore o vapore)",
      linearRelative: "soluzione lineare (relativo)",
      energyBalance: "bilancio energetico",
      massBalance: "bilancio di massa",
      failsTitle: "Ore non a convergenza (prime 30 del periodo)",
      colHour: "Ora",
      colNotConverged: "Non a convergenza",
      colIterations: "Iterazioni",
      colLinearResidual: "Residuo lineare",
      converged: "a convergenza",
      linearNotConverged: "soluzione lineare non a convergenza",
      activeSetNotConverged: "insieme attivo o Sd delle membrane non a convergenza",
      // charts
      curvesShown: "Curve visualizzate (clic per nascondere o mostrare)",
      rhCondensation: "100 % (condensazione)",
      membrane: "membrana",
      profileLabel: "Umidità relativa attraverso il componente",
      outsideCaps: "ESTERNO",
      profileMean: "media delle medie giornaliere",
      profileMax: "media giornaliera più alta",
      profileMin: "media giornaliera più bassa",
      insideAtBottom: "interno in basso",
      quantity: "Grandezza",
      fieldRh: "UR",
      fieldT: "Temperatura",
      fieldWc: "Condensa",
      fieldHover: (layer: string, stud: boolean, x: string, y: string, temperature: string, unit: string, rh: string, condensate: string) =>
        `${layer}${stud ? " (montante)" : ""} · x ${x} mm, ${y} mm dall'interno · T ${temperature} ${unit} · UR ${rh} % · condensa ${condensate} kg/m³`,
      noCondensateHour: "nessuna condensa in quest'ora",
      fieldHint: "Passi con il mouse su una cella per vederne i valori. Larghezza: metà dell'interasse dei montanti tra i piani di simmetria.",
    },
    results: {
      badge2D: "mesh 2D - ultimo calcolo",
      badgeSimplified: "semplificato - ultimo calcolo",
      badgeNone: "nessun risultato",
      warnings: "Avvertenze",
      stratigraphy: "Stratigrafia",
      fieldTitle: "Campo attraverso la stratigrafia",
      zoomResetTitle: "Reimposta lo zoom (scorra sul disegno per ingrandire/ridurre)",
      standingNoteTitle: "Metodi numerici e scelta dei materiali",
      standingNoteDetail: "Ove applicabile (modalità mesh 2D) si utilizzano metodi numerici. La verifica della loro validità e dell'idoneità di ogni materiale e prodotto scelto nella stratigrafia, per il caso specifico, è responsabilità del progettista.",
      inputErrorTitle: "Calcolo non eseguito - dati non validi",
      inputErrorTitleQuota: "Calcolo non eseguito - tempo di calcolo del server esaurito",
      inputErrorTitleRateLimit: "Calcolo non eseguito - limite di richieste raggiunto",
      cancelledTitle: "CALCOLO ANNULLATO",
      cancelledDetail: "Calcolo annullato dall'utente. I valori seguenti provengono dall'ultimo calcolo effettivamente completato.",
      noWarnings: "Aggiunga almeno uno strato alla stratigrafia per vedere qui le avvertenze tecniche.",
      uLimitLabels: {
        wall: "Parete esterna (DE Bestand, 0,24)",
        roof: "Copertura / ultimo solaio (DE Bestand, 0,24)",
        floor: "Solaio verso terreno/locali non riscaldati (DE Bestand, 0,30)",
        custom: "Limite personalizzato",
      },
      checks: {
        uValue: "Trasmittanza U",
        uValue2D: "Trasmittanza U (2D)",
        limit: (value: string, reference: string) => `limite ${value} (${reference})`,
        interstitial: "Condensa interstiziale e muffa",
        interstitial2D: "Condensa interstiziale e muffa (2D)",
        interstitialCondensation: "condensa interstiziale",
        possibleMould: "possibile formazione di muffa",
        none: "nessuna",
        wet1D: (rate: string, node: string) => `${rate} g/(m²·h) al nodo ${node} (EN ISO 13788, mese di progetto)`,
        wet2D: (cells: number, share: string) => `${cells} ${cells > 1 ? "celle" : "cella"} con UR > 100 % · ${share} % della sezione (condizione di progetto)`,
        maxRh: (where: string, rh: string) => `UR max. ${where} ${rh} % (condizione di progetto)`,
        betweenLayers: "tra gli strati",
        inTheField: "nel campo",
        noFace: "nessuna interfaccia tra strati",
        surface1D: "UR superficie interna (muffa)",
        surfaceDetail1D: "limite 80 % · clicchi per il diagramma psicrometrico",
        surface2D: "Superficie interna (muffa)",
        surfaceDetail2D: "UR max., limite 80 % · temperatura min. · clicchi per il diagramma psicrometrico",
        solver: "Solutore",
        notConverged: "non convergente",
        solverDetail: "campi termico e del vapore",
        monthly: "Glaser mensile",
        notCalculated: "non calcolato",
        checksNotSatisfied: (n: number) => `${n} ${n > 1 ? "verifiche non soddisfatte" : "verifica non soddisfatta"}`,
        verified: "verificato",
        monthlyOk: "bilancio annuale, condensa ammissibile, muffa",
        monthlyFailure: {
          residual: "condensa residua a fine ciclo",
          admissible: "condensa ammissibile superata",
          mould: "rischio di muffa (f_Rsi)",
        },
        satisfied: "Soddisfatta",
        notSatisfied: "Non soddisfatta",
        ofLimit: (percent: number) => `${percent} % del limite`,
        notSatisfiedCount: (bad: number, total: number) => `⚠ ${bad} di ${total} verifiche non soddisfatte`,
        allSatisfied: (total: number) => `tutte le ${total} verifiche soddisfatte`,
        mode2D: "Mesh 2D (stazionario, volumi finiti)",
        mode1D: "1D semplificato (EN ISO 6946 / EN ISO 13788)",
        clickForDetails: "clicchi su una verifica per i dettagli",
      },
      temperature: "Temperatura",
      humidity: "Umidità",
      humidityRh: "Umidità (UR)",
      vapourPressure: "Pressione di vapore",
      vapourPressurePa: "Pressione di vapore [Pa]",
      moistureContent: "Contenuto d'acqua",
      secondProfileChart: "Secondo grafico del profilo",
      field2DDesc: "Mezzo interasse simmetrico dei montanti (o l'intera campata in assenza di elementi strutturali), altezze delle righe in scala. Clicchi su una cella per i suoi valori esatti. Trascini la linea verticale per spostare i grafici del profilo verticale accanto in quella posizione",
      moistureNote: "Rosso = sovrasaturo (UR>100%) - l'equilibrio di sorbimento w(UR) non descrive più correttamente quella cella, indipendentemente dalla disponibilità di un modello di umidità.",
      midBay: "metà campata",
      midBayColumn: "colonna a metà campata",
      studCentreline: "asse del montante",
      fromStud: (distance: string) => `${distance} dall'asse del montante`,
      verticalProfile: (where: string) => `Profilo verticale (${where})`,
      temperatureThroughThickness: "Temperatura nello spessore",
      rhThroughThickness: "UR nello spessore",
      psatChartLabel: "Pressione di saturazione e di vapore nello spessore",
      rhChartLabel: "Umidità relativa nello spessore",
      cell: {
        hint: "Clicchi su una cella della mappa qui sopra per vederne qui i valori.",
        interiorFilm: "Strato liminare interno",
        exteriorFilm: "Strato liminare esterno",
        layer: (n: number) => `Strato ${n}`,
        material: "materiale",
        noMoistureModel: "non disponibile (nessun modello di umidità)",
        dryNoModel: "(secco, nessun modello)",
        saturated: "Stato: SATURO / POTENZIALE CONDENSA",
        rawNote: ">100% - valore tecnico, non l'UR indicata sopra",
      },
      field1DDesc: "La sezione mostra l'unica condizione di progetto fissa a cui il calcolo risolve (modifichi Te/URe/Ti/URi direttamente sulla sezione grafica del pannello Stratigrafia), non una media annuale, disegnata alla stessa scala del pannello Stratigrafia. Senza un calcolo 2D del ponte termico, il campo di colore è 1D: varia solo nello spessore e i contorni degli elementi strutturali sono disegnati solo come riferimento (il colore non varia lateralmente). Quando è disponibile un calcolo del ponte termico su mesh 2D, la vista Temperatura mostra quel campo effettivamente risolto e la vista Umidità l'UR locale dello stesso campo, calcolata insieme al profilo 1D di pressione di vapore di Glaser (la resistenza al vapore non è risolta lateralmente, quindi si tratta di un'approssimazione e non di un vero calcolo 2D del vapore - per questo selezioni la modalità di calcolo mesh 2D).",
      sectionTitle: (field: string) => `${field} attraverso la stratigrafia - condizione di progetto`,
      sectionTitleShort: (field: string) => `${field} attraverso la stratigrafia`,
      noResultsYet: "Nessun risultato.",
      diagramNote2D: (humidity: boolean): string =>
        humidity
          ? "Campo di temperatura della mesh 2D + profilo del vapore 1D (pv non risolta lateralmente), specchiato rispetto all'asse di ogni montante"
          : "Mesh 2D, specchiata rispetto all'asse di ogni montante (2 interassi mostrati)",
      profilesTitle: "Profili nello spessore (condizione di progetto)",
      glaserSubtitleBefore: "Diagramma di Glaser — condensa dove ",
      glaserSubtitleAfter: ", alla condizione di progetto",
      info: {
        uvalue: "Coefficiente di trasmissione termica, verificato rispetto al limite di riferimento scelto (o a un valore personalizzato).",
        rw: "Stima semplificata secondo la legge di massa (sul modello della EN 12354-1, Rw ≈ 37,5·log10(m′) − 42 dB) dalla sola massa superficiale totale - non valida per pacchetti a più strati/con intercapedine (risonanza e coincidenza non modellate). Non sostituisce un Rw misurato o certificato.",
        condensationGlaser: "Glaser 1D: pressione di vapore superiore a quella di saturazione su un'interfaccia interna, alla condizione di progetto fissa (pannello Stratigrafia). Una verifica stazionaria, non un bilancio annuale.",
        surfaceCondensation: "Temperatura/UR sulla superficie interna, alla condizione di progetto fissa. Oltre l'80% circa di UR favorisce la muffa anche senza vera condensa; al 100% di UR la superficie condensa.",
        interstitialCondensation2d: "UR locale massima nel campo 2D a convergenza. Oltre l'80% circa favorisce la muffa; al 100% la cella è sovrasatura (condensa).",
      },
      bar: {
        empty: "Aggiunga almeno uno strato per vedere qui i risultati.",
        moreInfo: "Maggiori informazioni",
        previousSection: "Sezione precedente",
        nextSection: "Sezione successiva",
        sectionOf: (i: number, n: number) => `Sezione ${i} di ${n}`,
        collapseToggle: "Comprimi/espandi la barra dei risultati",
        thermal: "Termica",
        thermal2D: "Termica (2D)",
        hygrometric: "Igrometrica",
        hygrometric2D: "Igrometrica (2D)",
        acoustic: "Acustica",
        uValue: "Trasmittanza U",
        uValue2D: "Trasmittanza U (2D)",
        transmittanceConvergence: "Convergenza trasmittanza",
        vapourConvergence: "Convergenza pressione di vapore / UR",
        converged: "Convergente",
        notConverged: "Non convergente",
        solveTime: (time: string) => `Tempo di calcolo: ${time}`,
        surfaceCondensation: "Condensa superficiale",
        condensing: "Condensa",
        mouldRisk: "Rischio muffa",
        ok: "OK",
        noSurfaceCondensation: "Nessuna condensa superficiale",
        condensationGlaser: "Condensa (Glaser)",
        interstitialCondensation: "Condensa interstiziale",
        noCondensation: "Nessuna condensa",
        suggestion: "Suggerimento",
        leverIterations: "aumentare le iterazioni massime",
        leverMesh: "infittire la mesh (più righe)",
        leverTolerance: "allentare la tolleranza (εU/εRH)",
        leverOr: ", oppure ",
        suggestTry: (levers: string) => `Provi a: ${levers} (Impostazioni → Solutore 2D).`,
        suggestAllTried: "Iterazioni/mesh già aumentate e tolleranza già allentata rispetto ai valori predefiniti, ma il calcolo non converge - verifichi nella stratigrafia un rapporto di conducibilità estremo tra i materiali.",
        weight: "Peso",
        rw: "Rw (stima legge di massa)",
      },
      psychro: {
        title: "Diagramma psicrometrico",
        hint: "l'aria interna fino all'80 % di UR",
        hintValue: (temperature: string) => `l'aria interna fino all'80 % di UR: ${temperature}`,
        unavailable: "Disponibile una volta calcolata la stratigrafia.",
        insideAir: "Aria interna",
        humidityRatio: "Titolo W",
        theta80: "θ all'80 % di UR",
        insideSurface: "Superficie interna",
        insideSurfaceMin: "Superficie interna, min.",
        noMouldRisk: "Superficie sopra θ80%: nessun rischio di muffa",
        mouldRisk: "Superficie sotto θ80%: rischio di muffa",
        chartLabel: "Diagramma psicrometrico: l'aria interna raffreddata a titolo costante fino all'80 % di UR",
        dryBulb: (unit: string) => `temperatura a bulbo secco [${unit}]`,
        humidityRatioAxis: "titolo W [g/kg aria secca]",
        enthalpy: "entalpia h [kJ/kg]",
      },
    },
    tutorial: {
      startTitle: "Avviare il tutorial?",
      startMessage: "Una breve dimostrazione delle funzioni principali sulla parete a telaio in legno validata con Ubakus, già calcolata: ogni passo viene eseguito per Lei, prosegue con Avanti. Il Suo progetto viene messo da parte e torna com'è alla fine.",
      start: "Avvia il tutorial",
      preparing: "Preparazione del tutorial…",
      running: "Il tutorial è in corso: prema Avanti, oppure Esci dal tutorial per lasciarlo.",
      stepOf: (step: number, total: number) => `Passo ${step} di ${total}`,
      showing: "In corso…",
      stepDone: "✓ Fatto",
      skip: "Esci dal tutorial",
      next: "Avanti",
      startButton: "Inizia",
      finish: "Fine",
      demoName: "Parete demo",
      steps: {
        intro: {
          title: "Tutorial",
          text: "Una breve dimostrazione delle funzioni principali su un esempio reale - la parete a telaio in legno con intercapedine impiantistica validata con Ubakus, già calcolata. Ogni passo viene eseguito per Lei e mostrato sulla pagina: prema Avanti per proseguire, oppure Esci dal tutorial per lasciarlo. Il Suo progetto viene messo da parte e torna com'è alla fine.",
        },
        name: {
          title: "Nome del progetto",
          text: "Il progetto riceve un nome (facoltativo): compare nel report.",
        },
        site: {
          title: "Sito e clima",
          text: "Il sito definisce il clima delle verifiche di condensa: un clic sulla mappa, un luogo cercato per nome oppure uno dei luoghi sulla mappa. Qui viene mantenuto il sito dell'esempio.",
        },
        element: {
          title: "Elemento",
          text: "Parete: l'esempio è una parete esterna. Copertura e contro terra impostano altre resistenze superficiali.",
        },
        analysis: {
          title: "Analisi",
          text: "Statica: trasmittanza, condensa di Glaser nella condizione di progetto e Glaser mensile sul clima del sito. Quasi-dinamica: calore e umidità ora per ora su più anni del clima del sito, nelle sue schede. L'esempio usa l'analisi statica.",
        },
        stratigraphy: {
          title: "Stratigrafia",
          text: "La scheda Stratigrafia: gli strati della parete, dall'esterno all'interno, con i loro montanti.",
        },
        addMaterial: {
          title: "Aggiungere un materiale",
          text: "Un materiale viene trascinato dalla libreria sul disegno: uno spazio mostra dove andrà, e viene aggiunto dove lo si rilascia.",
        },
        thickness: {
          title: "Modificare uno spessore",
          text: "Un clic su uno strato apre le sue proprietà: lo spessore viene digitato e confermato con Invio.",
        },
        layerValues: {
          title: "Valori di un solo strato",
          text: "Nello stesso pannello si modifica la λ di questo strato: cambia solo questo strato, il materiale nella libreria resta com'è.",
        },
        undo: {
          title: "Annulla",
          text: "Annulla (o Ctrl+Z) riporta indietro le modifiche una alla volta, finché l'esempio torna com'era; ▾ elenca le modifiche recenti.",
        },
        results: {
          title: "Analisi e risultati",
          text: "Analisi e risultati: l'esempio è già calcolato.",
        },
        checks: {
          title: "Le verifiche",
          text: "Trasmittanza rispetto al limite, condensa interstiziale (Glaser), umidità superficiale interna (muffa) e l'esito del Glaser mensile; sotto, la temperatura e la pressione di vapore attraverso la parete.",
        },
        monthly: {
          title: "Glaser mensile",
          text: "Un clic sulla verifica Glaser mensile: condensa e asciugatura mese per mese sul clima del sito.",
        },
        report: {
          title: "Report",
          text: "Il Report: tutto in un unico documento, da stampare o salvare in PDF.",
        },
        done: {
          title: "Fatto",
          text: "Questo è il percorso principale: progetto e sito, stratigrafia, risultati, report. Prema Fine: il Suo progetto torna come l'ha lasciato. Tutorial, nella barra laterale, lo mostra di nuovo in qualsiasi momento.",
        },
      },
    },
    report: {
      printAndContents: "Stampa e contenuti del report",
      print: "Stampa…",
      printInfo: (pages: number, bw: boolean) => `${pages} ${pages > 1 ? "pagine" : "pagina"} · A4 · ${bw ? "bianco e nero" : "colori"}`,
      contents: "Contenuti",
      groups: {
        inputs: "Input",
        results: "Risultati",
      },
      all: "tutti",
      none: "nessuno",
      notAvailable: "Non disponibile per il calcolo corrente",
      fieldShown: "Campo mostrato",
      sections: {
        project: "Progetto",
        site: "Sito e clima",
        conditions: "Condizioni di progetto",
        drawing: "Disegno della stratigrafia",
        layers: "Tabella degli strati",
        checks: "Verifiche",
        uvalue: "Calcolo della trasmittanza U",
        nodes: "Valori alle interfacce",
        temp: "Profilo di temperatura",
        profiles2d: "Profili verticali 2D",
        compare: "Confronto 1D / 2D",
        climate: "Anno di riferimento del sito",
      },
      relativeHumidity: "Umidità relativa",
      untitledProject: "Progetto senza titolo",
      notes: "Note",
      date: "Data",
      calculation2D: "Mesh 2D (regime stazionario, volumi finiti)",
      location: "Località",
      climateData: "Dati climatici",
      elevation: "Altitudine",
      topSide: (side: string) => `Lato superiore (${side}): temperatura / UR / resistenza superficiale`,
      bottomSide: (side: string) => `Lato inferiore (${side}): temperatura / UR / resistenza superficiale`,
      materialInsideOutside: "Materiale (interno → esterno)",
      check: "Verifica",
      value: "Valore",
      reference: "Riferimento",
      outcome: "Esito",
      satisfied: "SODDISFATTA",
      notSatisfied: "NON SODDISFATTA",
      overallOutcome: "Esito complessivo",
      allSatisfied: "TUTTE LE VERIFICHE SODDISFATTE",
      notAllSatisfied: "NON TUTTE LE VERIFICHE SODDISFATTE",
      uFraming: "U con gli elementi dell'orditura (metodo combinato EN ISO 6946)",
      interface: "Interfaccia",
      insideNode: (n: number) => `${n} (interno)`,
      outsideNode: (n: number) => `${n} (esterno)`,
      verdict: "Esito",
      notVerified: (failures: string) => `non verificata: ${failures}`,
      method: "Metodo",
      methodValue: (preset: string, start: string) => `${preset}; inizio del ciclo: ${start}`,
      monthlySection: (u: string, maxAccumulated: string, left: string, fRsi: string, fRsiMin: string) => `U ${u} W/(m²K) · max. accumulato ${maxAccumulated} · residuo a fine ciclo ${left} · f_Rsi ${fRsi} / ${fRsiMin}`,
      glaserMonthShown: "Diagramma di Glaser (mese mostrato nella pagina)",
      quantity: "Grandezza",
      simplified1D: "1D semplificato",
      mesh2D: "Mesh 2D",
      insideSurfaceTemperature: "Temperatura superficiale interna",
      insideSurfaceRh: "UR superficiale interna",
      min: "(min.)",
      max: "(max.)",
      empty: "Selezioni nell'elenco Contenuti le sezioni da includere.",
      disclaimer1: "Tutti i calcoli devono essere verificati e approvati dal progettista responsabile prima dell'uso.",
      disclaimer2: "I valori dei materiali sono tratti dai certificati o dalle schede tecniche dei prodotti; l'uso di metodi numerici (modalità mesh 2D) e la scelta dei materiali e dei prodotti della stratigrafia sono responsabilità del progettista.",
      pageOf: (page: number, pages: number) => `Pagina ${page} di ${pages}`,
      reportPages: "Pagine del report",
      viewer: "Visualizzatore del report",
      previousPage: "Pagina precedente",
      nextPage: "Pagina successiva",
      page: "Pagina",
      zoomOut: "Riduci",
      zoomIn: "Ingrandisci",
      zoom: "Zoom",
      fitWidthPct: (percent: string) => `Adatta alla larghezza (${percent} %)`,
      fitPagePct: (percent: string) => `Adatta alla pagina (${percent} %)`,
      fitWidth: "Adatta alla larghezza",
      fitPage: "Adatta alla pagina",
      pageLayout: "Layout di pagina",
      layouts: {
        continuous: "Pagine continue",
        single: "Pagina singola",
        two: "Due pagine affiancate",
      },
      blackAndWhite: "Bianco e nero",
      blackAndWhiteOn: "Bianco e nero (attivo: anche in stampa)",
      bwShort: "B/N",
    },
  },

  // Texts of the Design System V2 components (src/components/ds2).
  designSystem: {
    openInNewLabel: "Si apre in una nuova scheda",
    breadcrumbLabel: "Percorso di navigazione",
    openMenuLabel: "Apri menu",
    closeMenuLabel: "Chiudi menu",
    closeLabel: "Chiudi",
    accountMenuLabel: "Menu account",
    paginationOfLabel: "di",
    previousPageLabel: "Pagina precedente",
    nextPageLabel: "Pagina successiva",
    selectPlaceholder: "Seleziona...",
    dropFilesLabel: "Rilascialo!",
    dropZoneLabel: "Area di rilascio file",
    dropRejectedLabel: "Questo tipo di file non è supportato",
    searchPlaceholder: "Cerca",
    clearSearchLabel: "Cancella ricerca",
    toggleSubmenuLabel: (text) => `Attiva/disattiva ${text}`,
    contextMenuTriggerLabel: "Apri menu azioni",
    increaseLabel: (value: number) => `Incrementa ${value}`,
    decreaseLabel: (value: number) => `Decrementa ${value}`,
    datePicker: {
      clearButton: "Rimuovi data",
      nextMonth: "Mese successivo",
      placeholder: "Seleziona data",
      previousMonth: "Mese precedente",
      selectMonth: "Seleziona mese",
      selectYear: "Seleziona anno",
      calendarLabel: "Scegli una data",
    },
    cancelLabel: "Annulla",
    okLabel: "OK",
    removeLabel: "Rimuovi",
    requiredFieldLabel: "Questo campo è obbligatorio.",
    fieldTooSmallLabel: "Il valore di questo campo è troppo basso.",
    fieldTooLargeLabel: "Il valore di questo campo è troppo alto.",
    fieldInvalidFormatLabel: "Il valore di questo campo ha un formato non valido.",
    fieldInvalidValueLabel: "Il valore di questo campo non è valido.",
    browseOrDropFileLabel: "Trascina qui il file o fai clic per selezionarlo",
    browseOrDropImageLabel: "Trascina qui l'immagine o fai clic per caricarla",
    removeImageLabel: "Rimuovi immagine",
    resetImageLabel: "Ripristina immagine",
    translationsLabel: "Traduzioni",
    increaseOrderLabel: "Aumenta ordine",
    decreaseOrderLabel: "Diminuisci ordine",
    stagingLabel: "Ambiente di test",
    unexpectedErrorLabel: "Qualcosa è andato storto. Riprova.",
    saveLabel: "Salva",
    loadingLabel: "Caricamento",
    browseFilesLabel: "Sfoglia file",
    noOptionsLabel: "Nessuna opzione",
    quantityOfLabel: (label: string) => `Quantità di ${label}`,
    chooseOrDropFileLabel: "Scegli il file o trascinalo qui per caricarlo.",
    stepOfLabel: (current: number, total: number) => `Passo ${current} di ${total}`,
    stepCompletedLabel: "completato",
    backToStepLabel: (step: number) => `Torna al passo ${step}`,
  },
};

export default it;
